// Full UI copy for every shipped locale. Any locale without an
// entry here falls back to English (see getTranslations).

export interface TranslationStrings {
  navProjects: string;
  navHistory: string;
  navTools: string;
  navChat: string;
  navCerts: string;
  navAccount: string;
  toolsTitle: string;
  toolsDesc: string;
  toolsCountLabel: string;
  all: string;
  projectsTitle: string;
  projectsDesc: string;
  projTab: string;
  histTab: string;
  historyTitle: string;
  historyDesc: string;
  createProject: string;
  editProject: string;
  emptyProjectsTitle: string;
  emptyProjectsDesc: string;
  /** Upsell tile beside Default for guests / free users. */
  projectsProCtaTitle: string;
  projectsProCtaDesc: string;
  projectsProCtaBenefits: string[];
  emptyHistoryTitle: string;
  emptyHistoryDesc: string;
  emptyHistoryCta: string;
  notFoundTitle: string;
  notFoundDesc: string;
  notFoundCtaTools: string;
  notFoundCtaProjects: string;
  paginationPrevious: string;
  paginationNext: string;
  /** Placeholders: `{page}`, `{totalPages}`. */
  paginationPageOf: string;
  paginationAria: string;
  chatTitle: string;
  chatDesc: string;
  chatPlaceholder: string;
  chatSend: string;
  certsTitle: string;
  certsDesc: string;
  certsObserverNote: string;
  /** Shown when a prior certificate exists but access is revoked/expired. */
  certsRepurchaseNote: string;
  certsWhyTitle: string;
  certsBenefits: string[];
  certsPlanLabel: string;
  certsPriceWas: string;
  certsPriceNow: string;
  certsNotInPro: string;
  certsCta: string;
  certsSecurePay: string;
  certsSpecimen: string;
  certsSpecimenCaption: string;
  certsSpecimenAlt: string;
  certsPurchasedNote: string;
  /** Placeholder: `{serial}`. */
  certsSerialNumber: string;
  certsIncludesTitle: string;
  certsDownload: string;
  certsDownloading: string;
  certsDownloadError: string;
  certsPreviewCaption: string;
  certsPreviewAlt: string;
  certsProfileIncompleteBoth: string;
  certsProfileIncompleteFirst: string;
  certsProfileIncompleteLast: string;
  certsProfileIncompleteLink: string;
  certsVerifyEyebrow: string;
  certsVerifyTitle: string;
  certsVerifyDesc: string;
  certsVerifyAssociation: string;
  certsVerifyPrivacy: string;
  certsVerifyLabel: string;
  certsVerifyPlaceholder: string;
  certsVerifyHint: string;
  certsVerifyRequired: string;
  certsVerifyFormatError: string;
  certsVerifyCta: string;
  certsVerifyChecking: string;
  certsVerifyValidTitle: string;
  /** Placeholder: `{date}`. */
  certsVerifyValidBody: string;
  certsVerifyInvalidTitle: string;
  certsVerifyInvalidBody: string;
  certsVerifyAgain: string;
  certsVerifyError: string;
  certsVerifyBackToCerts: string;
  /** Link on /certificates to the public verify page. */
  certsVerifyOpenLink: string;
  accountTitle: string;
  /** Shown under email when the user has a password (email) identity. */
  accountEmailVerified: string;
  accountEmailUnverified: string;
  accountResendConfirmation: string;
  accountResendConfirmationSending: string;
  accountResendConfirmationSent: string;
  emailConfirmConfirming: string;
  emailConfirmSuccessTitle: string;
  emailConfirmSuccessBody: string;
  emailConfirmInvalidTitle: string;
  emailConfirmInvalidBody: string;
  emailConfirmMissingTitle: string;
  emailConfirmMissingBody: string;
  emailConfirmErrorTitle: string;
  emailConfirmGoToAccount: string;
  emailConfirmGoHome: string;
  loginLinkConsuming: string;
  loginLinkMissingTitle: string;
  loginLinkMissingBody: string;
  loginLinkInvalidTitle: string;
  loginLinkInvalidBody: string;
  loginLinkErrorTitle: string;
  loginLinkGoHome: string;
  projectLandingEyebrow: string;
  projectLandingBody: string;
  projectLandingMissingTitle: string;
  projectLandingMissingBody: string;
  projectLandingErrorTitle: string;
  projectLandingGoHome: string;
  projectLandingOpenWorkspace: string;
  paymentCheckoutSuccessTitle: string;
  paymentCheckoutSuccessBody: string;
  /** While exchanging Stripe session_id for account/session. */
  paymentCheckoutSuccessCompleting: string;
  /** Guest checkout created a new account; password sent by email. */
  paymentCheckoutSuccessGuestBody: string;
  /** Guest paid with an email that already has an account. */
  paymentCheckoutSuccessExistingAccountBody: string;
  paymentCheckoutSuccessSignIn: string;
  paymentCheckoutSuccessGoToAccount: string;
  paymentCheckoutSuccessGoToTools: string;
  paymentCheckoutSuccessGoToCertificates: string;
  paymentCheckoutCancelTitle: string;
  paymentCheckoutCancelBody: string;
  paymentCheckoutCancelRetry: string;
  paymentCheckoutCancelGoToTools: string;
  paymentCheckoutCancelGoToCertificates: string;
  accountSubscription: string;
  accountDataPrivacy: string;
  accountPlanFree: string;
  accountProfileHeading: string;
  accountFirstName: string;
  accountLastName: string;
  accountFirstNamePh: string;
  accountLastNamePh: string;
  accountSaveProfile: string;
  accountProfileSaved: string;
  menuRecents: string;
  menuRecentsEmpty: string;
  /** Link under sidebar Recents when history has more than 5 rows. */
  menuRecentsShowMore: string;
  menuAccountSettings: string;
  menuAbout: string;
  menuPersonalize: string;
  menuPrivacy: string;
  menuTerms: string;
  menuContact: string;
  signIn: string;
  signOut: string;
  itemDownload: string;
  itemEdit: string;
  /** Label for the markdown body field while editing a history draft. */
  itemBodyLabel: string;
  itemGeneratedWith: string;
  itemProject: string;
  itemGenerateNew: string;
  itemDraftNote: string;
  /** Speech player — accessible control labels. */
  audioPlay: string;
  audioPause: string;
  audioMute: string;
  audioUnmute: string;
  audioSeek: string;
  audioVolume: string;
  audioError: string;
  audioLabel: string;
  cpSubtitle: string;
  cpNameLabel: string;
  cpNamePh: string;
  cpDescLabel: string;
  cpOptional: string;
  cpRequired: string;
  cpDescPh: string;
  cpAsideTitle: string;
  cpAsideText: string;
  cpAsideHint: string;
  cpCancel: string;
  cpCreate: string;
  cpSave: string;
  /** Short title when a project has no generations yet. */
  cpEmptyProjTitle: string;
  cpEmptyProj: string;
  tfTitleLabel: string;
  tfTitlePh: string;
  tfProjectLabel: string;
  tfContextLabel: string;
  tfContextPh: string;
  tfToneLabel: string;
  tfToneOpts: string[];
  tfNextLabel: string;
  tfDeepLabel: string;
  tfReset: string;
  tfGenerate: string;
  tfMoreIn: string;
  tfMoreEmptyTitle: string;
  tfMoreEmptyDesc: string;
  badgePro: string;
  badgeExp: string;
  tfToday: string;
  tfGenBlockH: string;
  tfGenBlockP: string;
  tfCtxBlockH: string;
  tfClientLabel: string;
  tfClientPh: string;
  tfModalityLabel: string;
  tfModalityOpts: string[];
  tfFocusLabel: string;
  tfFocusPh: string;
  tfFormatLabel: string;
  tfFormatOpts: string[];
  tfLengthLabel: string;
  tfLengthOpts: string[];
  tfReadingLabel: string;
  tfReadingOpts: string[];
  tfRiskLabel: string;
  tfMeasuresLabel: string;
  tfAnonLabel: string;
  ctxTitle: string;
  ctxNotesLabel: string;
  ctxNotesPh: string;
  ctxPrivate: string;
  ctxSummaryLabel: string;
  ctxAuto: string;
  ctxUpdate: string;
  ctxSavedTag: string;
  ctxToolsUsed: string;
  ctxNotesHelp: string;
  ctxSummaryTpl: string;
  ctxDraftWord: string;
  ctxDraftsWord: string;
  ctxCovers: string;
  ctxEmptySummary: string;
  authTitle: string;
  authSubtitle: string;
  authGoogle: string;
  authApple: string;
  authEmail: string;
  authGuest: string;
  authClose: string;
  authBack: string;
  authOr: string;
  authEmailLabel: string;
  authEmailPh: string;
  authPasswordLabel: string;
  authPasswordPh: string;
  authPasswordHint: string;
  /** “Forgot password?” link next to the password field on sign-in. */
  authRestoreAccess: string;
  authRestoreTitle: string;
  authRestoreSubtitle: string;
  authRestoreSubmit: string;
  authRestoreSuccess: string;
  authSignInTitle: string;
  authSignUpTitle: string;
  authSubmitSignIn: string;
  authSubmitSignUp: string;
  authSubmitting: string;
  authSwitchToSignUp: string;
  authSwitchToSignIn: string;
  authErrorFallback: string;
  /** Shown when authenticate/register/OAuth is rate-limited (429). */
  authTooManyAttempts: string;
  authEmailRequired: string;
  authEmailInvalid: string;
  authPasswordRequired: string;
  authPasswordTooShort: string;
  authInvalidCredentials: string;
  authEmailTaken: string;
  /** Prompt when several fields failed validation. */
  formCheckFields: string;
  validationFieldRequired: string;
  validationFieldEmail: string;
  validationFieldTaken: string;
  validationFieldMin: string;
  validationFieldMax: string;
  fieldLabelEmail: string;
  fieldLabelPassword: string;
  fieldLabelName: string;
  fieldLabelDescription: string;
  fieldLabelNotes: string;
  fieldLabelTitle: string;
  fieldLabelFirstName: string;
  fieldLabelLastName: string;
  cpNameRequired: string;
  hiTitleRequired: string;
  /** Shown when POST /v1/tool/{slug}/run fails unexpectedly. */
  tfRunError: string;
  /** Guest hit free-tier lifetime tool-run quota. */
  tfUsageLimitGuest: string;
  /** Free account hit free-tier lifetime tool-run quota. */
  tfUsageLimitFree: string;
  /** Authenticated user has no project to attach a tool run to. */
  tfNeedProject: string;
  /** Local bucket name for guest tool-run drafts. */
  guestDraftsName: string;
  /** Seeded project name for signed-out users (localStorage). */
  defaultProjectName: string;
  /** Guest localStorage → account sync failed (generic). */
  guestSyncFailed: string;
  /** Guest sync 422: generation missing or owned by another user. */
  guestSyncNotClaimable: string;
  /** Guest bag exceeds API sync limits (50/100/200). */
  guestSyncLimitsExceeded: string;
  /** Shown above the progress bar while a tool run is in flight. */
  tfGenerating: string;
  /** Short reassurance under the progress bar. */
  tfGeneratingHint: string;
  /** Cancel an in-flight tool run. */
  tfCancelRequest: string;
  authGateTitle: string;
  authGateSubtitle: string;
  authGateCta: string;
  retry: string;
  proModalTitle: string;
  proModalDesc: string;
  proModalPriceWas: string;
  proModalPriceNow: string;
  proModalOffer: string;
  proModalRating: string;
  proModalQuote: string;
  proModalTrusted: string;
  /** Noun after the live count, e.g. "specialists". */
  proModalTrustedLabel: string;
  proModalFeatures: Array<{ emoji: string; title: string; desc: string }>;
  proModalCta: string;
  proModalClose: string;
  proModalSchoolsAlt: string;
  proModalReviews: Array<{
    name: string;
    country: string;
    title: string;
    body: string;
    initials: string;
    /** Optional portrait under /promo — falls back to initials. */
    avatar?: string;
  }>;
  proModalDataProtected: string;
  proModalTermsShort: string;
  proModalGuarantee: string;
  proModalLegalNote: string;
}

const en: TranslationStrings = {
  navProjects: "Projects",
  navHistory: "History",
  navTools: "Tools",
  navChat: "Chat",
  navCerts: "Certificates",
  navAccount: "Account",
  toolsTitle: "Tools",
  toolsDesc:
    "AI tools for every stage of clinical work — from referral to follow-up. Each one produces a draft you review and own.",
  toolsCountLabel: "tools · growing",
  all: "All",
  projectsTitle: "Projects",
  projectsDesc: "Everything you’ve generated, organized by client work.",
  projTab: "Projects",
  histTab: "History",
  historyTitle: "History",
  historyDesc: "All drafts you’ve generated — across every project, newest first.",
  createProject: "Create project",
  editProject: "Edit project",
  emptyProjectsTitle: "No projects yet",
  emptyProjectsDesc:
    "Start a project for a client or piece of work. Drafts you generate will live here, organized and easy to find.",
  projectsProCtaTitle: "Organize your work in projects",
  projectsProCtaDesc:
    "Upgrade to Pro to create custom projects for each client or case — keep every draft structured and easy to find.",
  projectsProCtaBenefits: [
    "Create unlimited custom projects",
    "Separate work by client or case",
    "Keep every draft in the right place",
  ],
  emptyHistoryTitle: "No history yet",
  emptyHistoryDesc:
    "When you generate a draft, it will show up here — newest first.",
  emptyHistoryCta: "Browse tools",
  notFoundTitle: "This page couldn’t be found",
  notFoundDesc:
    "The tool, project, or draft you’re looking for doesn’t exist or has moved.",
  notFoundCtaTools: "Back to Tools",
  notFoundCtaProjects: "Go to Projects",
  paginationPrevious: "Previous",
  paginationNext: "Next",
  paginationPageOf: "Page {page} of {totalPages}",
  paginationAria: "Projects pagination",
  chatTitle: "Assistant",
  chatDesc: "Think out loud with an assistant that knows your tools.",
  chatPlaceholder: "Ask anything…",
  chatSend: "Send",
  certsTitle: "Membership certificate",
  certsDesc:
    "Full membership in the World Innovative Teaching Association with a printable, verifiable certificate — separate from your Aidemia Pro subscription.",
  certsObserverNote:
    "You are an observing member of the World Innovative Teaching Association. Upgrade to full membership to receive your certificate.",
  certsRepurchaseNote:
    "Your previous membership access is no longer active. You can purchase the certificate again.",
  certsWhyTitle: "Why get the certificate",
  certsBenefits: [
    "Official proof of professional membership in WITA",
    "Unique serial number with online validation",
    "Landscape certificate ready to print or share",
    "Recognized credential for schools and institutions",
    "Listed in the association membership registry",
    "Monthly membership at a limited-time rate",
  ],
  certsPlanLabel: "Monthly Membership",
  certsPriceWas: "$100.00",
  certsPriceNow: "$49.99",
  certsNotInPro:
    "Not included in Aidemia Pro. You pay only for the membership certificate and WITA full membership.",
  certsCta: "Join & get Certificate",
  certsSecurePay: "Secure checkout on our payment page.",
  certsSpecimen: "Specimen",
  certsSpecimenCaption:
    "Specimen — not a real issued certificate. Preview of the official WITA design.",
  certsSpecimenAlt: "Specimen — membership certificate preview",
  certsPurchasedNote:
    "Your full membership certificate for the World Innovative Teaching Association is active.",
  certsSerialNumber: "Document serial number: {serial}",
  certsIncludesTitle: "Your membership includes",
  certsDownload: "Download certificate",
  certsDownloading: "Downloading…",
  certsDownloadError: "Could not download the certificate. Please try again.",
  certsPreviewCaption:
    "Your issued certificate uses your profile name and serial number.",
  certsPreviewAlt: "Membership certificate preview",
  certsProfileIncompleteBoth:
    "Your first and last name are not filled in. The certificate uses the name from your profile.",
  certsProfileIncompleteFirst:
    "Your first name is not filled in. The certificate uses the name from your profile.",
  certsProfileIncompleteLast:
    "Your last name is not filled in. The certificate uses the name from your profile.",
  certsProfileIncompleteLink: "Fill in your profile",
  certsVerifyEyebrow: "WITA membership",
  certsVerifyTitle: "Verify a certificate",
  certsVerifyDesc:
    "Enter the document serial number to confirm that a membership certificate is authentic and currently valid.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "Verification confirms validity only — personal details are never shown.",
  certsVerifyLabel: "Document serial number",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Format: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Enter a certificate serial number.",
  certsVerifyFormatError: "Enter a number like CERT-2026-000001.",
  certsVerifyCta: "Verify certificate",
  certsVerifyChecking: "Checking…",
  certsVerifyValidTitle: "Certificate is valid",
  certsVerifyValidBody:
    "This membership certificate was issued on {date} and is currently active.",
  certsVerifyInvalidTitle: "Certificate not verified",
  certsVerifyInvalidBody:
    "We could not confirm this number. It may be mistyped, expired, or no longer active.",
  certsVerifyAgain: "Check another number",
  certsVerifyError:
    "Could not reach the verification service. Check your connection and try again.",
  certsVerifyBackToCerts: "Back to membership certificate",
  certsVerifyOpenLink: "Verify a certificate number",
  accountTitle: "Account",
  accountEmailVerified: "Email confirmed",
  accountEmailUnverified: "Email not confirmed",
  accountResendConfirmation: "Resend confirmation email",
  accountResendConfirmationSending: "Sending…",
  accountResendConfirmationSent: "Confirmation email sent. Check your inbox.",
  emailConfirmConfirming: "Confirming your email…",
  emailConfirmSuccessTitle: "Email confirmed",
  emailConfirmSuccessBody:
    "Your email address has been verified. You can continue using Aidemia.",
  emailConfirmInvalidTitle: "Confirmation link invalid",
  emailConfirmInvalidBody:
    "This confirmation link is invalid or has expired. Request a new confirmation email and try again.",
  emailConfirmMissingTitle: "Missing confirmation link",
  emailConfirmMissingBody:
    "Open the confirmation link from your email to verify your address.",
  emailConfirmErrorTitle: "Could not confirm email",
  emailConfirmGoToAccount: "Go to account",
  emailConfirmGoHome: "Back to home",
  loginLinkConsuming: "Signing you in…",
  loginLinkMissingTitle: "Link is invalid",
  loginLinkMissingBody:
    "This sign-in link is missing a token. Open the link from your email or payment confirmation again.",
  loginLinkInvalidTitle: "Link expired or already used",
  loginLinkInvalidBody:
    "This sign-in link has expired or was already used. Request a new one to continue.",
  loginLinkErrorTitle: "Could not sign you in",
  loginLinkGoHome: "Back to home",
  projectLandingEyebrow: "Your project",
  projectLandingBody: "You're signed in. Open the workspace to continue.",
  projectLandingMissingTitle: "Project not found",
  projectLandingMissingBody: "This project does not exist or you do not have access to it.",
  projectLandingErrorTitle: "Could not load project",
  projectLandingGoHome: "Back to home",
  projectLandingOpenWorkspace: "Open workspace",
  paymentCheckoutSuccessTitle: "Payment successful",
  paymentCheckoutSuccessBody:
    "Thank you. Your payment went through. Pro access will appear in your account shortly.",
  paymentCheckoutSuccessCompleting: "Confirming your payment…",
  paymentCheckoutSuccessGuestBody:
    "Thank you. Your account is ready — we’ve emailed you a password. You’re signed in and Pro access is active.",
  paymentCheckoutSuccessExistingAccountBody:
    "Thank you. Your payment is linked to your existing account. Sign in with your usual password to continue.",
  paymentCheckoutSuccessSignIn: "Sign in",
  paymentCheckoutSuccessGoToAccount: "Go to account",
  paymentCheckoutSuccessGoToTools: "Back to tools",
  paymentCheckoutSuccessGoToCertificates: "Back to certificates",
  paymentCheckoutCancelTitle: "Payment cancelled",
  paymentCheckoutCancelBody:
    "Checkout was cancelled. No charge was made. You can try again whenever you’re ready.",
  paymentCheckoutCancelRetry: "Try again",
  paymentCheckoutCancelGoToTools: "Back to tools",
  paymentCheckoutCancelGoToCertificates: "Back to certificates",
  accountSubscription: "Subscription",
  accountDataPrivacy: "Data & privacy",
  accountPlanFree: "Free",
  accountProfileHeading: "Your name",
  accountFirstName: "First name",
  accountLastName: "Last name",
  accountFirstNamePh: "Given name",
  accountLastNamePh: "Family name",
  accountSaveProfile: "Save changes",
  accountProfileSaved: "Profile updated.",
  menuRecents: "Recents",
  menuRecentsEmpty: "No recent generations yet",
  menuRecentsShowMore: "Show more",
  menuAccountSettings: "Account settings",
  menuAbout: "About Aidemia",
  menuPersonalize: "Personalize",
  menuPrivacy: "Privacy policy",
  menuTerms: "Terms & Conditions",
  menuContact: "Contact us",
  signIn: "Sign in",
  signOut: "Sign out",
  itemDownload: "Download",
  itemEdit: "Edit",
  itemBodyLabel: "Content",
  itemGeneratedWith: "Generated with",
  itemProject: "Project",
  itemGenerateNew: "Generate a new one",
  itemDraftNote: "Draft generated by Aidemia — review and edit before use.",
  audioPlay: "Play",
  audioPause: "Pause",
  audioMute: "Mute",
  audioUnmute: "Unmute",
  audioSeek: "Seek",
  audioVolume: "Volume",
  audioError: "Couldn’t load this audio. Try downloading the file instead.",
  audioLabel: "Audio",
  cpSubtitle: "A home for one piece of client work.",
  cpNameLabel: "Project name",
  cpNamePh: "e.g. Client S. — Panic",
  cpDescLabel: "Description",
  cpOptional: "optional",
  cpRequired: "required",
  cpDescPh: "Presenting concern, modality, stage of care…",
  cpAsideTitle: "About projects",
  cpAsideText:
    "A project keeps everything for one piece of client work in one place — notes, plans, and letters — so it’s easy to find later.",
  cpAsideHint: "Tip: use initials or a short code instead of full client names.",
  cpCancel: "Cancel",
  cpCreate: "Create",
  cpSave: "Save",
  cpEmptyProjTitle: "This project is empty",
  cpEmptyProj: "Nothing here yet — drafts you generate into this project will appear here.",
  tfTitleLabel: "Draft title",
  tfTitlePh: "e.g. Session prep — week 7",
  tfProjectLabel: "Save to project",
  tfContextLabel: "Clinical context",
  tfContextPh: "Paste session notes, a referral letter, or a few lines about the client…",
  tfToneLabel: "Tone of voice",
  tfToneOpts: ["Warm", "Neutral", "Clinical"],
  tfNextLabel: "Suggest next steps",
  tfDeepLabel: "Deep clinical reasoning",
  tfReset: "Reset",
  tfGenerate: "Generate",
  tfMoreIn: "More from this category",
  tfMoreEmptyTitle: "No other tools here yet",
  tfMoreEmptyDesc: "This is the only tool in this category right now.",
  badgePro: "PRO",
  badgeExp: "EXPERIMENTAL",
  tfToday: "Today",
  tfGenBlockH: "Draft",
  tfGenBlockP:
    "A structured first draft based on your context — ready to review, edit, and make your own.",
  tfCtxBlockH: "Your context",
  tfClientLabel: "Client reference",
  tfClientPh: "e.g. Client M. (initials only)",
  tfModalityLabel: "Therapeutic modality",
  tfModalityOpts: ["CBT", "ACT", "DBT", "Psychodynamic", "Person-centred", "Integrative"],
  tfFocusLabel: "Key focus areas",
  tfFocusPh: "Themes, goals, or anything the draft should emphasise…",
  tfFormatLabel: "Output format",
  tfFormatOpts: ["Bulleted", "Prose", "SOAP note", "Letter"],
  tfLengthLabel: "Length",
  tfLengthOpts: ["Brief", "Standard", "Detailed"],
  tfReadingLabel: "Reading level",
  tfReadingOpts: ["Plain language", "Standard", "Clinical"],
  tfRiskLabel: "Include risk screening",
  tfMeasuresLabel: "Reference outcome measures",
  tfAnonLabel: "Anonymise client details",
  ctxTitle: "Project context",
  ctxNotesLabel: "Notes",
  ctxNotesPh:
    "Add your own context — background, goals, risk flags, preferences, anything to remember for next time…",
  ctxPrivate: "Private to you · saved on this device",
  ctxSummaryLabel: "From your drafts",
  ctxAuto: "Auto",
  ctxUpdate: "Update",
  ctxSavedTag: "Saved",
  ctxToolsUsed: "Tools used",
  ctxNotesHelp:
    "Used whenever you select this project in an AI Tool, to enrich the drafts it generates.",
  ctxSummaryTpl: "{n} {drafts} so far, most recently on {date}.",
  ctxDraftWord: "draft",
  ctxDraftsWord: "drafts",
  ctxCovers: "Covers",
  ctxEmptySummary:
    "No drafts yet — a summary appears here as you generate work into this project.",
  authTitle: "Secure your account",
  authSubtitle: "Having an account helps you keep your app data and PRO status secure.",
  authGoogle: "Continue with Google",
  authApple: "Continue with Apple",
  authEmail: "Continue with email",
  authGuest: "Continue as guest",
  authClose: "Close",
  authBack: "Back",
  authOr: "or",
  authEmailLabel: "Email",
  authEmailPh: "you@example.com",
  authPasswordLabel: "Password",
  authPasswordPh: "••••••••",
  authPasswordHint: "At least 8 characters.",
  authRestoreAccess: "Forgot password?",
  authRestoreTitle: "Restore access",
  authRestoreSubtitle: "Enter your email and we’ll send a new password.",
  authRestoreSubmit: "Send new password",
  authRestoreSuccess: "If an account exists for this email, a new password is on its way.",
  authSignInTitle: "Sign in",
  authSignUpTitle: "Create your account",
  authSubmitSignIn: "Sign in",
  authSubmitSignUp: "Create account",
  authSubmitting: "Please wait…",
  authSwitchToSignUp: "Don't have an account? Sign up",
  authSwitchToSignIn: "Already have an account? Sign in",
  authErrorFallback: "Something went wrong. Please try again.",
  authTooManyAttempts: "Too many attempts. Please wait a moment and try again.",
  authEmailRequired: "Email is required.",
  authEmailInvalid: "Enter a valid email address.",
  authPasswordRequired: "Password is required.",
  authPasswordTooShort: "Password must be at least 8 characters.",
  authInvalidCredentials: "Invalid credentials.",
  authEmailTaken: "This email is already registered.",
  formCheckFields: "Please check the highlighted fields.",
  validationFieldRequired: "{field} is required.",
  validationFieldEmail: "Enter a valid {field}.",
  validationFieldTaken: "This {field} is already taken.",
  validationFieldMin: "{field} must be at least {min} characters.",
  validationFieldMax: "{field} must be at most {max} characters.",
  fieldLabelEmail: "Email",
  fieldLabelPassword: "Password",
  fieldLabelName: "Name",
  fieldLabelDescription: "Description",
  fieldLabelNotes: "Notes",
  fieldLabelTitle: "Title",
  fieldLabelFirstName: "First name",
  fieldLabelLastName: "Last name",
  cpNameRequired: "Project name is required.",
  hiTitleRequired: "Title is required.",
  tfRunError: "Could not generate. Please try again.",
  tfUsageLimitGuest: "Guest limit reached. Sign in or create an account to continue.",
  tfUsageLimitFree: "Free limit reached. Upgrade to Pro for more generations.",
  tfNeedProject: "Create a project first to save this generation.",
  guestDraftsName: "Drafts",
  defaultProjectName: "Default",
  guestSyncFailed: "Could not import your guest projects. Your local drafts are still saved — try again.",
  guestSyncNotClaimable:
    "Some guest generations could not be claimed (missing or already owned). Your local drafts are still saved.",
  guestSyncLimitsExceeded:
    "Too many guest projects or generations to import at once. Remove some drafts and try again.",
  tfGenerating: "Creating…",
  tfGeneratingHint: "Usually takes a few seconds.",
  tfCancelRequest: "Cancel request",
  authGateTitle: "Sign in to continue",
  authGateSubtitle: "Create a free account to keep your projects and certificates in one place.",
  authGateCta: "Sign in or create account",
  retry: "Try again",
  proModalTitle: "Create without limits",
  proModalDesc:
    "Unique psychotherapeutic content and ideas tailored for you by ChatGPT and 30 other AI engines — without writing complex prompts.",
  proModalPriceWas: "$49",
  proModalPriceNow: "$19.99",
  proModalOffer: "Join for unlimited requests · today’s special offer",
  proModalRating: "4.9 · 3,920 reviews",
  proModalQuote: "“Best app for psychotherapists in 2025.” — Open Academy",
  proModalTrusted: "Trusted by",
  proModalTrustedLabel: "specialists",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Over 50 creative modules",
      desc: "Create any necessary content to easen your work.",
    },
    {
      emoji: "🐣",
      title: "No tech skills needed",
      desc: "Use simple forms instead of typing complex prompts manually.",
    },
    {
      emoji: "🚩",
      title: "Regional adjustments",
      desc: "Adjust content to your local standards.",
    },
    {
      emoji: "🧠",
      title: "Personalised results",
      desc: "Get content based on patient history and your style.",
    },
    {
      emoji: "⌛",
      title: "No waiting time",
      desc: "Generate content and ideas quickly without waiting.",
    },
    {
      emoji: "🚀",
      title: "Unlimited quota",
      desc: "Create as much content as you need.",
    },
  ],
  proModalCta: "Get full access",
  proModalClose: "Close",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow, and Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Time-saving",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemia is an absolute game-changer for therapists seeking to streamline their work. As a therapist, time is always of the essence, and Aidemia understands this perfectly. Its intuitive interface and plethora of AI-powered tools significantly cut down the time required to prepare and analyse my sessions.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Affordable",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemia isn't just a time-saver, it's also a money-saver. By consolidating the features of numerous AI tools into one platform, Aidemia eliminates the need for costly subscriptions to multiple services. As an psychotherapist, I can't underestimate the range of possibilities and the value it provides to me daily.",
    },
  ],
  proModalDataProtected: "Your data is protected",
  proModalTermsShort: "Terms",
  proModalGuarantee: "14-day money-back guarantee",
  proModalLegalNote:
    "The discount is applied. Your pass gives you access to all types of content, including future features without increasing costs. You can cancel any time or autorenew every four weeks. Part of your contribution goes to childcare charities.",
};
const es: TranslationStrings = {
  navProjects: "Proyectos",
  navHistory: "Historial",
  navTools: "Herramientas",
  navChat: "Chat",
  navCerts: "Certificados",
  navAccount: "Cuenta",
  toolsTitle: "Herramientas",
  toolsDesc:
    "Herramientas de IA para cada etapa del trabajo clínico, de la derivación al seguimiento. Cada una produce un borrador que tú revisas.",
  toolsCountLabel: "herramientas",
  all: "Todas",
  projectsTitle: "Proyectos",
  projectsDesc: "Todo lo que has generado, organizado por caso.",
  projTab: "Proyectos",
  histTab: "Historial",
  historyTitle: "Historial",
  historyDesc: "Todos los borradores que has generado — en todos los proyectos, primero los más recientes.",
  createProject: "Crear proyecto",
  editProject: "Editar proyecto",
  emptyProjectsTitle: "Aún no hay proyectos",
  emptyProjectsDesc:
    "Crea un proyecto para un cliente o caso. Los borradores que generes vivirán aquí, organizados y fáciles de encontrar.",
  projectsProCtaTitle: "Organiza tu trabajo en proyectos",
  projectsProCtaDesc:
    "Pasa a Pro para crear proyectos personalizados por cliente o caso — mantén cada borrador ordenado y fácil de encontrar.",
  projectsProCtaBenefits: [
    "Crea proyectos personalizados sin límite",
    "Separa el trabajo por cliente o caso",
    "Guarda cada borrador en su lugar",
  ],
  emptyHistoryTitle: "Aún no hay historial",
  emptyHistoryDesc:
    "Cuando generes un borrador, aparecerá aquí — primero los más recientes.",
  emptyHistoryCta: "Explorar herramientas",
  notFoundTitle: "No se encontró esta página",
  notFoundDesc:
    "La herramienta, el proyecto o el borrador que buscas no existe o se ha movido.",
  notFoundCtaTools: "Volver a Herramientas",
  notFoundCtaProjects: "Ir a Proyectos",
  paginationPrevious: "Anterior",
  paginationNext: "Siguiente",
  paginationPageOf: "Página {page} de {totalPages}",
  paginationAria: "Paginación de proyectos",
  chatTitle: "Asistente",
  chatDesc: "Piensa en voz alta con un asistente que conoce tus herramientas.",
  chatPlaceholder: "Pregunta lo que quieras…",
  chatSend: "Enviar",
  certsTitle: "Certificado de membresía",
  certsDesc:
    "Membresía completa en la World Innovative Teaching Association con un certificado impreso y verificable — independiente de tu suscripción Aidemia Pro.",
  certsObserverNote:
    "Eres miembro observador de la World Innovative Teaching Association. Pasa a la membresía completa para recibir tu certificado.",
  certsRepurchaseNote:
    "Tu acceso de membresía anterior ya no está activo. Puedes comprar el certificado de nuevo.",
  certsWhyTitle: "Por qué obtener el certificado",
  certsBenefits: [
    "Prueba oficial de membresía profesional en WITA",
    "Número de serie único con validación en línea",
    "Certificado horizontal listo para imprimir o compartir",
    "Credencial reconocida para centros educativos e instituciones",
    "Registro en el listado de miembros de la asociación",
    "Membresía mensual a un precio por tiempo limitado",
  ],
  certsPlanLabel: "Membresía mensual",
  certsPriceWas: "100,00 $",
  certsPriceNow: "49,99 $",
  certsNotInPro:
    "No incluido en Aidemia Pro. Solo pagas el certificado de membresía y la membresía completa en WITA.",
  certsCta: "Unirme y obtener el certificado",
  certsSecurePay: "Pago seguro en nuestra página de pago.",
  certsSpecimen: "Muestra",
  certsSpecimenCaption:
    "Muestra — no es un certificado emitido. Vista previa del diseño oficial de WITA.",
  certsSpecimenAlt: "Muestra — vista previa del certificado de membresía",
  certsPurchasedNote:
    "Tu certificado de membresía completa de la World Innovative Teaching Association está activo.",
  certsSerialNumber: "Número de serie del documento: {serial}",
  certsIncludesTitle: "Tu membresía incluye",
  certsDownload: "Descargar certificado",
  certsDownloading: "Descargando…",
  certsDownloadError: "No se pudo descargar el certificado. Inténtalo de nuevo.",
  certsPreviewCaption:
    "Tu certificado emitido usa el nombre de tu perfil y el número de serie.",
  certsPreviewAlt: "Vista previa del certificado de membresía",
  certsProfileIncompleteBoth:
    "Tu nombre y apellido no están completos. El certificado usa el nombre de tu perfil.",
  certsProfileIncompleteFirst:
    "Tu nombre no está completo. El certificado usa el nombre de tu perfil.",
  certsProfileIncompleteLast:
    "Tu apellido no está completo. El certificado usa el nombre de tu perfil.",
  certsProfileIncompleteLink: "Completa tu perfil",
  certsVerifyEyebrow: "Membresía WITA",
  certsVerifyTitle: "Verificar un certificado",
  certsVerifyDesc:
    "Introduce el número de serie del documento para confirmar que un certificado de membresía es auténtico y está vigente.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "La verificación confirma solo la validez: nunca se muestran datos personales.",
  certsVerifyLabel: "Número de serie del documento",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Formato: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Introduce el número de serie del certificado.",
  certsVerifyFormatError: "Introduce un número como CERT-2026-000001.",
  certsVerifyCta: "Verificar certificado",
  certsVerifyChecking: "Comprobando…",
  certsVerifyValidTitle: "El certificado es válido",
  certsVerifyValidBody:
    "Este certificado de membresía se emitió el {date} y está activo.",
  certsVerifyInvalidTitle: "Certificado no verificado",
  certsVerifyInvalidBody:
    "No pudimos confirmar este número. Puede estar mal escrito, caducado o ya no activo.",
  certsVerifyAgain: "Comprobar otro número",
  certsVerifyError:
    "No se pudo contactar el servicio de verificación. Revisa tu conexión e inténtalo de nuevo.",
  certsVerifyBackToCerts: "Volver al certificado de membresía",
  certsVerifyOpenLink: "Verificar un número de certificado",
  accountTitle: "Cuenta",
  accountEmailVerified: "Correo confirmado",
  accountEmailUnverified: "Correo no confirmado",
  accountResendConfirmation: "Reenviar correo de confirmación",
  accountResendConfirmationSending: "Enviando…",
  accountResendConfirmationSent: "Correo de confirmación enviado. Revisa tu bandeja de entrada.",
  emailConfirmConfirming: "Confirmando tu correo…",
  emailConfirmSuccessTitle: "Correo confirmado",
  emailConfirmSuccessBody:
    "Tu dirección de correo ha sido verificada. Puedes seguir usando Aidemia.",
  emailConfirmInvalidTitle: "Enlace de confirmación no válido",
  emailConfirmInvalidBody:
    "Este enlace de confirmación no es válido o ha caducado. Solicita un nuevo correo de confirmación e inténtalo de nuevo.",
  emailConfirmMissingTitle: "Falta el enlace de confirmación",
  emailConfirmMissingBody:
    "Abre el enlace de confirmación de tu correo para verificar tu dirección.",
  emailConfirmErrorTitle: "No se pudo confirmar el correo",
  emailConfirmGoToAccount: "Ir a la cuenta",
  emailConfirmGoHome: "Volver al inicio",
  loginLinkConsuming: "Iniciando sesión…",
  loginLinkMissingTitle: "El enlace no es válido",
  loginLinkMissingBody:
    "A este enlace de acceso le falta el token. Abre de nuevo el enlace del correo o de la confirmación de pago.",
  loginLinkInvalidTitle: "Enlace caducado o ya usado",
  loginLinkInvalidBody:
    "Este enlace de acceso ha caducado o ya se ha usado. Solicita uno nuevo para continuar.",
  loginLinkErrorTitle: "No se pudo iniciar sesión",
  loginLinkGoHome: "Volver al inicio",
  projectLandingEyebrow: "Tu proyecto",
  projectLandingBody: "Has iniciado sesión. Abre el espacio de trabajo para continuar.",
  projectLandingMissingTitle: "Proyecto no encontrado",
  projectLandingMissingBody: "Este proyecto no existe o no tienes acceso a él.",
  projectLandingErrorTitle: "No se pudo cargar el proyecto",
  projectLandingGoHome: "Volver al inicio",
  projectLandingOpenWorkspace: "Abrir espacio de trabajo",
  paymentCheckoutSuccessTitle: "Pago realizado",
  paymentCheckoutSuccessBody:
    "Gracias. Tu pago se ha completado. El acceso Pro aparecerá en tu cuenta en breve.",
  paymentCheckoutSuccessCompleting: "Confirmando tu pago…",
  paymentCheckoutSuccessGuestBody:
    "Gracias. Tu cuenta está lista: te hemos enviado una contraseña por correo. Ya has iniciado sesión y el acceso Pro está activo.",
  paymentCheckoutSuccessExistingAccountBody:
    "Gracias. Tu pago está vinculado a tu cuenta existente. Inicia sesión con tu contraseña habitual para continuar.",
  paymentCheckoutSuccessSignIn: "Iniciar sesión",
  paymentCheckoutSuccessGoToAccount: "Ir a la cuenta",
  paymentCheckoutSuccessGoToTools: "Volver a herramientas",
  paymentCheckoutSuccessGoToCertificates: "Volver a certificados",
  paymentCheckoutCancelTitle: "Pago cancelado",
  paymentCheckoutCancelBody:
    "El pago se canceló. No se ha cobrado nada. Puedes intentarlo de nuevo cuando quieras.",
  paymentCheckoutCancelRetry: "Intentar de nuevo",
  paymentCheckoutCancelGoToTools: "Volver a herramientas",
  paymentCheckoutCancelGoToCertificates: "Volver a certificados",
  accountSubscription: "Suscripción",
  accountDataPrivacy: "Datos y privacidad",
  accountPlanFree: "Gratis",
  accountProfileHeading: "Tu nombre",
  accountFirstName: "Nombre",
  accountLastName: "Apellidos",
  accountFirstNamePh: "Nombre de pila",
  accountLastNamePh: "Apellidos",
  accountSaveProfile: "Guardar cambios",
  accountProfileSaved: "Perfil actualizado.",
  menuRecents: "Recientes",
  menuRecentsEmpty: "Aún no hay generaciones recientes",
  menuRecentsShowMore: "Ver más",
  menuAccountSettings: "Configuración de la cuenta",
  menuAbout: "Acerca de Aidemia",
  menuPersonalize: "Personalizar",
  menuPrivacy: "Política de privacidad",
  menuTerms: "Términos y condiciones",
  menuContact: "Contáctanos",
  signIn: "Iniciar sesión",
  signOut: "Cerrar sesión",
  itemDownload: "Descargar",
  itemEdit: "Editar",
  itemBodyLabel: "Contenido",
  itemGeneratedWith: "Generado con",
  itemProject: "Proyecto",
  itemGenerateNew: "Generar uno nuevo",
  itemDraftNote: "Borrador generado por Aidemia — revísalo y edítalo antes de usarlo.",
  audioPlay: "Reproducir",
  audioPause: "Pausar",
  audioMute: "Silenciar",
  audioUnmute: "Activar sonido",
  audioSeek: "Buscar",
  audioVolume: "Volumen",
  audioError: "No se pudo cargar este audio. Prueba a descargar el archivo.",
  audioLabel: "Audio",
  cpSubtitle: "Un espacio para el trabajo de un caso.",
  cpNameLabel: "Nombre del proyecto",
  cpNamePh: "p. ej., Cliente S. — Pánico",
  cpDescLabel: "Descripción",
  cpOptional: "opcional",
  cpRequired: "obligatorio",
  cpDescPh: "Motivo de consulta, modalidad, etapa del tratamiento…",
  cpAsideTitle: "Sobre los proyectos",
  cpAsideText:
    "Un proyecto reúne todo el trabajo de un caso en un solo lugar — notas, planes y cartas — para encontrarlo fácilmente.",
  cpAsideHint: "Consejo: usa iniciales o un código breve en lugar del nombre completo.",
  cpCancel: "Cancelar",
  cpCreate: "Crear",
  cpSave: "Guardar",
  cpEmptyProjTitle: "Este proyecto está vacío",
  cpEmptyProj:
    "Aún no hay nada — los borradores que generes en este proyecto aparecerán aquí.",
  tfTitleLabel: "Título del borrador",
  tfTitlePh: "p. ej., Preparación — semana 7",
  tfProjectLabel: "Guardar en proyecto",
  tfContextLabel: "Contexto clínico",
  tfContextPh: "Pega notas de sesión, una carta de derivación o unas líneas sobre el cliente…",
  tfToneLabel: "Tono",
  tfToneOpts: ["Cálido", "Neutro", "Clínico"],
  tfNextLabel: "Sugerir próximos pasos",
  tfDeepLabel: "Razonamiento clínico profundo",
  tfReset: "Restablecer",
  tfGenerate: "Generar",
  tfMoreIn: "Más de esta categoría",
  tfMoreEmptyTitle: "Aún no hay más herramientas",
  tfMoreEmptyDesc: "Por ahora es la única herramienta de esta categoría.",
  badgePro: "PRO",
  badgeExp: "EXPERIMENTAL",
  tfToday: "Hoy",
  tfGenBlockH: "Borrador",
  tfGenBlockP:
    "Un primer borrador estructurado a partir de tu contexto — listo para revisar, editar y hacer tuyo.",
  tfCtxBlockH: "Tu contexto",
  tfClientLabel: "Referencia del cliente",
  tfClientPh: "p. ej., Cliente M. (solo iniciales)",
  tfModalityLabel: "Modalidad terapéutica",
  tfModalityOpts: ["TCC", "ACT", "TDC", "Psicodinámica", "Centrada en la persona", "Integradora"],
  tfFocusLabel: "Áreas de enfoque",
  tfFocusPh: "Temas, objetivos o lo que el borrador deba enfatizar…",
  tfFormatLabel: "Formato de salida",
  tfFormatOpts: ["Viñetas", "Prosa", "Nota SOAP", "Carta"],
  tfLengthLabel: "Extensión",
  tfLengthOpts: ["Breve", "Estándar", "Detallado"],
  tfReadingLabel: "Nivel de lectura",
  tfReadingOpts: ["Lenguaje sencillo", "Estándar", "Clínico"],
  tfRiskLabel: "Incluir cribado de riesgo",
  tfMeasuresLabel: "Referir medidas de resultado",
  tfAnonLabel: "Anonimizar datos del cliente",
  ctxTitle: "Contexto del proyecto",
  ctxNotesLabel: "Notas",
  ctxNotesPh:
    "Añade tu propio contexto — antecedentes, objetivos, señales de riesgo, preferencias, lo que quieras recordar…",
  ctxPrivate: "Privado · guardado en este dispositivo",
  ctxSummaryLabel: "De tus borradores",
  ctxAuto: "Auto",
  ctxUpdate: "Actualizar",
  ctxSavedTag: "Guardado",
  ctxToolsUsed: "Herramientas usadas",
  ctxNotesHelp:
    "Se usa cuando seleccionas este proyecto en una Herramienta de IA, para enriquecer los borradores que genera.",
  ctxSummaryTpl: "{n} {drafts} hasta ahora, el más reciente el {date}.",
  ctxDraftWord: "borrador",
  ctxDraftsWord: "borradores",
  ctxCovers: "Cubre",
  ctxEmptySummary:
    "Aún no hay borradores — aquí aparecerá un resumen a medida que generes trabajo en este proyecto.",
  authTitle: "Protege tu cuenta",
  authSubtitle: "Tener una cuenta te ayuda a mantener seguros tus datos y tu estado PRO.",
  authGoogle: "Continuar con Google",
  authApple: "Continuar con Apple",
  authEmail: "Continuar con email",
  authGuest: "Continuar como invitado",
  authClose: "Cerrar",
  authBack: "Atrás",
  authOr: "o",
  authEmailLabel: "Email",
  authEmailPh: "tu@ejemplo.com",
  authPasswordLabel: "Contraseña",
  authPasswordPh: "••••••••",
  authPasswordHint: "Al menos 8 caracteres.",
  authRestoreAccess: "¿Olvidaste la contraseña?",
  authRestoreTitle: "Restablecer acceso",
  authRestoreSubtitle: "Introduce tu email y te enviaremos una nueva contraseña.",
  authRestoreSubmit: "Enviar nueva contraseña",
  authRestoreSuccess: "Si existe una cuenta con este email, ya está en camino una nueva contraseña.",
  authSignInTitle: "Iniciar sesión",
  authSignUpTitle: "Crea tu cuenta",
  authSubmitSignIn: "Iniciar sesión",
  authSubmitSignUp: "Crear cuenta",
  authSubmitting: "Un momento…",
  authSwitchToSignUp: "¿No tienes cuenta? Regístrate",
  authSwitchToSignIn: "¿Ya tienes cuenta? Inicia sesión",
  authErrorFallback: "Algo salió mal. Inténtalo de nuevo.",
  authTooManyAttempts: "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
  authEmailRequired: "El correo es obligatorio.",
  authEmailInvalid: "Introduce un correo válido.",
  authPasswordRequired: "La contraseña es obligatoria.",
  authPasswordTooShort: "La contraseña debe tener al menos 8 caracteres.",
  authInvalidCredentials: "Credenciales incorrectas.",
  authEmailTaken: "Este correo ya está registrado.",
  formCheckFields: "Revisa los campos resaltados.",
  validationFieldRequired: "{field} es obligatorio.",
  validationFieldEmail: "Introduce un {field} válido.",
  validationFieldTaken: "Este {field} ya está en uso.",
  validationFieldMin: "{field} debe tener al menos {min} caracteres.",
  validationFieldMax: "{field} debe tener como máximo {max} caracteres.",
  fieldLabelEmail: "Correo",
  fieldLabelPassword: "Contraseña",
  fieldLabelName: "Nombre",
  fieldLabelDescription: "Descripción",
  fieldLabelNotes: "Notas",
  fieldLabelTitle: "Título",
  fieldLabelFirstName: "Nombre",
  fieldLabelLastName: "Apellidos",
  cpNameRequired: "El nombre del proyecto es obligatorio.",
  hiTitleRequired: "El título es obligatorio.",
  tfRunError: "No se pudo generar. Inténtalo de nuevo.",
  tfUsageLimitGuest: "Límite de invitado alcanzado. Inicia sesión o crea una cuenta para continuar.",
  tfUsageLimitFree: "Límite gratuito alcanzado. Mejora a Pro para más generaciones.",
  tfNeedProject: "Crea un proyecto primero para guardar esta generación.",
  guestDraftsName: "Borradores",
  defaultProjectName: "Predeterminado",
  guestSyncFailed:
    "No se pudieron importar tus proyectos de invitado. Tus borradores locales siguen guardados — inténtalo de nuevo.",
  guestSyncNotClaimable:
    "Algunas generaciones de invitado no se pudieron reclamar (faltan o ya pertenecen a otro). Tus borradores locales siguen guardados.",
  guestSyncLimitsExceeded:
    "Hay demasiados proyectos o generaciones de invitado para importar de una vez. Elimina algunos borradores e inténtalo de nuevo.",
  tfGenerating: "Creando…",
  tfGeneratingHint: "Suele tardar unos segundos.",
  tfCancelRequest: "Cancelar solicitud",
  authGateTitle: "Inicia sesión para continuar",
  authGateSubtitle:
    "Crea una cuenta gratuita para mantener tus proyectos y certificados en un solo lugar.",
  authGateCta: "Iniciar sesión o crear cuenta",
  retry: "Reintentar",
  proModalTitle: "Crea sin límites",
  proModalDesc:
    "Contenido e ideas psicoterapéuticas únicos, adaptados para ti con ChatGPT y otros 30 motores de IA — sin escribir prompts complejos.",
  proModalPriceWas: "49 $",
  proModalPriceNow: "19,99 $",
  proModalOffer: "Únete para solicitudes ilimitadas · oferta especial de hoy",
  proModalRating: "4,9 · 3.920 reseñas",
  proModalQuote: "«La mejor app para psicoterapeutas en 2025.» — Open Academy",
  proModalTrusted: "La confían",
  proModalTrustedLabel: "especialistas",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Más de 50 módulos creativos",
      desc: "Crea cualquier contenido necesario para aliviar tu trabajo.",
    },
    {
      emoji: "🐣",
      title: "Sin habilidades técnicas",
      desc: "Usa formularios simples en lugar de escribir prompts complejos a mano.",
    },
    {
      emoji: "🚩",
      title: "Ajustes regionales",
      desc: "Adapta el contenido a tus estándares locales.",
    },
    {
      emoji: "🧠",
      title: "Resultados personalizados",
      desc: "Obtén contenido según el historial del paciente y tu estilo.",
    },
    {
      emoji: "⌛",
      title: "Sin tiempo de espera",
      desc: "Genera contenido e ideas rápido, sin esperar.",
    },
    {
      emoji: "🚀",
      title: "Cuota ilimitada",
      desc: "Crea tanto contenido como necesites.",
    },
  ],
  proModalCta: "Obtener acceso completo",
  proModalClose: "Cerrar",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow y Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Ahorra tiempo",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemia cambia por completo el trabajo de quienes buscan agilizar su práctica. Como terapeuta, el tiempo siempre es oro, y Aidemia lo entiende a la perfección. Su interfaz intuitiva y sus herramientas de IA reducen mucho el tiempo de preparación y análisis.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Asequible",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemia no solo ahorra tiempo: también ahorra dinero. Al reunir las funciones de muchas herramientas de IA en una sola plataforma, elimina la necesidad de varias suscripciones caras. Como psicoterapeuta, no puedo subestimar el abanico de posibilidades y el valor que me aporta cada día.",
    },
  ],
  proModalDataProtected: "Tus datos están protegidos",
  proModalTermsShort: "Términos",
  proModalGuarantee: "Garantía de devolución de 14 días",
  proModalLegalNote:
    "El descuento está aplicado. Tu pase da acceso a todo tipo de contenido, incluidas funciones futuras sin subir el precio. Puedes cancelar en cualquier momento o renovar automáticamente cada cuatro semanas. Parte de tu aportación va a obras benéficas infantiles.",
};

const de: TranslationStrings = {
  navProjects: "Projekte",
  navHistory: "Verlauf",
  navTools: "Tools",
  navChat: "Chat",
  navCerts: "Zertifikate",
  navAccount: "Konto",
  toolsTitle: "Tools",
  toolsDesc:
    "KI-Tools für jede Phase der klinischen Arbeit — von der Überweisung bis zur Nachsorge. Jedes erstellt einen Entwurf, den Sie prüfen.",
  toolsCountLabel: "Tools",
  all: "Alle",
  projectsTitle: "Projekte",
  projectsDesc: "Alles, was Sie erstellt haben, nach Fall geordnet.",
  projTab: "Projekte",
  histTab: "Verlauf",
  historyTitle: "Verlauf",
  historyDesc: "Alle Entwürfe, die Sie erstellt haben — über alle Projekte hinweg, Neueste zuerst.",
  createProject: "Projekt erstellen",
  editProject: "Projekt bearbeiten",
  emptyProjectsTitle: "Noch keine Projekte",
  emptyProjectsDesc:
    "Legen Sie ein Projekt für einen Klienten oder Fall an. Entwürfe, die Sie erzeugen, erscheinen hier — geordnet und leicht auffindbar.",
  projectsProCtaTitle: "Organisieren Sie Ihre Arbeit in Projekten",
  projectsProCtaDesc:
    "Mit Pro erstellen Sie eigene Projekte für jeden Klienten oder Fall — Entwürfe bleiben strukturiert und leicht auffindbar.",
  projectsProCtaBenefits: [
    "Unbegrenzt eigene Projekte erstellen",
    "Arbeit nach Klient oder Fall trennen",
    "Jeden Entwurf am richtigen Ort behalten",
  ],
  emptyHistoryTitle: "Noch kein Verlauf",
  emptyHistoryDesc:
    "Wenn Sie einen Entwurf erzeugen, erscheint er hier — die neuesten zuerst.",
  emptyHistoryCta: "Tools durchsuchen",
  notFoundTitle: "Diese Seite wurde nicht gefunden",
  notFoundDesc:
    "Das gesuchte Tool, Projekt oder der Entwurf existiert nicht oder wurde verschoben.",
  notFoundCtaTools: "Zurück zu Tools",
  notFoundCtaProjects: "Zu Projekten",
  paginationPrevious: "Zurück",
  paginationNext: "Weiter",
  paginationPageOf: "Seite {page} von {totalPages}",
  paginationAria: "Projekt-Paginierung",
  chatTitle: "Assistent",
  chatDesc: "Denken Sie laut mit einem Assistenten, der Ihre Tools kennt.",
  chatPlaceholder: "Fragen Sie etwas…",
  chatSend: "Senden",
  certsTitle: "Mitgliedschaftszertifikat",
  certsDesc:
    "Vollmitgliedschaft in der World Innovative Teaching Association mit druckbarem, überprüfbarem Zertifikat — getrennt von Ihrem Aidemia-Pro-Abo.",
  certsObserverNote:
    "Sie sind beobachtendes Mitglied der World Innovative Teaching Association. Wechseln Sie zur Vollmitgliedschaft, um Ihr Zertifikat zu erhalten.",
  certsRepurchaseNote:
    "Ihr früherer Mitgliedschaftszugang ist nicht mehr aktiv. Sie können das Zertifikat erneut kaufen.",
  certsWhyTitle: "Warum das Zertifikat holen",
  certsBenefits: [
    "Offizieller Nachweis der professionellen WITA-Mitgliedschaft",
    "Eindeutige Seriennummer mit Online-Prüfung",
    "Querformat-Zertifikat zum Drucken oder Versenden",
    "Anerkannte Qualifikation für Schulen und Institutionen",
    "Eintrag im Mitgliederverzeichnis der Association",
    "Monatsmitgliedschaft zum zeitlich begrenzten Preis",
  ],
  certsPlanLabel: "Monatsmitgliedschaft",
  certsPriceWas: "100,00 $",
  certsPriceNow: "49,99 $",
  certsNotInPro:
    "Nicht in Aidemia Pro enthalten. Sie zahlen nur für das Mitgliedschaftszertifikat und die WITA-Vollmitgliedschaft.",
  certsCta: "Beitreten und Zertifikat erhalten",
  certsSecurePay: "Sichere Zahlung auf unserer Zahlungsseite.",
  certsSpecimen: "Muster",
  certsSpecimenCaption:
    "Muster — kein ausgestelltes Zertifikat. Vorschau des offiziellen WITA-Designs.",
  certsSpecimenAlt: "Muster — Vorschau des Mitgliedschaftszertifikats",
  certsPurchasedNote:
    "Ihr Vollmitgliedschaftszertifikat der World Innovative Teaching Association ist aktiv.",
  certsSerialNumber: "Dokumenten-Seriennummer: {serial}",
  certsIncludesTitle: "Ihre Mitgliedschaft umfasst",
  certsDownload: "Zertifikat herunterladen",
  certsDownloading: "Wird heruntergeladen…",
  certsDownloadError: "Das Zertifikat konnte nicht heruntergeladen werden. Bitte erneut versuchen.",
  certsPreviewCaption:
    "Ihr ausgestelltes Zertifikat verwendet Ihren Profilnamen und die Seriennummer.",
  certsPreviewAlt: "Vorschau des Mitgliedschaftszertifikats",
  certsProfileIncompleteBoth:
    "Vor- und Nachname sind nicht ausgefüllt. Das Zertifikat verwendet den Namen aus Ihrem Profil.",
  certsProfileIncompleteFirst:
    "Ihr Vorname ist nicht ausgefüllt. Das Zertifikat verwendet den Namen aus Ihrem Profil.",
  certsProfileIncompleteLast:
    "Ihr Nachname ist nicht ausgefüllt. Das Zertifikat verwendet den Namen aus Ihrem Profil.",
  certsProfileIncompleteLink: "Profil ausfüllen",
  certsVerifyEyebrow: "WITA-Mitgliedschaft",
  certsVerifyTitle: "Zertifikat prüfen",
  certsVerifyDesc:
    "Geben Sie die Dokumentennummer ein, um zu bestätigen, dass ein Mitgliedschaftszertifikat echt und derzeit gültig ist.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "Die Prüfung bestätigt nur die Gültigkeit — persönliche Daten werden nicht angezeigt.",
  certsVerifyLabel: "Dokumenten-Seriennummer",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Format: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Bitte eine Zertifikatsnummer eingeben.",
  certsVerifyFormatError: "Bitte eine Nummer wie CERT-2026-000001 eingeben.",
  certsVerifyCta: "Zertifikat prüfen",
  certsVerifyChecking: "Wird geprüft…",
  certsVerifyValidTitle: "Zertifikat ist gültig",
  certsVerifyValidBody:
    "Dieses Mitgliedschaftszertifikat wurde am {date} ausgestellt und ist derzeit aktiv.",
  certsVerifyInvalidTitle: "Zertifikat nicht bestätigt",
  certsVerifyInvalidBody:
    "Diese Nummer konnte nicht bestätigt werden. Sie ist möglicherweise falsch, abgelaufen oder nicht mehr aktiv.",
  certsVerifyAgain: "Andere Nummer prüfen",
  certsVerifyError:
    "Der Prüfdienst ist nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.",
  certsVerifyBackToCerts: "Zurück zum Mitgliedschaftszertifikat",
  certsVerifyOpenLink: "Zertifikatsnummer prüfen",
  accountTitle: "Konto",
  accountEmailVerified: "E-Mail bestätigt",
  accountEmailUnverified: "E-Mail nicht bestätigt",
  accountResendConfirmation: "Bestätigungs-E-Mail erneut senden",
  accountResendConfirmationSending: "Wird gesendet…",
  accountResendConfirmationSent: "Bestätigungs-E-Mail gesendet. Bitte Posteingang prüfen.",
  emailConfirmConfirming: "E-Mail wird bestätigt…",
  emailConfirmSuccessTitle: "E-Mail bestätigt",
  emailConfirmSuccessBody:
    "Ihre E-Mail-Adresse wurde verifiziert. Sie können Aidemia weiter nutzen.",
  emailConfirmInvalidTitle: "Bestätigungslink ungültig",
  emailConfirmInvalidBody:
    "Dieser Bestätigungslink ist ungültig oder abgelaufen. Fordern Sie eine neue Bestätigungs-E-Mail an und versuchen Sie es erneut.",
  emailConfirmMissingTitle: "Bestätigungslink fehlt",
  emailConfirmMissingBody:
    "Öffnen Sie den Bestätigungslink aus Ihrer E-Mail, um Ihre Adresse zu verifizieren.",
  emailConfirmErrorTitle: "E-Mail konnte nicht bestätigt werden",
  emailConfirmGoToAccount: "Zum Konto",
  emailConfirmGoHome: "Zur Startseite",
  loginLinkConsuming: "Anmeldung läuft…",
  loginLinkMissingTitle: "Link ungültig",
  loginLinkMissingBody:
    "Diesem Anmeldelink fehlt ein Token. Öffnen Sie den Link aus der E-Mail oder Zahlungsbestätigung erneut.",
  loginLinkInvalidTitle: "Link abgelaufen oder bereits verwendet",
  loginLinkInvalidBody:
    "Dieser Anmeldelink ist abgelaufen oder wurde bereits verwendet. Fordern Sie einen neuen an, um fortzufahren.",
  loginLinkErrorTitle: "Anmeldung fehlgeschlagen",
  loginLinkGoHome: "Zur Startseite",
  projectLandingEyebrow: "Ihr Projekt",
  projectLandingBody: "Sie sind angemeldet. Öffnen Sie den Workspace, um fortzufahren.",
  projectLandingMissingTitle: "Projekt nicht gefunden",
  projectLandingMissingBody: "Dieses Projekt existiert nicht oder Sie haben keinen Zugriff darauf.",
  projectLandingErrorTitle: "Projekt konnte nicht geladen werden",
  projectLandingGoHome: "Zur Startseite",
  projectLandingOpenWorkspace: "Workspace öffnen",
  paymentCheckoutSuccessTitle: "Zahlung erfolgreich",
  paymentCheckoutSuccessBody:
    "Danke. Deine Zahlung war erfolgreich. Der Pro-Zugang erscheint in Kürze in deinem Konto.",
  paymentCheckoutSuccessCompleting: "Zahlung wird bestätigt…",
  paymentCheckoutSuccessGuestBody:
    "Danke. Dein Konto ist bereit — wir haben dir ein Passwort per E-Mail geschickt. Du bist angemeldet und der Pro-Zugang ist aktiv.",
  paymentCheckoutSuccessExistingAccountBody:
    "Danke. Deine Zahlung ist mit deinem bestehenden Konto verknüpft. Melde dich mit deinem üblichen Passwort an, um fortzufahren.",
  paymentCheckoutSuccessSignIn: "Anmelden",
  paymentCheckoutSuccessGoToAccount: "Zum Konto",
  paymentCheckoutSuccessGoToTools: "Zurück zu den Tools",
  paymentCheckoutSuccessGoToCertificates: "Zurück zu den Zertifikaten",
  paymentCheckoutCancelTitle: "Zahlung abgebrochen",
  paymentCheckoutCancelBody:
    "Der Checkout wurde abgebrochen. Es wurde nichts berechnet. Du kannst es jederzeit erneut versuchen.",
  paymentCheckoutCancelRetry: "Erneut versuchen",
  paymentCheckoutCancelGoToTools: "Zurück zu den Tools",
  paymentCheckoutCancelGoToCertificates: "Zurück zu den Zertifikaten",
  accountSubscription: "Abo",
  accountDataPrivacy: "Daten & Datenschutz",
  accountPlanFree: "Kostenlos",
  accountProfileHeading: "Dein Name",
  accountFirstName: "Vorname",
  accountLastName: "Nachname",
  accountFirstNamePh: "Vorname",
  accountLastNamePh: "Nachname",
  accountSaveProfile: "Änderungen speichern",
  accountProfileSaved: "Profil aktualisiert.",
  menuRecents: "Zuletzt",
  menuRecentsEmpty: "Noch keine letzten Generierungen",
  menuRecentsShowMore: "Mehr anzeigen",
  menuAccountSettings: "Kontoeinstellungen",
  menuAbout: "Über Aidemia",
  menuPersonalize: "Personalisieren",
  menuPrivacy: "Datenschutz",
  menuTerms: "AGB",
  menuContact: "Kontakt",
  signIn: "Anmelden",
  signOut: "Abmelden",
  itemDownload: "Herunterladen",
  itemEdit: "Bearbeiten",
  itemBodyLabel: "Inhalt",
  itemGeneratedWith: "Erstellt mit",
  itemProject: "Projekt",
  itemGenerateNew: "Neu generieren",
  itemDraftNote: "Von Aidemia erstellter Entwurf — vor Verwendung prüfen und bearbeiten.",
  audioPlay: "Abspielen",
  audioPause: "Pause",
  audioMute: "Stummschalten",
  audioUnmute: "Ton einschalten",
  audioSeek: "Position",
  audioVolume: "Lautstärke",
  audioError: "Audio konnte nicht geladen werden. Versuchen Sie, die Datei herunterzuladen.",
  audioLabel: "Audio",
  cpSubtitle: "Ein Ort für die Arbeit an einem Fall.",
  cpNameLabel: "Projektname",
  cpNamePh: "z. B. Klient S. — Panik",
  cpDescLabel: "Beschreibung",
  cpOptional: "optional",
  cpRequired: "erforderlich",
  cpDescPh: "Anliegen, Methode, Behandlungsphase …",
  cpAsideTitle: "Über Projekte",
  cpAsideText:
    "Ein Projekt bündelt die gesamte Arbeit an einem Fall an einem Ort — Notizen, Pläne und Briefe — leicht wiederzufinden.",
  cpAsideHint: "Tipp: Initialen oder ein kurzes Kürzel statt vollständiger Namen verwenden.",
  cpCancel: "Abbrechen",
  cpCreate: "Erstellen",
  cpSave: "Speichern",
  cpEmptyProjTitle: "Dieses Projekt ist noch leer",
  cpEmptyProj:
    "Noch nichts hier — Entwürfe, die Sie in diesem Projekt erstellen, erscheinen hier.",
  tfTitleLabel: "Titel des Entwurfs",
  tfTitlePh: "z. B. Sitzungsvorbereitung — Woche 7",
  tfProjectLabel: "In Projekt speichern",
  tfContextLabel: "Klinischer Kontext",
  tfContextPh:
    "Sitzungsnotizen, ein Überweisungsschreiben oder ein paar Zeilen zum Klienten einfügen …",
  tfToneLabel: "Tonfall",
  tfToneOpts: ["Warm", "Neutral", "Klinisch"],
  tfNextLabel: "Nächste Schritte vorschlagen",
  tfDeepLabel: "Vertiefte klinische Analyse",
  tfReset: "Zurücksetzen",
  tfGenerate: "Generieren",
  tfMoreIn: "Mehr aus dieser Kategorie",
  tfMoreEmptyTitle: "Noch keine weiteren Tools",
  tfMoreEmptyDesc: "Dies ist derzeit das einzige Tool in dieser Kategorie.",
  badgePro: "PRO",
  badgeExp: "EXPERIMENTELL",
  tfToday: "Heute",
  tfGenBlockH: "Entwurf",
  tfGenBlockP:
    "Ein strukturierter erster Entwurf auf Basis Ihres Kontexts — bereit zum Prüfen, Bearbeiten und Übernehmen.",
  tfCtxBlockH: "Ihr Kontext",
  tfClientLabel: "Klientenreferenz",
  tfClientPh: "z. B. Klient M. (nur Initialen)",
  tfModalityLabel: "Therapeutische Methode",
  tfModalityOpts: ["VT", "ACT", "DBT", "Psychodynamisch", "Personzentriert", "Integrativ"],
  tfFocusLabel: "Schwerpunkte",
  tfFocusPh: "Themen, Ziele oder was der Entwurf betonen soll …",
  tfFormatLabel: "Ausgabeformat",
  tfFormatOpts: ["Stichpunkte", "Fließtext", "SOAP-Notiz", "Brief"],
  tfLengthLabel: "Länge",
  tfLengthOpts: ["Kurz", "Standard", "Ausführlich"],
  tfReadingLabel: "Leseniveau",
  tfReadingOpts: ["Einfache Sprache", "Standard", "Klinisch"],
  tfRiskLabel: "Risikoscreening einbeziehen",
  tfMeasuresLabel: "Ergebnismessungen referenzieren",
  tfAnonLabel: "Klientendaten anonymisieren",
  ctxTitle: "Projektkontext",
  ctxNotesLabel: "Notizen",
  ctxNotesPh:
    "Eigenen Kontext ergänzen — Hintergrund, Ziele, Risikohinweise, Vorlieben, was Sie sich merken möchten …",
  ctxPrivate: "Privat · auf diesem Gerät gespeichert",
  ctxSummaryLabel: "Aus Ihren Entwürfen",
  ctxAuto: "Auto",
  ctxUpdate: "Aktualisieren",
  ctxSavedTag: "Gespeichert",
  ctxToolsUsed: "Verwendete Tools",
  ctxNotesHelp:
    "Wird verwendet, wenn Sie dieses Projekt in einem KI-Tool auswählen, um die generierten Entwürfe anzureichern.",
  ctxSummaryTpl: "{n} {drafts} bisher, zuletzt am {date}.",
  ctxDraftWord: "Entwurf",
  ctxDraftsWord: "Entwürfe",
  ctxCovers: "Abgedeckt",
  ctxEmptySummary:
    "Noch keine Entwürfe — hier erscheint eine Zusammenfassung, sobald Sie in diesem Projekt Inhalte erstellen.",
  authTitle: "Konto sichern",
  authSubtitle: "Mit einem Konto sichern Sie Ihre App-Daten und Ihren PRO-Status.",
  authGoogle: "Mit Google fortfahren",
  authApple: "Mit Apple fortfahren",
  authEmail: "Mit E-Mail fortfahren",
  authGuest: "Als Gast fortfahren",
  authClose: "Schließen",
  authBack: "Zurück",
  authOr: "oder",
  authEmailLabel: "E-Mail",
  authEmailPh: "sie@beispiel.de",
  authPasswordLabel: "Passwort",
  authPasswordPh: "••••••••",
  authPasswordHint: "Mindestens 8 Zeichen.",
  authRestoreAccess: "Passwort vergessen?",
  authRestoreTitle: "Zugang wiederherstellen",
  authRestoreSubtitle: "Geben Sie Ihre E-Mail ein — wir senden Ihnen ein neues Passwort.",
  authRestoreSubmit: "Neues Passwort senden",
  authRestoreSuccess: "Wenn ein Konto mit dieser E-Mail existiert, ist ein neues Passwort unterwegs.",
  authSignInTitle: "Anmelden",
  authSignUpTitle: "Konto erstellen",
  authSubmitSignIn: "Anmelden",
  authSubmitSignUp: "Konto erstellen",
  authSubmitting: "Einen Moment…",
  authSwitchToSignUp: "Noch kein Konto? Registrieren",
  authSwitchToSignIn: "Bereits ein Konto? Anmelden",
  authErrorFallback: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
  authTooManyAttempts: "Zu viele Versuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.",
  authEmailRequired: "E-Mail ist erforderlich.",
  authEmailInvalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
  authPasswordRequired: "Passwort ist erforderlich.",
  authPasswordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein.",
  authInvalidCredentials: "Ungültige Anmeldedaten.",
  authEmailTaken: "Diese E-Mail ist bereits registriert.",
  formCheckFields: "Bitte prüfen Sie die markierten Felder.",
  validationFieldRequired: "{field} ist erforderlich.",
  validationFieldEmail: "Geben Sie ein gültiges {field} ein.",
  validationFieldTaken: "Dieses {field} ist bereits vergeben.",
  validationFieldMin: "{field} muss mindestens {min} Zeichen lang sein.",
  validationFieldMax: "{field} darf höchstens {max} Zeichen lang sein.",
  fieldLabelEmail: "E-Mail",
  fieldLabelPassword: "Passwort",
  fieldLabelName: "Name",
  fieldLabelDescription: "Beschreibung",
  fieldLabelNotes: "Notizen",
  fieldLabelTitle: "Titel",
  fieldLabelFirstName: "Vorname",
  fieldLabelLastName: "Nachname",
  cpNameRequired: "Projektname ist erforderlich.",
  hiTitleRequired: "Titel ist erforderlich.",
  tfRunError: "Generierung fehlgeschlagen. Bitte versuchen Sie es erneut.",
  tfUsageLimitGuest: "Gastlimit erreicht. Melden Sie sich an oder erstellen Sie ein Konto.",
  tfUsageLimitFree: "Kostenloses Limit erreicht. Upgraden Sie auf Pro für mehr Generierungen.",
  tfNeedProject: "Erstellen Sie zuerst ein Projekt, um diese Generierung zu speichern.",
  guestDraftsName: "Entwürfe",
  defaultProjectName: "Standard",
  guestSyncFailed:
    "Ihre Gastprojekte konnten nicht importiert werden. Ihre lokalen Entwürfe sind noch gespeichert — bitte erneut versuchen.",
  guestSyncNotClaimable:
    "Einige Gast-Generierungen konnten nicht übernommen werden (fehlen oder gehören bereits jemand anderem). Ihre lokalen Entwürfe sind noch gespeichert.",
  guestSyncLimitsExceeded:
    "Zu viele Gastprojekte oder Generierungen für einen Import. Entfernen Sie einige Entwürfe und versuchen Sie es erneut.",
  tfGenerating: "Wird erstellt…",
  tfGeneratingHint: "Dauert in der Regel ein paar Sekunden.",
  tfCancelRequest: "Anfrage abbrechen",
  authGateTitle: "Zum Fortfahren anmelden",
  authGateSubtitle:
    "Erstellen Sie ein kostenloses Konto, um Ihre Projekte und Zertifikate an einem Ort zu behalten.",
  authGateCta: "Anmelden oder Konto erstellen",
  retry: "Erneut versuchen",
  proModalTitle: "Ohne Grenzen erstellen",
  proModalDesc:
    "Einzigartige psychotherapeutische Inhalte und Ideen — mit ChatGPT und 30 weiteren KI-Engines, ohne komplexe Prompts.",
  proModalPriceWas: "49 $",
  proModalPriceNow: "19,99 $",
  proModalOffer: "Unbegrenzte Anfragen · heutiges Sonderangebot",
  proModalRating: "4,9 · 3.920 Bewertungen",
  proModalQuote: "„Beste App für Psychotherapeuten 2025.“ — Open Academy",
  proModalTrusted: "Vertraut von",
  proModalTrustedLabel: "Fachkräften",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Über 50 kreative Module",
      desc: "Erstellen Sie alle Inhalte, die Ihre Arbeit erleichtern.",
    },
    {
      emoji: "🐣",
      title: "Keine Technikkenntnisse nötig",
      desc: "Nutzen Sie einfache Formulare statt komplexer Prompts.",
    },
    {
      emoji: "🚩",
      title: "Regionale Anpassungen",
      desc: "Passen Sie Inhalte an lokale Standards an.",
    },
    {
      emoji: "🧠",
      title: "Personalisierte Ergebnisse",
      desc: "Inhalte basierend auf Patientenverlauf und Ihrem Stil.",
    },
    {
      emoji: "⌛",
      title: "Keine Wartezeit",
      desc: "Erzeugen Sie Inhalte und Ideen schnell ohne Warten.",
    },
    {
      emoji: "🚀",
      title: "Unbegrenztes Kontingent",
      desc: "Erstellen Sie so viel Inhalt, wie Sie brauchen.",
    },
  ],
  proModalCta: "Vollen Zugang erhalten",
  proModalClose: "Schließen",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow und Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Zeitersparnis",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemia ist ein echter Gamechanger für Therapeuten, die ihre Arbeit straffen wollen. Zeit ist immer knapp — und Aidemia versteht das. Die intuitive Oberfläche und die KI-Tools verkürzen Vorbereitung und Analyse deutlich.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Preiswert",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemia spart nicht nur Zeit, sondern auch Geld. Indem die Funktionen zahlreicher KI-Tools auf einer Plattform gebündelt werden, entfallen teure Mehrfach-Abos. Als Psychotherapeut kann ich die Bandbreite und den täglichen Mehrwert kaum unterschätzen.",
    },
  ],
  proModalDataProtected: "Ihre Daten sind geschützt",
  proModalTermsShort: "AGB",
  proModalGuarantee: "14-Tage-Geld-zurück-Garantie",
  proModalLegalNote:
    "Der Rabatt ist bereits berücksichtigt. Ihr Pass gibt Zugang zu allen Inhaltsarten, inklusive künftiger Funktionen ohne Preiserhöhung. Sie können jederzeit kündigen oder alle vier Wochen automatisch verlängern. Ein Teil Ihres Beitrags geht an Kinderhilfsorganisationen.",
};

const fr: TranslationStrings = {
  navProjects: "Projets",
  navHistory: "Historique",
  navTools: "Outils",
  navChat: "Chat",
  navCerts: "Certificats",
  navAccount: "Compte",
  toolsTitle: "Outils",
  toolsDesc:
    "Des outils d'IA pour chaque étape du travail clinique — de l'orientation au suivi. Chacun produit un brouillon que vous relisez et validez.",
  toolsCountLabel: "outils",
  all: "Tous",
  projectsTitle: "Projets",
  projectsDesc: "Tout ce que vous avez généré, organisé par dossier client.",
  projTab: "Projets",
  histTab: "Historique",
  historyTitle: "Historique",
  historyDesc: "Tous les brouillons que vous avez générés — tous projets confondus, les plus récents en premier.",
  createProject: "Créer un projet",
  editProject: "Modifier le projet",
  emptyProjectsTitle: "Aucun projet pour l’instant",
  emptyProjectsDesc:
    "Créez un projet pour un client ou un dossier. Les brouillons que vous générez vivront ici, organisés et faciles à retrouver.",
  projectsProCtaTitle: "Organisez votre travail en projets",
  projectsProCtaDesc:
    "Passez à Pro pour créer des projets personnalisés par client ou dossier — gardez chaque brouillon structuré et facile à retrouver.",
  projectsProCtaBenefits: [
    "Créez des projets personnalisés sans limite",
    "Séparez le travail par client ou dossier",
    "Gardez chaque brouillon à sa place",
  ],
  emptyHistoryTitle: "Aucun historique pour l’instant",
  emptyHistoryDesc:
    "Lorsque vous générez un brouillon, il apparaîtra ici — les plus récents en premier.",
  emptyHistoryCta: "Parcourir les outils",
  notFoundTitle: "Cette page est introuvable",
  notFoundDesc:
    "L’outil, le projet ou le brouillon que vous cherchez n’existe pas ou a été déplacé.",
  notFoundCtaTools: "Retour aux Outils",
  notFoundCtaProjects: "Aller aux Projets",
  paginationPrevious: "Précédent",
  paginationNext: "Suivant",
  paginationPageOf: "Page {page} sur {totalPages}",
  paginationAria: "Pagination des projets",
  chatTitle: "Assistant",
  chatDesc: "Réfléchissez à voix haute avec un assistant qui connaît vos outils.",
  chatPlaceholder: "Posez une question…",
  chatSend: "Envoyer",
  certsTitle: "Certificat d’adhésion",
  certsDesc:
    "Adhésion complète à la World Innovative Teaching Association avec un certificat imprimable et vérifiable — distinct de votre abonnement Aidemia Pro.",
  certsObserverNote:
    "Vous êtes membre observateur de la World Innovative Teaching Association. Passez à l’adhésion complète pour recevoir votre certificat.",
  certsRepurchaseNote:
    "Votre accès d’adhésion précédent n’est plus actif. Vous pouvez acheter le certificat à nouveau.",
  certsWhyTitle: "Pourquoi obtenir le certificat",
  certsBenefits: [
    "Preuve officielle d’adhésion professionnelle à la WITA",
    "Numéro de série unique avec validation en ligne",
    "Certificat au format paysage, prêt à imprimer ou envoyer",
    "Qualification reconnue pour les établissements scolaires et les institutions",
    "Inscription au registre des membres de l’association",
    "Adhésion mensuelle à un tarif limité dans le temps",
  ],
  certsPlanLabel: "Adhésion mensuelle",
  certsPriceWas: "100,00 $",
  certsPriceNow: "49,99 $",
  certsNotInPro:
    "Non inclus dans Aidemia Pro. Vous ne payez que le certificat d’adhésion et l’adhésion complète à la WITA.",
  certsCta: "Adhérer et obtenir le certificat",
  certsSecurePay: "Paiement sécurisé sur notre page de paiement.",
  certsSpecimen: "Spécimen",
  certsSpecimenCaption:
    "Spécimen — pas un certificat délivré. Aperçu du design officiel WITA.",
  certsSpecimenAlt: "Spécimen — aperçu du certificat d’adhésion",
  certsPurchasedNote:
    "Votre certificat d’adhésion complète à la World Innovative Teaching Association est actif.",
  certsSerialNumber: "Numéro de série du document : {serial}",
  certsIncludesTitle: "Votre adhésion comprend",
  certsDownload: "Télécharger le certificat",
  certsDownloading: "Téléchargement…",
  certsDownloadError: "Impossible de télécharger le certificat. Veuillez réessayer.",
  certsPreviewCaption:
    "Votre certificat délivré utilise le nom de votre profil et le numéro de série.",
  certsPreviewAlt: "Aperçu du certificat d’adhésion",
  certsProfileIncompleteBoth:
    "Votre prénom et votre nom ne sont pas renseignés. Le certificat utilise le nom de votre profil.",
  certsProfileIncompleteFirst:
    "Votre prénom n’est pas renseigné. Le certificat utilise le nom de votre profil.",
  certsProfileIncompleteLast:
    "Votre nom n’est pas renseigné. Le certificat utilise le nom de votre profil.",
  certsProfileIncompleteLink: "Compléter votre profil",
  certsVerifyEyebrow: "Adhésion WITA",
  certsVerifyTitle: "Vérifier un certificat",
  certsVerifyDesc:
    "Saisissez le numéro de série du document pour confirmer qu’un certificat d’adhésion est authentique et actuellement valide.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "La vérification confirme uniquement la validité — aucun détail personnel n’est affiché.",
  certsVerifyLabel: "Numéro de série du document",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Format : CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Saisissez un numéro de série de certificat.",
  certsVerifyFormatError: "Saisissez un numéro comme CERT-2026-000001.",
  certsVerifyCta: "Vérifier le certificat",
  certsVerifyChecking: "Vérification…",
  certsVerifyValidTitle: "Le certificat est valide",
  certsVerifyValidBody:
    "Ce certificat d’adhésion a été délivré le {date} et est actuellement actif.",
  certsVerifyInvalidTitle: "Certificat non vérifié",
  certsVerifyInvalidBody:
    "Nous n’avons pas pu confirmer ce numéro. Il peut être mal saisi, expiré ou inactif.",
  certsVerifyAgain: "Vérifier un autre numéro",
  certsVerifyError:
    "Impossible de joindre le service de vérification. Vérifiez votre connexion et réessayez.",
  certsVerifyBackToCerts: "Retour au certificat d’adhésion",
  certsVerifyOpenLink: "Vérifier un numéro de certificat",
  accountTitle: "Compte",
  accountEmailVerified: "E-mail confirmé",
  accountEmailUnverified: "E-mail non confirmé",
  accountResendConfirmation: "Renvoyer l’e-mail de confirmation",
  accountResendConfirmationSending: "Envoi…",
  accountResendConfirmationSent: "E-mail de confirmation envoyé. Vérifiez votre boîte de réception.",
  emailConfirmConfirming: "Confirmation de votre e-mail…",
  emailConfirmSuccessTitle: "E-mail confirmé",
  emailConfirmSuccessBody:
    "Votre adresse e-mail a été vérifiée. Vous pouvez continuer à utiliser Aidemia.",
  emailConfirmInvalidTitle: "Lien de confirmation invalide",
  emailConfirmInvalidBody:
    "Ce lien de confirmation est invalide ou a expiré. Demandez un nouvel e-mail de confirmation et réessayez.",
  emailConfirmMissingTitle: "Lien de confirmation manquant",
  emailConfirmMissingBody:
    "Ouvrez le lien de confirmation reçu par e-mail pour vérifier votre adresse.",
  emailConfirmErrorTitle: "Impossible de confirmer l’e-mail",
  emailConfirmGoToAccount: "Aller au compte",
  emailConfirmGoHome: "Retour à l’accueil",
  loginLinkConsuming: "Connexion en cours…",
  loginLinkMissingTitle: "Lien invalide",
  loginLinkMissingBody:
    "Ce lien de connexion ne contient pas de jeton. Rouvrez le lien reçu par e-mail ou après le paiement.",
  loginLinkInvalidTitle: "Lien expiré ou déjà utilisé",
  loginLinkInvalidBody:
    "Ce lien de connexion a expiré ou a déjà été utilisé. Demandez-en un nouveau pour continuer.",
  loginLinkErrorTitle: "Impossible de vous connecter",
  loginLinkGoHome: "Retour à l’accueil",
  projectLandingEyebrow: "Votre projet",
  projectLandingBody: "Vous êtes connecté. Ouvrez l’espace de travail pour continuer.",
  projectLandingMissingTitle: "Projet introuvable",
  projectLandingMissingBody: "Ce projet n’existe pas ou vous n’y avez pas accès.",
  projectLandingErrorTitle: "Impossible de charger le projet",
  projectLandingGoHome: "Retour à l’accueil",
  projectLandingOpenWorkspace: "Ouvrir l’espace de travail",
  paymentCheckoutSuccessTitle: "Paiement réussi",
  paymentCheckoutSuccessBody:
    "Merci. Votre paiement a bien été effectué. L’accès Pro apparaîtra bientôt dans votre compte.",
  paymentCheckoutSuccessCompleting: "Confirmation du paiement…",
  paymentCheckoutSuccessGuestBody:
    "Merci. Votre compte est prêt — nous vous avons envoyé un mot de passe par e-mail. Vous êtes connecté et l’accès Pro est actif.",
  paymentCheckoutSuccessExistingAccountBody:
    "Merci. Votre paiement est lié à votre compte existant. Connectez-vous avec votre mot de passe habituel pour continuer.",
  paymentCheckoutSuccessSignIn: "Se connecter",
  paymentCheckoutSuccessGoToAccount: "Aller au compte",
  paymentCheckoutSuccessGoToTools: "Retour aux outils",
  paymentCheckoutSuccessGoToCertificates: "Retour aux certificats",
  paymentCheckoutCancelTitle: "Paiement annulé",
  paymentCheckoutCancelBody:
    "Le paiement a été annulé. Aucun montant n’a été débité. Vous pouvez réessayer quand vous voulez.",
  paymentCheckoutCancelRetry: "Réessayer",
  paymentCheckoutCancelGoToTools: "Retour aux outils",
  paymentCheckoutCancelGoToCertificates: "Retour aux certificats",
  accountSubscription: "Abonnement",
  accountDataPrivacy: "Données et confidentialité",
  accountPlanFree: "Gratuit",
  accountProfileHeading: "Votre nom",
  accountFirstName: "Prénom",
  accountLastName: "Nom",
  accountFirstNamePh: "Prénom",
  accountLastNamePh: "Nom de famille",
  accountSaveProfile: "Enregistrer",
  accountProfileSaved: "Profil mis à jour.",
  menuRecents: "Récents",
  menuRecentsEmpty: "Pas encore de générations récentes",
  menuRecentsShowMore: "Voir plus",
  menuAccountSettings: "Paramètres du compte",
  menuAbout: "À propos d'Aidemia",
  menuPersonalize: "Personnaliser",
  menuPrivacy: "Politique de confidentialité",
  menuTerms: "Conditions générales",
  menuContact: "Contactez-nous",
  signIn: "Se connecter",
  signOut: "Se déconnecter",
  itemDownload: "Télécharger",
  itemEdit: "Modifier",
  itemBodyLabel: "Contenu",
  itemGeneratedWith: "Généré avec",
  itemProject: "Projet",
  itemGenerateNew: "Générer un nouveau",
  itemDraftNote: "Brouillon généré par Aidemia — à relire et modifier avant utilisation.",
  audioPlay: "Lecture",
  audioPause: "Pause",
  audioMute: "Couper le son",
  audioUnmute: "Activer le son",
  audioSeek: "Position",
  audioVolume: "Volume",
  audioError: "Impossible de charger cet audio. Essayez de télécharger le fichier.",
  audioLabel: "Audio",
  cpSubtitle: "Un espace pour le travail d'un dossier client.",
  cpNameLabel: "Nom du projet",
  cpNamePh: "p. ex. Client S. — Panique",
  cpDescLabel: "Description",
  cpOptional: "facultatif",
  cpRequired: "obligatoire",
  cpDescPh: "Motif de consultation, modalité, étape du suivi…",
  cpAsideTitle: "À propos des projets",
  cpAsideText:
    "Un projet réunit tout le travail d'un dossier client en un seul endroit — notes, plans et lettres — pour le retrouver facilement.",
  cpAsideHint: "Astuce : utilisez des initiales ou un code court plutôt que le nom complet du client.",
  cpCancel: "Annuler",
  cpCreate: "Créer",
  cpSave: "Enregistrer",
  cpEmptyProjTitle: "Ce projet est vide",
  cpEmptyProj: "Rien pour l'instant — les brouillons générés dans ce projet apparaîtront ici.",
  tfTitleLabel: "Titre du brouillon",
  tfTitlePh: "p. ex. Préparation de séance — semaine 7",
  tfProjectLabel: "Enregistrer dans le projet",
  tfContextLabel: "Contexte clinique",
  tfContextPh: "Collez des notes de séance, une lettre d'orientation ou quelques lignes sur le client…",
  tfToneLabel: "Ton",
  tfToneOpts: ["Chaleureux", "Neutre", "Clinique"],
  tfNextLabel: "Suggérer les prochaines étapes",
  tfDeepLabel: "Raisonnement clinique approfondi",
  tfReset: "Réinitialiser",
  tfGenerate: "Générer",
  tfMoreIn: "Plus dans cette catégorie",
  tfMoreEmptyTitle: "Pas encore d’autres outils",
  tfMoreEmptyDesc: "C’est pour l’instant le seul outil de cette catégorie.",
  badgePro: "PRO",
  badgeExp: "EXPÉRIMENTAL",
  tfToday: "Aujourd'hui",
  tfGenBlockH: "Brouillon",
  tfGenBlockP:
    "Un premier brouillon structuré à partir de votre contexte — prêt à relire, modifier et vous approprier.",
  tfCtxBlockH: "Votre contexte",
  tfClientLabel: "Référence client",
  tfClientPh: "p. ex. Client M. (initiales uniquement)",
  tfModalityLabel: "Modalité thérapeutique",
  tfModalityOpts: ["TCC", "ACT", "TCD", "Psychodynamique", "Centrée sur la personne", "Intégrative"],
  tfFocusLabel: "Axes prioritaires",
  tfFocusPh: "Thèmes, objectifs ou points que le brouillon doit souligner…",
  tfFormatLabel: "Format de sortie",
  tfFormatOpts: ["Puces", "Texte suivi", "Note SOAP", "Lettre"],
  tfLengthLabel: "Longueur",
  tfLengthOpts: ["Bref", "Standard", "Détaillé"],
  tfReadingLabel: "Niveau de lecture",
  tfReadingOpts: ["Langage simple", "Standard", "Clinique"],
  tfRiskLabel: "Inclure un dépistage des risques",
  tfMeasuresLabel: "Référencer des mesures de résultats",
  tfAnonLabel: "Anonymiser les informations du client",
  ctxTitle: "Contexte du projet",
  ctxNotesLabel: "Notes",
  ctxNotesPh:
    "Ajoutez votre propre contexte — antécédents, objectifs, signaux de risque, préférences, tout ce dont vous voulez vous souvenir…",
  ctxPrivate: "Privé · enregistré sur cet appareil",
  ctxSummaryLabel: "D'après vos brouillons",
  ctxAuto: "Auto",
  ctxUpdate: "Mettre à jour",
  ctxSavedTag: "Enregistré",
  ctxToolsUsed: "Outils utilisés",
  ctxNotesHelp:
    "Utilisé lorsque vous sélectionnez ce projet dans un outil d'IA, pour enrichir les brouillons générés.",
  ctxSummaryTpl: "{n} {drafts} jusqu'ici, le plus récent le {date}.",
  ctxDraftWord: "brouillon",
  ctxDraftsWord: "brouillons",
  ctxCovers: "Couvre",
  ctxEmptySummary:
    "Pas encore de brouillon — un résumé apparaîtra ici au fur et à mesure que vous générerez du contenu dans ce projet.",
  authTitle: "Sécurisez votre compte",
  authSubtitle: "Avoir un compte vous aide à garder vos données et votre statut PRO en sécurité.",
  authGoogle: "Continuer avec Google",
  authApple: "Continuer avec Apple",
  authEmail: "Continuer avec l'email",
  authGuest: "Continuer en tant qu'invité",
  authClose: "Fermer",
  authBack: "Retour",
  authOr: "ou",
  authEmailLabel: "Email",
  authEmailPh: "vous@exemple.com",
  authPasswordLabel: "Mot de passe",
  authPasswordPh: "••••••••",
  authPasswordHint: "Au moins 8 caractères.",
  authRestoreAccess: "Mot de passe oublié ?",
  authRestoreTitle: "Restaurer l’accès",
  authRestoreSubtitle: "Indiquez votre email et nous vous enverrons un nouveau mot de passe.",
  authRestoreSubmit: "Envoyer un nouveau mot de passe",
  authRestoreSuccess: "Si un compte existe pour cet email, un nouveau mot de passe est en route.",
  authSignInTitle: "Se connecter",
  authSignUpTitle: "Créez votre compte",
  authSubmitSignIn: "Se connecter",
  authSubmitSignUp: "Créer un compte",
  authSubmitting: "Veuillez patienter…",
  authSwitchToSignUp: "Pas encore de compte ? Inscrivez-vous",
  authSwitchToSignIn: "Déjà un compte ? Connectez-vous",
  authErrorFallback: "Une erreur est survenue. Veuillez réessayer.",
  authTooManyAttempts: "Trop de tentatives. Veuillez patienter un instant et réessayer.",
  authEmailRequired: "L'e-mail est obligatoire.",
  authEmailInvalid: "Saisissez une adresse e-mail valide.",
  authPasswordRequired: "Le mot de passe est obligatoire.",
  authPasswordTooShort: "Le mot de passe doit contenir au moins 8 caractères.",
  authInvalidCredentials: "Identifiants incorrects.",
  authEmailTaken: "Cet e-mail est déjà enregistré.",
  formCheckFields: "Veuillez vérifier les champs surlignés.",
  validationFieldRequired: "{field} est obligatoire.",
  validationFieldEmail: "Saisissez un {field} valide.",
  validationFieldTaken: "Ce {field} est déjà utilisé.",
  validationFieldMin: "{field} doit contenir au moins {min} caractères.",
  validationFieldMax: "{field} doit contenir au plus {max} caractères.",
  fieldLabelEmail: "E-mail",
  fieldLabelPassword: "Mot de passe",
  fieldLabelName: "Nom",
  fieldLabelDescription: "Description",
  fieldLabelNotes: "Notes",
  fieldLabelTitle: "Titre",
  fieldLabelFirstName: "Prénom",
  fieldLabelLastName: "Nom",
  cpNameRequired: "Le nom du projet est obligatoire.",
  hiTitleRequired: "Le titre est obligatoire.",
  tfRunError: "Génération impossible. Veuillez réessayer.",
  tfUsageLimitGuest: "Limite invité atteinte. Connectez-vous ou créez un compte pour continuer.",
  tfUsageLimitFree: "Limite gratuite atteinte. Passez à Pro pour plus de générations.",
  tfNeedProject: "Créez d’abord un projet pour enregistrer cette génération.",
  guestDraftsName: "Brouillons",
  defaultProjectName: "Par défaut",
  guestSyncFailed:
    "Impossible d’importer vos projets invité. Vos brouillons locaux sont toujours enregistrés — réessayez.",
  guestSyncNotClaimable:
    "Certaines générations invité n’ont pas pu être récupérées (manquantes ou déjà possédées). Vos brouillons locaux sont toujours enregistrés.",
  guestSyncLimitsExceeded:
    "Trop de projets ou de générations invité à importer d’un coup. Supprimez des brouillons et réessayez.",
  tfGenerating: "Création…",
  tfGeneratingHint: "Cela prend généralement quelques secondes.",
  tfCancelRequest: "Annuler la demande",
  authGateTitle: "Connectez-vous pour continuer",
  authGateSubtitle:
    "Créez un compte gratuit pour garder vos projets et certificats au même endroit.",
  authGateCta: "Se connecter ou créer un compte",
  retry: "Réessayer",
  proModalTitle: "Créez sans limites",
  proModalDesc:
    "Des contenus et idées psychothérapeutiques uniques, adaptés pour vous avec ChatGPT et 30 autres moteurs d’IA — sans prompts complexes.",
  proModalPriceWas: "49 $",
  proModalPriceNow: "19,99 $",
  proModalOffer: "Requêtes illimitées · offre spéciale du jour",
  proModalRating: "4,9 · 3 920 avis",
  proModalQuote: "« Meilleure appli pour psychothérapeutes en 2025. » — Open Academy",
  proModalTrusted: "Adoptée par",
  proModalTrustedLabel: "spécialistes",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Plus de 50 modules créatifs",
      desc: "Créez tout le contenu nécessaire pour faciliter votre travail.",
    },
    {
      emoji: "🐣",
      title: "Aucune compétence technique",
      desc: "Utilisez des formulaires simples plutôt que des prompts complexes.",
    },
    {
      emoji: "🚩",
      title: "Ajustements régionaux",
      desc: "Adaptez le contenu à vos standards locaux.",
    },
    {
      emoji: "🧠",
      title: "Résultats personnalisés",
      desc: "Du contenu selon l’historique du patient et votre style.",
    },
    {
      emoji: "⌛",
      title: "Sans temps d’attente",
      desc: "Générez contenu et idées rapidement, sans attendre.",
    },
    {
      emoji: "🚀",
      title: "Quota illimité",
      desc: "Créez autant de contenu que vous en avez besoin.",
    },
  ],
  proModalCta: "Obtenir l’accès complet",
  proModalClose: "Fermer",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow et Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Gain de temps",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemia change vraiment la donne pour les thérapeutes qui veulent alléger leur travail. Le temps est précieux, et Aidemia le comprend. L’interface intuitive et les outils d’IA réduisent nettement la préparation et l’analyse.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Abordable",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemia ne fait pas qu’économiser du temps : il économise aussi de l’argent. En regroupant les fonctions de nombreux outils d’IA sur une seule plateforme, Aidemia évite les abonnements multiples coûteux. En tant que psychothérapeute, je ne peux sous-estimer l’éventail de possibilités et la valeur qu’il m’apporte chaque jour.",
    },
  ],
  proModalDataProtected: "Vos données sont protégées",
  proModalTermsShort: "Conditions",
  proModalGuarantee: "Garantie satisfait ou remboursé 14 jours",
  proModalLegalNote:
    "La réduction est appliquée. Votre pass donne accès à tous les types de contenu, y compris les futures fonctionnalités sans hausse de prix. Vous pouvez annuler à tout moment ou renouveler automatiquement toutes les quatre semaines. Une partie de votre contribution va à des associations pour l’enfance.",
};

const it: TranslationStrings = {
  navProjects: "Progetti",
  navHistory: "Cronologia",
  navTools: "Strumenti",
  navChat: "Chat",
  navCerts: "Certificati",
  navAccount: "Account",
  toolsTitle: "Strumenti",
  toolsDesc:
    "Strumenti di IA per ogni fase del lavoro clinico — dall'invio al follow-up. Ognuno produce una bozza che tu rivedi e possiedi.",
  toolsCountLabel: "strumenti",
  all: "Tutti",
  projectsTitle: "Progetti",
  projectsDesc: "Tutto ciò che hai generato, organizzato per caso clinico.",
  projTab: "Progetti",
  histTab: "Cronologia",
  historyTitle: "Cronologia",
  historyDesc: "Tutte le bozze che hai generato — in tutti i progetti, prima le più recenti.",
  createProject: "Crea progetto",
  editProject: "Modifica progetto",
  emptyProjectsTitle: "Nessun progetto ancora",
  emptyProjectsDesc:
    "Crea un progetto per un cliente o un caso. Le bozze che generi vivranno qui, organizzate e facili da trovare.",
  projectsProCtaTitle: "Organizza il lavoro in progetti",
  projectsProCtaDesc:
    "Passa a Pro per creare progetti personalizzati per ogni cliente o caso — tieni ogni bozza ordinata e facile da trovare.",
  projectsProCtaBenefits: [
    "Crea progetti personalizzati senza limiti",
    "Separa il lavoro per cliente o caso",
    "Tieni ogni bozza al posto giusto",
  ],
  emptyHistoryTitle: "Nessuna cronologia ancora",
  emptyHistoryDesc:
    "Quando generi una bozza, apparirà qui — prima le più recenti.",
  emptyHistoryCta: "Esplora gli strumenti",
  notFoundTitle: "Questa pagina non è stata trovata",
  notFoundDesc:
    "Lo strumento, il progetto o la bozza che stai cercando non esiste o è stato spostato.",
  notFoundCtaTools: "Torna agli Strumenti",
  notFoundCtaProjects: "Vai ai Progetti",
  paginationPrevious: "Precedente",
  paginationNext: "Successivo",
  paginationPageOf: "Pagina {page} di {totalPages}",
  paginationAria: "Paginazione progetti",
  chatTitle: "Assistente",
  chatDesc: "Pensa ad alta voce con un assistente che conosce i tuoi strumenti.",
  chatPlaceholder: "Chiedi qualsiasi cosa…",
  chatSend: "Invia",
  certsTitle: "Certificato di iscrizione",
  certsDesc:
    "Iscrizione completa alla World Innovative Teaching Association con certificato stampabile e verificabile — separato dall’abbonamento Aidemia Pro.",
  certsObserverNote:
    "Sei un membro osservatore della World Innovative Teaching Association. Passa all’iscrizione completa per ricevere il certificato.",
  certsRepurchaseNote:
    "Il tuo accesso di iscrizione precedente non è più attivo. Puoi acquistare di nuovo il certificato.",
  certsWhyTitle: "Perché ottenere il certificato",
  certsBenefits: [
    "Prova ufficiale dell’iscrizione professionale alla WITA",
    "Numero di serie univoco con verifica online",
    "Certificato orizzontale pronto da stampare o inviare",
    "Qualifica riconosciuta per scuole e istituzioni",
    "Iscrizione nel registro dei membri dell’associazione",
    "Iscrizione mensile a un prezzo a tempo limitato",
  ],
  certsPlanLabel: "Iscrizione mensile",
  certsPriceWas: "100,00 $",
  certsPriceNow: "49,99 $",
  certsNotInPro:
    "Non incluso in Aidemia Pro. Paghi solo il certificato di iscrizione e l’iscrizione completa alla WITA.",
  certsCta: "Iscriviti e ottieni il certificato",
  certsSecurePay: "Pagamento sicuro sulla nostra pagina di pagamento.",
  certsSpecimen: "Campione",
  certsSpecimenCaption:
    "Campione — non è un certificato rilasciato. Anteprima del design ufficiale WITA.",
  certsSpecimenAlt: "Campione — anteprima del certificato di iscrizione",
  certsPurchasedNote:
    "Il tuo certificato di iscrizione completa alla World Innovative Teaching Association è attivo.",
  certsSerialNumber: "Numero di serie del documento: {serial}",
  certsIncludesTitle: "La tua iscrizione include",
  certsDownload: "Scarica certificato",
  certsDownloading: "Download in corso…",
  certsDownloadError: "Impossibile scaricare il certificato. Riprova.",
  certsPreviewCaption:
    "Il certificato emesso usa il nome del tuo profilo e il numero di serie.",
  certsPreviewAlt: "Anteprima del certificato di iscrizione",
  certsProfileIncompleteBoth:
    "Nome e cognome non sono compilati. Il certificato usa il nome del tuo profilo.",
  certsProfileIncompleteFirst:
    "Il nome non è compilato. Il certificato usa il nome del tuo profilo.",
  certsProfileIncompleteLast:
    "Il cognome non è compilato. Il certificato usa il nome del tuo profilo.",
  certsProfileIncompleteLink: "Completa il profilo",
  certsVerifyEyebrow: "Iscrizione WITA",
  certsVerifyTitle: "Verifica un certificato",
  certsVerifyDesc:
    "Inserisci il numero di serie del documento per confermare che un certificato di iscrizione sia autentico e attualmente valido.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "La verifica conferma solo la validità: i dati personali non vengono mai mostrati.",
  certsVerifyLabel: "Numero di serie del documento",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Formato: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Inserisci un numero di serie del certificato.",
  certsVerifyFormatError: "Inserisci un numero come CERT-2026-000001.",
  certsVerifyCta: "Verifica certificato",
  certsVerifyChecking: "Verifica in corso…",
  certsVerifyValidTitle: "Il certificato è valido",
  certsVerifyValidBody:
    "Questo certificato di iscrizione è stato rilasciato il {date} ed è attualmente attivo.",
  certsVerifyInvalidTitle: "Certificato non verificato",
  certsVerifyInvalidBody:
    "Non è stato possibile confermare questo numero. Potrebbe essere errato, scaduto o non più attivo.",
  certsVerifyAgain: "Controlla un altro numero",
  certsVerifyError:
    "Impossibile raggiungere il servizio di verifica. Controlla la connessione e riprova.",
  certsVerifyBackToCerts: "Torna al certificato di iscrizione",
  certsVerifyOpenLink: "Verifica un numero di certificato",
  accountTitle: "Account",
  accountEmailVerified: "Email confermata",
  accountEmailUnverified: "Email non confermata",
  accountResendConfirmation: "Reinvia email di conferma",
  accountResendConfirmationSending: "Invio…",
  accountResendConfirmationSent: "Email di conferma inviata. Controlla la posta in arrivo.",
  emailConfirmConfirming: "Conferma dell’email in corso…",
  emailConfirmSuccessTitle: "Email confermata",
  emailConfirmSuccessBody:
    "Il tuo indirizzo email è stato verificato. Puoi continuare a usare Aidemia.",
  emailConfirmInvalidTitle: "Link di conferma non valido",
  emailConfirmInvalidBody:
    "Questo link di conferma non è valido o è scaduto. Richiedi una nuova email di conferma e riprova.",
  emailConfirmMissingTitle: "Link di conferma mancante",
  emailConfirmMissingBody:
    "Apri il link di conferma dall’email per verificare il tuo indirizzo.",
  emailConfirmErrorTitle: "Impossibile confermare l’email",
  emailConfirmGoToAccount: "Vai all’account",
  emailConfirmGoHome: "Torna alla home",
  loginLinkConsuming: "Accesso in corso…",
  loginLinkMissingTitle: "Link non valido",
  loginLinkMissingBody:
    "A questo link di accesso manca il token. Riapri il link dall’email o dalla conferma di pagamento.",
  loginLinkInvalidTitle: "Link scaduto o già usato",
  loginLinkInvalidBody:
    "Questo link di accesso è scaduto o è già stato usato. Richiedine uno nuovo per continuare.",
  loginLinkErrorTitle: "Impossibile accedere",
  loginLinkGoHome: "Torna alla home",
  projectLandingEyebrow: "Il tuo progetto",
  projectLandingBody: "Hai effettuato l’accesso. Apri lo spazio di lavoro per continuare.",
  projectLandingMissingTitle: "Progetto non trovato",
  projectLandingMissingBody: "Questo progetto non esiste oppure non hai accesso.",
  projectLandingErrorTitle: "Impossibile caricare il progetto",
  projectLandingGoHome: "Torna alla home",
  projectLandingOpenWorkspace: "Apri spazio di lavoro",
  paymentCheckoutSuccessTitle: "Pagamento riuscito",
  paymentCheckoutSuccessBody:
    "Grazie. Il pagamento è andato a buon fine. L’accesso Pro apparirà a breve nel tuo account.",
  paymentCheckoutSuccessCompleting: "Conferma del pagamento…",
  paymentCheckoutSuccessGuestBody:
    "Grazie. Il tuo account è pronto: ti abbiamo inviato una password via e-mail. Hai effettuato l’accesso e l’accesso Pro è attivo.",
  paymentCheckoutSuccessExistingAccountBody:
    "Grazie. Il pagamento è collegato al tuo account esistente. Accedi con la password abituale per continuare.",
  paymentCheckoutSuccessSignIn: "Accedi",
  paymentCheckoutSuccessGoToAccount: "Vai all’account",
  paymentCheckoutSuccessGoToTools: "Torna agli strumenti",
  paymentCheckoutSuccessGoToCertificates: "Torna ai certificati",
  paymentCheckoutCancelTitle: "Pagamento annullato",
  paymentCheckoutCancelBody:
    "Il checkout è stato annullato. Non è stato addebitato nulla. Puoi riprovare quando vuoi.",
  paymentCheckoutCancelRetry: "Riprova",
  paymentCheckoutCancelGoToTools: "Torna agli strumenti",
  paymentCheckoutCancelGoToCertificates: "Torna ai certificati",
  accountSubscription: "Abbonamento",
  accountDataPrivacy: "Dati e privacy",
  accountPlanFree: "Gratuito",
  accountProfileHeading: "Il tuo nome",
  accountFirstName: "Nome",
  accountLastName: "Cognome",
  accountFirstNamePh: "Nome",
  accountLastNamePh: "Cognome",
  accountSaveProfile: "Salva modifiche",
  accountProfileSaved: "Profilo aggiornato.",
  menuRecents: "Recenti",
  menuRecentsEmpty: "Nessuna generazione recente",
  menuRecentsShowMore: "Mostra di più",
  menuAccountSettings: "Impostazioni account",
  menuAbout: "Informazioni su Aidemia",
  menuPersonalize: "Personalizza",
  menuPrivacy: "Informativa sulla privacy",
  menuTerms: "Termini e condizioni",
  menuContact: "Contattaci",
  signIn: "Accedi",
  signOut: "Esci",
  itemDownload: "Scarica",
  itemEdit: "Modifica",
  itemBodyLabel: "Contenuto",
  itemGeneratedWith: "Generato con",
  itemProject: "Progetto",
  itemGenerateNew: "Genera uno nuovo",
  itemDraftNote: "Bozza generata da Aidemia — rivedi e modifica prima dell'uso.",
  audioPlay: "Riproduci",
  audioPause: "Pausa",
  audioMute: "Disattiva audio",
  audioUnmute: "Attiva audio",
  audioSeek: "Posizione",
  audioVolume: "Volume",
  audioError: "Impossibile caricare questo audio. Prova a scaricare il file.",
  audioLabel: "Audio",
  cpSubtitle: "Uno spazio per il lavoro su un caso.",
  cpNameLabel: "Nome del progetto",
  cpNamePh: "es. Cliente S. — Panico",
  cpDescLabel: "Descrizione",
  cpOptional: "opzionale",
  cpRequired: "obbligatorio",
  cpDescPh: "Motivo della richiesta, modalità, fase del percorso…",
  cpAsideTitle: "Informazioni sui progetti",
  cpAsideText:
    "Un progetto riunisce tutto il lavoro su un caso in un unico posto — note, piani e lettere — così è facile ritrovarlo.",
  cpAsideHint: "Suggerimento: usa iniziali o un codice breve invece del nome completo del cliente.",
  cpCancel: "Annulla",
  cpCreate: "Crea",
  cpSave: "Salva",
  cpEmptyProjTitle: "Questo progetto è vuoto",
  cpEmptyProj: "Ancora niente qui — le bozze generate in questo progetto appariranno qui.",
  tfTitleLabel: "Titolo della bozza",
  tfTitlePh: "es. Preparazione seduta — settimana 7",
  tfProjectLabel: "Salva nel progetto",
  tfContextLabel: "Contesto clinico",
  tfContextPh: "Incolla note di seduta, una lettera di invio o qualche riga sul cliente…",
  tfToneLabel: "Tono di voce",
  tfToneOpts: ["Caldo", "Neutro", "Clinico"],
  tfNextLabel: "Suggerisci i prossimi passi",
  tfDeepLabel: "Ragionamento clinico approfondito",
  tfReset: "Ripristina",
  tfGenerate: "Genera",
  tfMoreIn: "Altro in questa categoria",
  tfMoreEmptyTitle: "Nessun altro strumento",
  tfMoreEmptyDesc: "Per ora è l’unico strumento di questa categoria.",
  badgePro: "PRO",
  badgeExp: "SPERIMENTALE",
  tfToday: "Oggi",
  tfGenBlockH: "Bozza",
  tfGenBlockP:
    "Una prima bozza strutturata basata sul tuo contesto — pronta da rivedere, modificare e rendere tua.",
  tfCtxBlockH: "Il tuo contesto",
  tfClientLabel: "Riferimento cliente",
  tfClientPh: "es. Cliente M. (solo iniziali)",
  tfModalityLabel: "Modalità terapeutica",
  tfModalityOpts: ["CBT", "ACT", "DBT", "Psicodinamica", "Centrata sulla persona", "Integrata"],
  tfFocusLabel: "Aree di interesse principali",
  tfFocusPh: "Temi, obiettivi o ciò che la bozza dovrebbe enfatizzare…",
  tfFormatLabel: "Formato di output",
  tfFormatOpts: ["Elenco puntato", "Prosa", "Nota SOAP", "Lettera"],
  tfLengthLabel: "Lunghezza",
  tfLengthOpts: ["Breve", "Standard", "Dettagliata"],
  tfReadingLabel: "Livello di lettura",
  tfReadingOpts: ["Linguaggio semplice", "Standard", "Clinico"],
  tfRiskLabel: "Includi screening del rischio",
  tfMeasuresLabel: "Fai riferimento a misure di esito",
  tfAnonLabel: "Anonimizza i dati del cliente",
  ctxTitle: "Contesto del progetto",
  ctxNotesLabel: "Note",
  ctxNotesPh:
    "Aggiungi il tuo contesto — background, obiettivi, segnali di rischio, preferenze, qualsiasi cosa da ricordare per la prossima volta…",
  ctxPrivate: "Privato · salvato su questo dispositivo",
  ctxSummaryLabel: "Dalle tue bozze",
  ctxAuto: "Auto",
  ctxUpdate: "Aggiorna",
  ctxSavedTag: "Salvato",
  ctxToolsUsed: "Strumenti usati",
  ctxNotesHelp:
    "Usato ogni volta che selezioni questo progetto in uno Strumento IA, per arricchire le bozze generate.",
  ctxSummaryTpl: "{n} {drafts} finora, l'ultima il {date}.",
  ctxDraftWord: "bozza",
  ctxDraftsWord: "bozze",
  ctxCovers: "Copre",
  ctxEmptySummary:
    "Ancora nessuna bozza — un riepilogo apparirà qui quando genererai lavoro in questo progetto.",
  authTitle: "Proteggi il tuo account",
  authSubtitle: "Avere un account ti aiuta a mantenere sicuri i dati della tua app e il tuo stato PRO.",
  authGoogle: "Continua con Google",
  authApple: "Continua con Apple",
  authEmail: "Continua con l'email",
  authGuest: "Continua come ospite",
  authClose: "Chiudi",
  authBack: "Indietro",
  authOr: "o",
  authEmailLabel: "Email",
  authEmailPh: "tu@esempio.com",
  authPasswordLabel: "Password",
  authPasswordPh: "••••••••",
  authPasswordHint: "Almeno 8 caratteri.",
  authRestoreAccess: "Password dimenticata?",
  authRestoreTitle: "Ripristina accesso",
  authRestoreSubtitle: "Inserisci la tua email e ti invieremo una nuova password.",
  authRestoreSubmit: "Invia nuova password",
  authRestoreSuccess: "Se esiste un account con questa email, una nuova password è in arrivo.",
  authSignInTitle: "Accedi",
  authSignUpTitle: "Crea il tuo account",
  authSubmitSignIn: "Accedi",
  authSubmitSignUp: "Crea account",
  authSubmitting: "Un momento…",
  authSwitchToSignUp: "Non hai un account? Registrati",
  authSwitchToSignIn: "Hai già un account? Accedi",
  authErrorFallback: "Qualcosa è andato storto. Riprova.",
  authTooManyAttempts: "Troppi tentativi. Attendi un momento e riprova.",
  authEmailRequired: "L'email è obbligatoria.",
  authEmailInvalid: "Inserisci un indirizzo email valido.",
  authPasswordRequired: "La password è obbligatoria.",
  authPasswordTooShort: "La password deve contenere almeno 8 caratteri.",
  authInvalidCredentials: "Credenziali non valide.",
  authEmailTaken: "Questa email è già registrata.",
  formCheckFields: "Controlla i campi evidenziati.",
  validationFieldRequired: "{field} è obbligatorio.",
  validationFieldEmail: "Inserisci un {field} valido.",
  validationFieldTaken: "Questo {field} è già in uso.",
  validationFieldMin: "{field} deve contenere almeno {min} caratteri.",
  validationFieldMax: "{field} deve contenere al massimo {max} caratteri.",
  fieldLabelEmail: "Email",
  fieldLabelPassword: "Password",
  fieldLabelName: "Nome",
  fieldLabelDescription: "Descrizione",
  fieldLabelNotes: "Note",
  fieldLabelTitle: "Titolo",
  fieldLabelFirstName: "Nome",
  fieldLabelLastName: "Cognome",
  cpNameRequired: "Il nome del progetto è obbligatorio.",
  hiTitleRequired: "Il titolo è obbligatorio.",
  tfRunError: "Generazione non riuscita. Riprova.",
  tfUsageLimitGuest: "Limite ospite raggiunto. Accedi o crea un account per continuare.",
  tfUsageLimitFree: "Limite gratuito raggiunto. Passa a Pro per più generazioni.",
  tfNeedProject: "Crea prima un progetto per salvare questa generazione.",
  guestDraftsName: "Bozze",
  defaultProjectName: "Predefinito",
  guestSyncFailed:
    "Impossibile importare i progetti ospite. Le bozze locali sono ancora salvate — riprova.",
  guestSyncNotClaimable:
    "Alcune generazioni ospite non possono essere reclamate (mancanti o già di proprietà altrui). Le bozze locali sono ancora salvate.",
  guestSyncLimitsExceeded:
    "Troppi progetti o generazioni ospite da importare in una volta. Rimuovi alcune bozze e riprova.",
  tfGenerating: "Creazione…",
  tfGeneratingHint: "Di solito richiede pochi secondi.",
  tfCancelRequest: "Annulla richiesta",
  authGateTitle: "Accedi per continuare",
  authGateSubtitle: "Crea un account gratuito per mantenere progetti e certificati in un unico posto.",
  authGateCta: "Accedi o crea un account",
  retry: "Riprova",
  proModalTitle: "Crea senza limiti",
  proModalDesc:
    "Contenuti e idee psicoterapeutiche unici, su misura per te con ChatGPT e altri 30 motori IA — senza prompt complessi.",
  proModalPriceWas: "49 $",
  proModalPriceNow: "19,99 $",
  proModalOffer: "Richieste illimitate · offerta speciale di oggi",
  proModalRating: "4,9 · 3.920 recensioni",
  proModalQuote: "«La migliore app per psicoterapeuti nel 2025.» — Open Academy",
  proModalTrusted: "Scelta da",
  proModalTrustedLabel: "specialisti",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Oltre 50 moduli creativi",
      desc: "Crea qualsiasi contenuto necessario per alleggerire il lavoro.",
    },
    {
      emoji: "🐣",
      title: "Nessuna competenza tecnica",
      desc: "Usa moduli semplici invece di scrivere prompt complessi a mano.",
    },
    {
      emoji: "🚩",
      title: "Adattamenti regionali",
      desc: "Adatta i contenuti agli standard locali.",
    },
    {
      emoji: "🧠",
      title: "Risultati personalizzati",
      desc: "Contenuti in base alla storia del paziente e al tuo stile.",
    },
    {
      emoji: "⌛",
      title: "Nessuna attesa",
      desc: "Genera contenuti e idee rapidamente, senza aspettare.",
    },
    {
      emoji: "🚀",
      title: "Quota illimitata",
      desc: "Crea tutto il contenuto di cui hai bisogno.",
    },
  ],
  proModalCta: "Ottieni l’accesso completo",
  proModalClose: "Chiudi",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow e Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Risparmio di tempo",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemia cambia davvero il lavoro di chi vuole snellire la pratica. Come terapeuta il tempo è sempre poco, e Aidemia lo capisce. L’interfaccia intuitiva e gli strumenti IA riducono nettamente preparazione e analisi.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Conveniente",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemia non risparmia solo tempo: risparmia anche denaro. Riunendo le funzioni di numerosi strumenti di IA in un’unica piattaforma, elimina la necessità di costosi abbonamenti multipli. Come psicoterapeuta, non posso sottovalutare la gamma di possibilità e il valore che mi offre ogni giorno.",
    },
  ],
  proModalDataProtected: "I tuoi dati sono protetti",
  proModalTermsShort: "Termini",
  proModalGuarantee: "Garanzia di rimborso di 14 giorni",
  proModalLegalNote:
    "Lo sconto è già applicato. Il pass dà accesso a tutti i tipi di contenuto, incluse le funzioni future senza aumenti di prezzo. Puoi annullare in qualsiasi momento o rinnovare automaticamente ogni quattro settimane. Parte del contributo va a enti benefici per l’infanzia.",
};

const ja: TranslationStrings = {
  navProjects: "プロジェクト",
  navHistory: "履歴",
  navTools: "ツール",
  navChat: "チャット",
  navCerts: "証明書",
  navAccount: "アカウント",
  toolsTitle: "ツール",
  toolsDesc:
    "紹介からフォローアップまで、臨床業務のあらゆる段階に対応するAIツール。それぞれが確認・調整できる草案を作成します。",
  toolsCountLabel: "ツール",
  all: "すべて",
  projectsTitle: "プロジェクト",
  projectsDesc: "生成したものすべてを、クライアント別に整理して表示します。",
  projTab: "プロジェクト",
  histTab: "履歴",
  historyTitle: "履歴",
  historyDesc: "生成したすべての草案 — すべてのプロジェクトを横断し、新しい順に表示します。",
  createProject: "プロジェクトを作成",
  editProject: "プロジェクトを編集",
  emptyProjectsTitle: "プロジェクトはまだありません",
  emptyProjectsDesc:
    "クライアントや案件ごとにプロジェクトを作成しましょう。生成した草案はここに整理されて表示されます。",
  projectsProCtaTitle: "仕事をプロジェクトで整理",
  projectsProCtaDesc:
    "Proにアップグレードすると、クライアントや案件ごとのカスタムプロジェクトを作成でき、草案を整理して見つけやすくなります。",
  projectsProCtaBenefits: [
    "カスタムプロジェクトを無制限に作成",
    "クライアントや案件ごとに作業を分ける",
    "すべての草案を適切な場所に保管",
  ],
  emptyHistoryTitle: "履歴はまだありません",
  emptyHistoryDesc:
    "草案を生成すると、ここに新しい順で表示されます。",
  emptyHistoryCta: "ツールを見る",
  notFoundTitle: "ページが見つかりません",
  notFoundDesc:
    "お探しのツール、プロジェクト、または草案は存在しないか、移動された可能性があります。",
  notFoundCtaTools: "ツールに戻る",
  notFoundCtaProjects: "プロジェクトへ",
  paginationPrevious: "前へ",
  paginationNext: "次へ",
  paginationPageOf: "{totalPages}ページ中 {page}ページ",
  paginationAria: "プロジェクトのページネーション",
  chatTitle: "アシスタント",
  chatDesc: "あなたのツールを理解したアシスタントと一緒に考えを整理しましょう。",
  chatPlaceholder: "何でも聞いてください…",
  chatSend: "送信",
  certsTitle: "会員証明書",
  certsDesc:
    "World Innovative Teaching Associationの正会員資格と、印刷・検証可能な証明書 — Aidemia Proのサブスクリプションとは別です。",
  certsObserverNote:
    "あなたはWorld Innovative Teaching Associationのオブザーバー会員です。証明書を受け取るには正会員へアップグレードしてください。",
  certsRepurchaseNote:
    "以前の会員アクセスは無効です。証明書を再度購入できます。",
  certsWhyTitle: "証明書を取得する理由",
  certsBenefits: [
    "WITAの専門会員資格の公式証明",
    "オンライン検証付きの固有シリアル番号",
    "印刷や共有に適した横向き証明書",
    "学校や教育機関に認められる資格証明",
    "協会の会員登録簿への掲載",
    "期間限定価格の月額会員資格",
  ],
  certsPlanLabel: "月額会員資格",
  certsPriceWas: "$100.00",
  certsPriceNow: "$49.99",
  certsNotInPro:
    "Aidemia Proには含まれません。お支払いいただくのは会員証明書とWITA正会員資格のみです。",
  certsCta: "参加して証明書を取得",
  certsSecurePay: "決済ページでの安全なお支払い。",
  certsSpecimen: "見本",
  certsSpecimenCaption:
    "見本 — 発行済みの証明書ではありません。公式WITAデザインのプレビューです。",
  certsSpecimenAlt: "見本 — 会員証明書のプレビュー",
  certsPurchasedNote:
    "World Innovative Teaching Association の正会員証明書は有効です。",
  certsSerialNumber: "書類シリアル番号: {serial}",
  certsIncludesTitle: "会員資格に含まれるもの",
  certsDownload: "証明書をダウンロード",
  certsDownloading: "ダウンロード中…",
  certsDownloadError: "証明書をダウンロードできませんでした。もう一度お試しください。",
  certsPreviewCaption:
    "発行済み証明書にはプロフィールの氏名とシリアル番号が使われます。",
  certsPreviewAlt: "会員証明書のプレビュー",
  certsProfileIncompleteBoth:
    "姓と名が未入力です。証明書にはプロフィールの氏名が使われます。",
  certsProfileIncompleteFirst:
    "名が未入力です。証明書にはプロフィールの氏名が使われます。",
  certsProfileIncompleteLast:
    "姓が未入力です。証明書にはプロフィールの氏名が使われます。",
  certsProfileIncompleteLink: "プロフィールを入力",
  certsVerifyEyebrow: "WITA会員",
  certsVerifyTitle: "証明書を確認",
  certsVerifyDesc:
    "文書のシリアル番号を入力して、会員証明書が真正で現在有効であることを確認します。",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "確認では有効性のみを示します。個人情報は表示されません。",
  certsVerifyLabel: "文書シリアル番号",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "形式: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "証明書のシリアル番号を入力してください。",
  certsVerifyFormatError: "CERT-2026-000001 のような番号を入力してください。",
  certsVerifyCta: "証明書を確認",
  certsVerifyChecking: "確認中…",
  certsVerifyValidTitle: "証明書は有効です",
  certsVerifyValidBody:
    "この会員証明書は {date} に発行され、現在有効です。",
  certsVerifyInvalidTitle: "証明書を確認できませんでした",
  certsVerifyInvalidBody:
    "この番号を確認できませんでした。入力ミス、期限切れ、または無効の可能性があります。",
  certsVerifyAgain: "別の番号を確認",
  certsVerifyError:
    "確認サービスに接続できませんでした。接続を確認して再試行してください。",
  certsVerifyBackToCerts: "会員証明書に戻る",
  certsVerifyOpenLink: "証明書番号を確認する",
  accountTitle: "アカウント",
  accountEmailVerified: "メール確認済み",
  accountEmailUnverified: "メール未確認",
  accountResendConfirmation: "確認メールを再送信",
  accountResendConfirmationSending: "送信中…",
  accountResendConfirmationSent: "確認メールを送信しました。受信トレイを確認してください。",
  emailConfirmConfirming: "メールを確認しています…",
  emailConfirmSuccessTitle: "メール確認済み",
  emailConfirmSuccessBody:
    "メールアドレスが確認されました。Aidemia をご利用ください。",
  emailConfirmInvalidTitle: "確認リンクが無効です",
  emailConfirmInvalidBody:
    "この確認リンクは無効か期限切れです。新しい確認メールをリクエストして、もう一度お試しください。",
  emailConfirmMissingTitle: "確認リンクがありません",
  emailConfirmMissingBody:
    "メール内の確認リンクを開いて、アドレスを確認してください。",
  emailConfirmErrorTitle: "メールを確認できませんでした",
  emailConfirmGoToAccount: "アカウントへ",
  emailConfirmGoHome: "ホームへ戻る",
  loginLinkConsuming: "サインインしています…",
  loginLinkMissingTitle: "リンクが無効です",
  loginLinkMissingBody:
    "このサインインリンクにはトークンがありません。メールまたは支払い確認のリンクをもう一度開いてください。",
  loginLinkInvalidTitle: "リンクの期限切れ、または使用済み",
  loginLinkInvalidBody:
    "このサインインリンクは期限切れか、すでに使用されています。続行するには新しいリンクをリクエストしてください。",
  loginLinkErrorTitle: "サインインできませんでした",
  loginLinkGoHome: "ホームへ戻る",
  projectLandingEyebrow: "あなたのプロジェクト",
  projectLandingBody: "サインインしました。続行するにはワークスペースを開いてください。",
  projectLandingMissingTitle: "プロジェクトが見つかりません",
  projectLandingMissingBody: "このプロジェクトは存在しないか、アクセス権がありません。",
  projectLandingErrorTitle: "プロジェクトを読み込めませんでした",
  projectLandingGoHome: "ホームへ戻る",
  projectLandingOpenWorkspace: "ワークスペースを開く",
  paymentCheckoutSuccessTitle: "お支払い完了",
  paymentCheckoutSuccessBody:
    "ありがとうございます。お支払いが完了しました。まもなくアカウントに Pro アクセスが表示されます。",
  paymentCheckoutSuccessCompleting: "お支払いを確認しています…",
  paymentCheckoutSuccessGuestBody:
    "ありがとうございます。アカウントの準備ができました。パスワードをメールでお送りしました。ログイン済みで、Pro アクセスが有効です。",
  paymentCheckoutSuccessExistingAccountBody:
    "ありがとうございます。お支払いは既存のアカウントに紐づけられました。いつものパスワードでサインインして続行してください。",
  paymentCheckoutSuccessSignIn: "サインイン",
  paymentCheckoutSuccessGoToAccount: "アカウントへ",
  paymentCheckoutSuccessGoToTools: "ツールに戻る",
  paymentCheckoutSuccessGoToCertificates: "証明書に戻る",
  paymentCheckoutCancelTitle: "お支払いをキャンセルしました",
  paymentCheckoutCancelBody:
    "チェックアウトがキャンセルされました。請求は発生していません。準備ができたらもう一度お試しください。",
  paymentCheckoutCancelRetry: "もう一度試す",
  paymentCheckoutCancelGoToTools: "ツールに戻る",
  paymentCheckoutCancelGoToCertificates: "証明書に戻る",
  accountSubscription: "サブスクリプション",
  accountDataPrivacy: "データとプライバシー",
  accountPlanFree: "無料",
  accountProfileHeading: "お名前",
  accountFirstName: "名",
  accountLastName: "姓",
  accountFirstNamePh: "名",
  accountLastNamePh: "姓",
  accountSaveProfile: "変更を保存",
  accountProfileSaved: "プロフィールを更新しました。",
  menuRecents: "最近使った項目",
  menuRecentsEmpty: "最近の生成はまだありません",
  menuRecentsShowMore: "もっと見る",
  menuAccountSettings: "アカウント設定",
  menuAbout: "Aidemiaについて",
  menuPersonalize: "パーソナライズ",
  menuPrivacy: "プライバシーポリシー",
  menuTerms: "利用規約",
  menuContact: "お問い合わせ",
  signIn: "サインイン",
  signOut: "サインアウト",
  itemDownload: "ダウンロード",
  itemEdit: "編集",
  itemBodyLabel: "内容",
  itemGeneratedWith: "生成元",
  itemProject: "プロジェクト",
  itemGenerateNew: "新しく生成する",
  itemDraftNote: "Aidemiaが生成した草案です。使用前に確認・編集してください。",
  audioPlay: "再生",
  audioPause: "一時停止",
  audioMute: "ミュート",
  audioUnmute: "ミュート解除",
  audioSeek: "シーク",
  audioVolume: "音量",
  audioError: "音声を読み込めませんでした。ファイルをダウンロードしてみてください。",
  audioLabel: "音声",
  cpSubtitle: "1件のクライアント対応をまとめる場所。",
  cpNameLabel: "プロジェクト名",
  cpNamePh: "例：クライアントS. — パニック",
  cpDescLabel: "説明",
  cpOptional: "任意",
  cpRequired: "必須",
  cpDescPh: "主訴、モダリティ、支援段階など…",
  cpAsideTitle: "プロジェクトについて",
  cpAsideText:
    "プロジェクトは1件のクライアント対応に関するすべて — メモ、計画、レター — を1か所にまとめ、後から見つけやすくします。",
  cpAsideHint: "ヒント：クライアントの氏名の代わりにイニシャルや短いコードを使いましょう。",
  cpCancel: "キャンセル",
  cpCreate: "作成",
  cpSave: "保存",
  cpEmptyProjTitle: "このプロジェクトはまだ空です",
  cpEmptyProj: "まだ何もありません — このプロジェクトで生成した草案はここに表示されます。",
  tfTitleLabel: "草案のタイトル",
  tfTitlePh: "例：セッション準備 — 第7週",
  tfProjectLabel: "プロジェクトに保存",
  tfContextLabel: "臨床コンテキスト",
  tfContextPh: "セッションメモ、紹介状、クライアントについての数行などを貼り付けてください…",
  tfToneLabel: "文体のトーン",
  tfToneOpts: ["温かい", "中立的", "臨床的"],
  tfNextLabel: "次のステップを提案する",
  tfDeepLabel: "深い臨床的推論",
  tfReset: "リセット",
  tfGenerate: "生成",
  tfMoreIn: "このカテゴリーの他のツール",
  tfMoreEmptyTitle: "他のツールはまだありません",
  tfMoreEmptyDesc: "このカテゴリーには現在このツールだけです。",
  badgePro: "PRO",
  badgeExp: "実験的",
  tfToday: "今日",
  tfGenBlockH: "草案",
  tfGenBlockP:
    "あなたのコンテキストに基づく構造化された最初の草案 — 確認、編集して自分のものにする準備が整っています。",
  tfCtxBlockH: "あなたのコンテキスト",
  tfClientLabel: "クライアント参照",
  tfClientPh: "例：クライアントM.（イニシャルのみ）",
  tfModalityLabel: "治療モダリティ",
  tfModalityOpts: ["CBT", "ACT", "DBT", "精神力動的", "パーソンセンタード", "統合的"],
  tfFocusLabel: "重点分野",
  tfFocusPh: "テーマ、目標、または草案で強調すべきこと…",
  tfFormatLabel: "出力形式",
  tfFormatOpts: ["箇条書き", "文章形式", "SOAPノート", "レター"],
  tfLengthLabel: "長さ",
  tfLengthOpts: ["簡潔", "標準", "詳細"],
  tfReadingLabel: "読解レベル",
  tfReadingOpts: ["わかりやすい言葉", "標準", "臨床的"],
  tfRiskLabel: "リスクスクリーニングを含める",
  tfMeasuresLabel: "アウトカム指標を参照する",
  tfAnonLabel: "クライアント情報を匿名化する",
  ctxTitle: "プロジェクトのコンテキスト",
  ctxNotesLabel: "メモ",
  ctxNotesPh:
    "独自のコンテキストを追加してください — 背景、目標、リスクの兆候、好み、次回のために覚えておきたいことなど…",
  ctxPrivate: "非公開・このデバイスに保存",
  ctxSummaryLabel: "草案から",
  ctxAuto: "自動",
  ctxUpdate: "更新",
  ctxSavedTag: "保存済み",
  ctxToolsUsed: "使用したツール",
  ctxNotesHelp: "AIツールでこのプロジェクトを選択した際に使用され、生成される草案を充実させます。",
  ctxSummaryTpl: "これまでに{n}件の{drafts}、最新は{date}。",
  ctxDraftWord: "草案",
  ctxDraftsWord: "草案",
  ctxCovers: "対象",
  ctxEmptySummary: "まだ草案はありません — このプロジェクトで作業を生成すると、ここに概要が表示されます。",
  authTitle: "アカウントを保護する",
  authSubtitle: "アカウントを作成すると、アプリのデータとPROステータスを安全に保てます。",
  authGoogle: "Googleで続ける",
  authApple: "Appleで続ける",
  authEmail: "メールで続ける",
  authGuest: "ゲストとして続ける",
  authClose: "閉じる",
  authBack: "戻る",
  authOr: "または",
  authEmailLabel: "メールアドレス",
  authEmailPh: "you@example.com",
  authPasswordLabel: "パスワード",
  authPasswordPh: "••••••••",
  authPasswordHint: "8文字以上。",
  authRestoreAccess: "パスワードをお忘れですか？",
  authRestoreTitle: "アクセスを復元",
  authRestoreSubtitle: "メールアドレスを入力すると、新しいパスワードをお送りします。",
  authRestoreSubmit: "新しいパスワードを送信",
  authRestoreSuccess: "このメールのアカウントがある場合、新しいパスワードを送信しました。",
  authSignInTitle: "サインイン",
  authSignUpTitle: "アカウントを作成",
  authSubmitSignIn: "サインイン",
  authSubmitSignUp: "アカウントを作成",
  authSubmitting: "お待ちください…",
  authSwitchToSignUp: "アカウントをお持ちでないですか？登録する",
  authSwitchToSignIn: "すでにアカウントをお持ちですか？サインイン",
  authErrorFallback: "問題が発生しました。もう一度お試しください。",
  authTooManyAttempts: "試行回数が多すぎます。しばらくしてからもう一度お試しください。",
  authEmailRequired: "メールアドレスを入力してください。",
  authEmailInvalid: "有効なメールアドレスを入力してください。",
  authPasswordRequired: "パスワードを入力してください。",
  authPasswordTooShort: "パスワードは8文字以上である必要があります。",
  authInvalidCredentials: "認証情報が正しくありません。",
  authEmailTaken: "このメールアドレスは既に登録されています。",
  formCheckFields: "ハイライトされた項目を確認してください。",
  validationFieldRequired: "{field}は必須です。",
  validationFieldEmail: "有効な{field}を入力してください。",
  validationFieldTaken: "この{field}は既に使用されています。",
  validationFieldMin: "{field}は{min}文字以上である必要があります。",
  validationFieldMax: "{field}は{max}文字以下である必要があります。",
  fieldLabelEmail: "メール",
  fieldLabelPassword: "パスワード",
  fieldLabelName: "名前",
  fieldLabelDescription: "説明",
  fieldLabelNotes: "メモ",
  fieldLabelTitle: "タイトル",
  fieldLabelFirstName: "名",
  fieldLabelLastName: "姓",
  cpNameRequired: "プロジェクト名は必須です。",
  hiTitleRequired: "タイトルは必須です。",
  tfRunError: "生成できませんでした。もう一度お試しください。",
  tfUsageLimitGuest: "ゲストの上限に達しました。続行するにはサインインまたはアカウント作成を。",
  tfUsageLimitFree: "無料枠の上限に達しました。さらに生成するには Pro にアップグレード。",
  tfNeedProject: "この生成を保存するには、先にプロジェクトを作成してください。",
  guestDraftsName: "下書き",
  defaultProjectName: "デフォルト",
  guestSyncFailed:
    "ゲストのプロジェクトをインポートできませんでした。ローカルの下書きは残っています。もう一度お試しください。",
  guestSyncNotClaimable:
    "一部のゲスト生成を引き継げませんでした（存在しないか、既に他のユーザーのもの）。ローカルの下書きは残っています。",
  guestSyncLimitsExceeded:
    "一度にインポートできるゲストのプロジェクト／生成数が上限を超えています。下書きを減らしてから再試行してください。",
  tfGenerating: "作成中…",
  tfGeneratingHint: "通常、数秒かかります。",
  tfCancelRequest: "リクエストをキャンセル",
  authGateTitle: "続けるにはサインインしてください",
  authGateSubtitle: "無料アカウントを作成して、プロジェクトと証明書を1か所で管理しましょう。",
  authGateCta: "サインインまたはアカウントを作成",
  retry: "再試行",
  proModalTitle: "制限なく作成",
  proModalDesc:
    "ChatGPTと他30のAIエンジンが、複雑なプロンプトなしであなた向けの心理療法コンテンツとアイデアを用意します。",
  proModalPriceWas: "$49",
  proModalPriceNow: "$19.99",
  proModalOffer: "無制限リクエスト · 本日の特別オファー",
  proModalRating: "4.9 · 3,920件のレビュー",
  proModalQuote: "「2025年、心理療法家に最適なアプリ。」— Open Academy",
  proModalTrusted: "信頼されています",
  proModalTrustedLabel: "人の専門家",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "50以上のクリエイティブモジュール",
      desc: "仕事を楽にするために必要なコンテンツを作成できます。",
    },
    {
      emoji: "🐣",
      title: "技術スキル不要",
      desc: "複雑なプロンプトではなく、簡単なフォームを使います。",
    },
    {
      emoji: "🚩",
      title: "地域に合わせた調整",
      desc: "コンテンツを地域の基準に合わせて調整します。",
    },
    {
      emoji: "🧠",
      title: "パーソナライズされた結果",
      desc: "患者の履歴とあなたのスタイルに基づくコンテンツ。",
    },
    {
      emoji: "⌛",
      title: "待ち時間なし",
      desc: "待たずにコンテンツとアイデアをすばやく生成。",
    },
    {
      emoji: "🚀",
      title: "無制限クォータ",
      desc: "必要なだけコンテンツを作成できます。",
    },
  ],
  proModalCta: "フルアクセスを取得",
  proModalClose: "閉じる",
  proModalSchoolsAlt: "Harvard、Eton、Cambridge、Harrow、Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "時間の節約",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemiaは業務を効率化したいセラピストにとって大きな転換点です。時間は常に貴重で、Aidemiaはそれをよく理解しています。直感的なUIとAIツールで、準備と分析の時間を大幅に短縮できます。",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "コスパが良い",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemiaは時間だけでなくお金も節約してくれます。多数のAIツールの機能を一つのプラットフォームに集約し、高額な複数サブスクの必要をなくします。心理療法家として、その可能性の幅と日々の価値は計り知れません。",
    },
  ],
  proModalDataProtected: "データは保護されています",
  proModalTermsShort: "利用規約",
  proModalGuarantee: "14日間の返金保証",
  proModalLegalNote:
    "割引は適用済みです。パスですべてのコンテンツと将来の機能に、追加料金なしでアクセスできます。いつでも解約でき、4週間ごとの自動更新も選べます。ご支援の一部は子ども支援の慈善団体に充てられます。",
};

const ru: TranslationStrings = {
  navProjects: "Проекты",
  navHistory: "История",
  navTools: "Инструменты",
  navChat: "Чат",
  navCerts: "Сертификаты",
  navAccount: "Аккаунт",
  toolsTitle: "Инструменты",
  toolsDesc:
    "ИИ-инструменты для каждого этапа клинической работы — от направления до последующего наблюдения. Каждый создаёт черновик, который вы проверяете и дорабатываете.",
  toolsCountLabel: "инструментов",
  all: "Все",
  projectsTitle: "Проекты",
  projectsDesc: "Всё, что вы создали, организовано по работе с клиентами.",
  projTab: "Проекты",
  histTab: "История",
  historyTitle: "История",
  historyDesc: "Все черновики, которые вы создали — по всем проектам, сначала новые.",
  createProject: "Создать проект",
  editProject: "Изменить проект",
  emptyProjectsTitle: "Пока нет проектов",
  emptyProjectsDesc:
    "Создайте проект для клиента или задачи. Черновики, которые вы сгенерируете, появятся здесь — в порядке и под рукой.",
  projectsProCtaTitle: "Организуйте работу в проектах",
  projectsProCtaDesc:
    "Перейдите на Pro, чтобы создавать отдельные проекты для каждого клиента или случая — черновики останутся структурированными и легко находимыми.",
  projectsProCtaBenefits: [
    "Создавайте неограниченное число проектов",
    "Разделяйте работу по клиентам и случаям",
    "Держите каждый черновик на своём месте",
  ],
  emptyHistoryTitle: "История пуста",
  emptyHistoryDesc:
    "Когда вы сгенерируете черновик, он появится здесь — сначала самые новые.",
  emptyHistoryCta: "Открыть инструменты",
  notFoundTitle: "Страница не найдена",
  notFoundDesc:
    "Инструмент, проект или черновик, который вы ищете, не существует или был перемещён.",
  notFoundCtaTools: "К инструментам",
  notFoundCtaProjects: "К проектам",
  paginationPrevious: "Назад",
  paginationNext: "Далее",
  paginationPageOf: "Страница {page} из {totalPages}",
  paginationAria: "Пагинация проектов",
  chatTitle: "Ассистент",
  chatDesc: "Размышляйте вслух с ассистентом, который знает ваши инструменты.",
  chatPlaceholder: "Спросите что угодно…",
  chatSend: "Отправить",
  certsTitle: "Сертификат членства",
  certsDesc:
    "Полное членство в World Innovative Teaching Association с печатным, проверяемым сертификатом — отдельно от подписки Aidemia Pro.",
  certsObserverNote:
    "Вы являетесь наблюдающим членом World Innovative Teaching Association. Перейдите на полное членство, чтобы получить сертификат.",
  certsRepurchaseNote:
    "Ваш предыдущий доступ к членству больше не активен. Вы можете купить сертификат снова.",
  certsWhyTitle: "Зачем получать сертификат",
  certsBenefits: [
    "Официальное подтверждение профессионального членства в WITA",
    "Уникальный серийный номер с онлайн-проверкой",
    "Горизонтальный сертификат, готовый к печати или отправке",
    "Признанная квалификация для школ и учреждений",
    "Запись в реестре членов ассоциации",
    "Ежемесячное членство по ограниченной цене",
  ],
  certsPlanLabel: "Ежемесячное членство",
  certsPriceWas: "100,00 $",
  certsPriceNow: "49,99 $",
  certsNotInPro:
    "Не входит в Aidemia Pro. Вы платите только за сертификат членства и полное членство в WITA.",
  certsCta: "Вступить и получить сертификат",
  certsSecurePay: "Безопасная оплата на нашей платёжной странице.",
  certsSpecimen: "Образец",
  certsSpecimenCaption:
    "Образец — не выданный сертификат. Предпросмотр официального дизайна WITA.",
  certsSpecimenAlt: "Образец — предпросмотр сертификата членства",
  certsPurchasedNote:
    "Ваш сертификат полного членства в World Innovative Teaching Association активен.",
  certsSerialNumber: "Серийный номер документа: {serial}",
  certsIncludesTitle: "Ваше членство включает",
  certsDownload: "Скачать сертификат",
  certsDownloading: "Скачивание…",
  certsDownloadError: "Не удалось скачать сертификат. Попробуйте ещё раз.",
  certsPreviewCaption:
    "В выданном сертификате используются имя из профиля и серийный номер.",
  certsPreviewAlt: "Предпросмотр сертификата членства",
  certsProfileIncompleteBoth:
    "Имя и фамилия не заполнены. В сертификате используется имя из вашего профиля.",
  certsProfileIncompleteFirst:
    "Имя не заполнено. В сертификате используется имя из вашего профиля.",
  certsProfileIncompleteLast:
    "Фамилия не заполнена. В сертификате используется имя из вашего профиля.",
  certsProfileIncompleteLink: "Заполнить профиль",
  certsVerifyEyebrow: "Членство WITA",
  certsVerifyTitle: "Проверка сертификата",
  certsVerifyDesc:
    "Введите серийный номер документа, чтобы подтвердить подлинность и актуальность сертификата членства.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "Проверка подтверждает только действительность — персональные данные не показываются.",
  certsVerifyLabel: "Серийный номер документа",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Формат: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Введите серийный номер сертификата.",
  certsVerifyFormatError: "Введите номер вида CERT-2026-000001.",
  certsVerifyCta: "Проверить сертификат",
  certsVerifyChecking: "Проверка…",
  certsVerifyValidTitle: "Сертификат действителен",
  certsVerifyValidBody:
    "Этот сертификат членства выдан {date} и сейчас активен.",
  certsVerifyInvalidTitle: "Сертификат не подтверждён",
  certsVerifyInvalidBody:
    "Не удалось подтвердить этот номер. Возможно, он введён с ошибкой, истёк или больше не действует.",
  certsVerifyAgain: "Проверить другой номер",
  certsVerifyError:
    "Не удалось связаться со службой проверки. Проверьте соединение и попробуйте снова.",
  certsVerifyBackToCerts: "К сертификату членства",
  certsVerifyOpenLink: "Проверить номер сертификата",
  accountTitle: "Аккаунт",
  accountEmailVerified: "Email подтверждён",
  accountEmailUnverified: "Email не подтверждён",
  accountResendConfirmation: "Отправить письмо подтверждения ещё раз",
  accountResendConfirmationSending: "Отправка…",
  accountResendConfirmationSent: "Письмо подтверждения отправлено. Проверьте почту.",
  emailConfirmConfirming: "Подтверждаем email…",
  emailConfirmSuccessTitle: "Email подтверждён",
  emailConfirmSuccessBody:
    "Ваш адрес email подтверждён. Можно продолжать пользоваться Aidemia.",
  emailConfirmInvalidTitle: "Ссылка подтверждения недействительна",
  emailConfirmInvalidBody:
    "Эта ссылка недействительна или устарела. Запросите новое письмо подтверждения и попробуйте снова.",
  emailConfirmMissingTitle: "Нет ссылки подтверждения",
  emailConfirmMissingBody:
    "Откройте ссылку из письма, чтобы подтвердить адрес email.",
  emailConfirmErrorTitle: "Не удалось подтвердить email",
  emailConfirmGoToAccount: "Перейти в аккаунт",
  emailConfirmGoHome: "На главную",
  loginLinkConsuming: "Выполняем вход…",
  loginLinkMissingTitle: "Ссылка недействительна",
  loginLinkMissingBody:
    "В этой ссылке для входа нет токена. Откройте ссылку из письма или подтверждения оплаты ещё раз.",
  loginLinkInvalidTitle: "Ссылка истекла или уже использована",
  loginLinkInvalidBody:
    "Эта ссылка для входа истекла или уже была использована. Запросите новую, чтобы продолжить.",
  loginLinkErrorTitle: "Не удалось войти",
  loginLinkGoHome: "На главную",
  projectLandingEyebrow: "Ваш проект",
  projectLandingBody: "Вы вошли. Откройте рабочее пространство, чтобы продолжить.",
  projectLandingMissingTitle: "Проект не найден",
  projectLandingMissingBody: "Этого проекта нет, или у вас нет к нему доступа.",
  projectLandingErrorTitle: "Не удалось загрузить проект",
  projectLandingGoHome: "На главную",
  projectLandingOpenWorkspace: "Открыть рабочее пространство",
  paymentCheckoutSuccessTitle: "Оплата прошла успешно",
  paymentCheckoutSuccessBody:
    "Спасибо. Оплата прошла успешно. Доступ Pro скоро появится в вашем аккаунте.",
  paymentCheckoutSuccessCompleting: "Подтверждаем оплату…",
  paymentCheckoutSuccessGuestBody:
    "Спасибо. Аккаунт готов — мы отправили пароль на email. Вы уже вошли, доступ Pro активен.",
  paymentCheckoutSuccessExistingAccountBody:
    "Спасибо. Оплата привязана к вашему существующему аккаунту. Войдите с обычным паролем, чтобы продолжить.",
  paymentCheckoutSuccessSignIn: "Войти",
  paymentCheckoutSuccessGoToAccount: "Перейти в аккаунт",
  paymentCheckoutSuccessGoToTools: "К инструментам",
  paymentCheckoutSuccessGoToCertificates: "К сертификатам",
  paymentCheckoutCancelTitle: "Оплата отменена",
  paymentCheckoutCancelBody:
    "Оформление отменено. Списаний не было. Вы можете попробовать снова, когда будете готовы.",
  paymentCheckoutCancelRetry: "Попробовать снова",
  paymentCheckoutCancelGoToTools: "К инструментам",
  paymentCheckoutCancelGoToCertificates: "К сертификатам",
  accountSubscription: "Подписка",
  accountDataPrivacy: "Данные и конфиденциальность",
  accountPlanFree: "Бесплатно",
  accountProfileHeading: "Ваше имя",
  accountFirstName: "Имя",
  accountLastName: "Фамилия",
  accountFirstNamePh: "Имя",
  accountLastNamePh: "Фамилия",
  accountSaveProfile: "Сохранить",
  accountProfileSaved: "Профиль обновлён.",
  menuRecents: "Недавние",
  menuRecentsEmpty: "Пока нет недавних генераций",
  menuRecentsShowMore: "Показать ещё",
  menuAccountSettings: "Настройки аккаунта",
  menuAbout: "О Aidemia",
  menuPersonalize: "Персонализация",
  menuPrivacy: "Политика конфиденциальности",
  menuTerms: "Условия использования",
  menuContact: "Связаться с нами",
  signIn: "Войти",
  signOut: "Выйти",
  itemDownload: "Скачать",
  itemEdit: "Редактировать",
  itemBodyLabel: "Содержание",
  itemGeneratedWith: "Создано с помощью",
  itemProject: "Проект",
  itemGenerateNew: "Создать новый",
  itemDraftNote: "Черновик создан Aidemia — проверьте и отредактируйте перед использованием.",
  audioPlay: "Слушать",
  audioPause: "Пауза",
  audioMute: "Выключить звук",
  audioUnmute: "Включить звук",
  audioSeek: "Перемотка",
  audioVolume: "Громкость",
  audioError: "Не удалось загрузить аудио. Попробуйте скачать файл.",
  audioLabel: "Аудио",
  cpSubtitle: "Место для работы с одним клиентом.",
  cpNameLabel: "Название проекта",
  cpNamePh: "например, Клиент С. — Панические атаки",
  cpDescLabel: "Описание",
  cpOptional: "необязательно",
  cpRequired: "обязательно",
  cpDescPh: "Причина обращения, модальность, этап работы…",
  cpAsideTitle: "О проектах",
  cpAsideText:
    "Проект собирает всю работу с одним клиентом в одном месте — заметки, планы и письма — чтобы легко находить их позже.",
  cpAsideHint: "Совет: используйте инициалы или короткий код вместо полного имени клиента.",
  cpCancel: "Отмена",
  cpCreate: "Создать",
  cpSave: "Сохранить",
  cpEmptyProjTitle: "Проект пока пуст",
  cpEmptyProj: "Здесь пока пусто — черновики, созданные в этом проекте, появятся здесь.",
  tfTitleLabel: "Название черновика",
  tfTitlePh: "например, Подготовка к сессии — неделя 7",
  tfProjectLabel: "Сохранить в проект",
  tfContextLabel: "Клинический контекст",
  tfContextPh: "Вставьте заметки сессии, письмо-направление или несколько строк о клиенте…",
  tfToneLabel: "Тон общения",
  tfToneOpts: ["Тёплый", "Нейтральный", "Клинический"],
  tfNextLabel: "Предложить следующие шаги",
  tfDeepLabel: "Глубокий клинический анализ",
  tfReset: "Сбросить",
  tfGenerate: "Создать",
  tfMoreIn: "Больше в этой категории",
  tfMoreEmptyTitle: "Других инструментов пока нет",
  tfMoreEmptyDesc: "Сейчас это единственный инструмент в этой категории.",
  badgePro: "PRO",
  badgeExp: "ЭКСПЕРИМЕНТАЛЬНО",
  tfToday: "Сегодня",
  tfGenBlockH: "Черновик",
  tfGenBlockP:
    "Структурированный первый черновик на основе вашего контекста — готов к проверке, редактированию и доработке.",
  tfCtxBlockH: "Ваш контекст",
  tfClientLabel: "Обозначение клиента",
  tfClientPh: "например, Клиент М. (только инициалы)",
  tfModalityLabel: "Терапевтическая модальность",
  tfModalityOpts: ["КПТ", "ACT", "ДБТ", "Психодинамическая", "Клиент-центрированная", "Интегративная"],
  tfFocusLabel: "Ключевые направления",
  tfFocusPh: "Темы, цели или то, на чём должен сделать акцент черновик…",
  tfFormatLabel: "Формат вывода",
  tfFormatOpts: ["Списком", "Текстом", "SOAP-заметка", "Письмо"],
  tfLengthLabel: "Длина",
  tfLengthOpts: ["Краткая", "Стандартная", "Подробная"],
  tfReadingLabel: "Уровень изложения",
  tfReadingOpts: ["Простой язык", "Стандартный", "Клинический"],
  tfRiskLabel: "Включить скрининг рисков",
  tfMeasuresLabel: "Указать шкалы результатов",
  tfAnonLabel: "Анонимизировать данные клиента",
  ctxTitle: "Контекст проекта",
  ctxNotesLabel: "Заметки",
  ctxNotesPh:
    "Добавьте свой контекст — предысторию, цели, признаки риска, предпочтения — всё, что нужно помнить в следующий раз…",
  ctxPrivate: "Приватно · сохранено на этом устройстве",
  ctxSummaryLabel: "Из ваших черновиков",
  ctxAuto: "Авто",
  ctxUpdate: "Обновить",
  ctxSavedTag: "Сохранено",
  ctxToolsUsed: "Использованные инструменты",
  ctxNotesHelp:
    "Используется при выборе этого проекта в ИИ-инструменте, чтобы обогатить создаваемые черновики.",
  ctxSummaryTpl: "{n} {drafts} на данный момент, последний — {date}.",
  ctxDraftWord: "черновик",
  ctxDraftsWord: "черновиков",
  ctxCovers: "Охватывает",
  ctxEmptySummary:
    "Черновиков пока нет — сводка появится здесь по мере создания работы в этом проекте.",
  authTitle: "Защитите свой аккаунт",
  authSubtitle: "Аккаунт помогает надёжно сохранить данные приложения и статус PRO.",
  authGoogle: "Продолжить с Google",
  authApple: "Продолжить с Apple",
  authEmail: "Продолжить с email",
  authGuest: "Продолжить как гость",
  authClose: "Закрыть",
  authBack: "Назад",
  authOr: "или",
  authEmailLabel: "Email",
  authEmailPh: "you@example.com",
  authPasswordLabel: "Пароль",
  authPasswordPh: "••••••••",
  authPasswordHint: "Минимум 8 символов.",
  authRestoreAccess: "Забыли пароль?",
  authRestoreTitle: "Восстановить доступ",
  authRestoreSubtitle: "Укажите email — мы отправим новый пароль.",
  authRestoreSubmit: "Отправить новый пароль",
  authRestoreSuccess: "Если аккаунт с этим email существует, новый пароль уже отправлен.",
  authSignInTitle: "Войти",
  authSignUpTitle: "Создайте аккаунт",
  authSubmitSignIn: "Войти",
  authSubmitSignUp: "Создать аккаунт",
  authSubmitting: "Подождите…",
  authSwitchToSignUp: "Нет аккаунта? Зарегистрироваться",
  authSwitchToSignIn: "Уже есть аккаунт? Войти",
  authErrorFallback: "Что-то пошло не так. Попробуйте снова.",
  authTooManyAttempts: "Слишком много попыток. Подождите немного и попробуйте снова.",
  authEmailRequired: "Укажите email.",
  authEmailInvalid: "Введите корректный email.",
  authPasswordRequired: "Укажите пароль.",
  authPasswordTooShort: "Пароль должен содержать не менее 8 символов.",
  authInvalidCredentials: "Неверный email или пароль.",
  authEmailTaken: "Этот email уже зарегистрирован.",
  formCheckFields: "Проверьте выделенные поля.",
  validationFieldRequired: "Поле «{field}» обязательно.",
  validationFieldEmail: "Введите корректный {field}.",
  validationFieldTaken: "Это значение «{field}» уже занято.",
  validationFieldMin: "«{field}» — не менее {min} символов.",
  validationFieldMax: "«{field}» — не более {max} символов.",
  fieldLabelEmail: "Email",
  fieldLabelPassword: "Пароль",
  fieldLabelName: "Название",
  fieldLabelDescription: "Описание",
  fieldLabelNotes: "Заметки",
  fieldLabelTitle: "Заголовок",
  fieldLabelFirstName: "Имя",
  fieldLabelLastName: "Фамилия",
  cpNameRequired: "Укажите название проекта.",
  hiTitleRequired: "Укажите заголовок.",
  tfRunError: "Не удалось создать. Попробуйте снова.",
  tfUsageLimitGuest: "Лимит гостя исчерпан. Войдите или создайте аккаунт, чтобы продолжить.",
  tfUsageLimitFree: "Бесплатный лимит исчерпан. Перейдите на Pro для новых генераций.",
  tfNeedProject: "Сначала создайте проект, чтобы сохранить генерацию.",
  guestDraftsName: "Черновики",
  defaultProjectName: "По умолчанию",
  guestSyncFailed:
    "Не удалось импортировать гостевые проекты. Локальные черновики сохранены — попробуйте снова.",
  guestSyncNotClaimable:
    "Часть гостевых генераций нельзя перенести (нет или уже принадлежат другому). Локальные черновики сохранены.",
  guestSyncLimitsExceeded:
    "Слишком много гостевых проектов или генераций для одного импорта. Удалите часть черновиков и попробуйте снова.",
  tfGenerating: "Создаём…",
  tfGeneratingHint: "Обычно это занимает несколько секунд.",
  tfCancelRequest: "Отменить запрос",
  authGateTitle: "Войдите, чтобы продолжить",
  authGateSubtitle: "Создайте бесплатный аккаунт, чтобы хранить проекты и сертификаты в одном месте.",
  authGateCta: "Войти или создать аккаунт",
  retry: "Повторить",
  proModalTitle: "Создавайте без ограничений",
  proModalDesc:
    "Уникальный психотерапевтический контент и идеи под вас — с ChatGPT и ещё 30 ИИ-движками, без сложных промптов.",
  proModalPriceWas: "$49",
  proModalPriceNow: "$19.99",
  proModalOffer: "Безлимитные запросы · специальное предложение дня",
  proModalRating: "4,9 · 3920 отзывов",
  proModalQuote: "«Лучшее приложение для психотерапевтов в 2025.» — Open Academy",
  proModalTrusted: "Нам доверяют",
  proModalTrustedLabel: "специалистов",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Более 50 творческих модулей",
      desc: "Создавайте любой нужный контент, чтобы упростить работу.",
    },
    {
      emoji: "🐣",
      title: "Без технических навыков",
      desc: "Простые формы вместо ручного написания сложных промптов.",
    },
    {
      emoji: "🚩",
      title: "Региональные настройки",
      desc: "Адаптируйте контент под локальные стандарты.",
    },
    {
      emoji: "🧠",
      title: "Персонализированные результаты",
      desc: "Контент на основе истории пациента и вашего стиля.",
    },
    {
      emoji: "⌛",
      title: "Без ожидания",
      desc: "Генерируйте контент и идеи быстро, без ожидания.",
    },
    {
      emoji: "🚀",
      title: "Безлимитная квота",
      desc: "Создавайте столько контента, сколько нужно.",
    },
  ],
  proModalCta: "Получить полный доступ",
  proModalClose: "Закрыть",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow и Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Экономия времени",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemia полностью меняет работу терапевтов, которым нужно упростить практику. Время всегда в дефиците — и Aidemia это понимает. Интуитивный интерфейс и ИИ-инструменты заметно сокращают подготовку и анализ.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Выгодно",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemia экономит не только время, но и деньги. Объединяя функции множества ИИ-инструментов на одной платформе, он избавляет от дорогих подписок на несколько сервисов. Как психотерапевт, я не могу недооценить спектр возможностей и ту ценность, которую получаю каждый день.",
    },
  ],
  proModalDataProtected: "Ваши данные защищены",
  proModalTermsShort: "Условия",
  proModalGuarantee: "Гарантия возврата 14 дней",
  proModalLegalNote:
    "Скидка уже учтена. Пропуск даёт доступ ко всем типам контента, включая будущие функции без повышения цены. Можно отменить в любой момент или продлевать автоматически каждые четыре недели. Часть взноса направляется в благотворительные фонды для детей.",
};

const nl: TranslationStrings = {
  navProjects: "Projecten",
  navHistory: "Geschiedenis",
  navTools: "Tools",
  navChat: "Chat",
  navCerts: "Certificaten",
  navAccount: "Account",
  toolsTitle: "Tools",
  toolsDesc:
    "AI-tools voor elke fase van het klinische werk — van verwijzing tot follow-up. Elke tool levert een concept dat u beoordeelt en zich eigen maakt.",
  toolsCountLabel: "tools",
  all: "Alle",
  projectsTitle: "Projecten",
  projectsDesc: "Alles wat u heeft gegenereerd, georganiseerd per cliëntdossier.",
  projTab: "Projecten",
  histTab: "Geschiedenis",
  historyTitle: "Geschiedenis",
  historyDesc: "Alle concepten die u heeft gegenereerd — over alle projecten, nieuwste eerst.",
  createProject: "Project aanmaken",
  editProject: "Project bewerken",
  emptyProjectsTitle: "Nog geen projecten",
  emptyProjectsDesc:
    "Start een project voor een cliënt of casus. Concepten die u genereert komen hier terecht — geordend en makkelijk te vinden.",
  projectsProCtaTitle: "Organiseer uw werk in projecten",
  projectsProCtaDesc:
    "Upgrade naar Pro om aangepaste projecten per cliënt of casus te maken — houd elk concept gestructureerd en makkelijk te vinden.",
  projectsProCtaBenefits: [
    "Maak onbeperkt aangepaste projecten",
    "Scheid werk per cliënt of casus",
    "Houd elk concept op de juiste plek",
  ],
  emptyHistoryTitle: "Nog geen geschiedenis",
  emptyHistoryDesc:
    "Wanneer u een concept genereert, verschijnt het hier — nieuwste eerst.",
  emptyHistoryCta: "Tools bekijken",
  notFoundTitle: "Deze pagina is niet gevonden",
  notFoundDesc:
    "De tool, het project of het concept dat u zoekt bestaat niet of is verplaatst.",
  notFoundCtaTools: "Terug naar Tools",
  notFoundCtaProjects: "Naar Projecten",
  paginationPrevious: "Vorige",
  paginationNext: "Volgende",
  paginationPageOf: "Pagina {page} van {totalPages}",
  paginationAria: "Paginering projecten",
  chatTitle: "Assistent",
  chatDesc: "Denk luidop met een assistent die uw tools kent.",
  chatPlaceholder: "Vraag iets…",
  chatSend: "Verzenden",
  certsTitle: "Lidmaatschapscertificaat",
  certsDesc:
    "Volledig lidmaatschap van de World Innovative Teaching Association met een afdrukbaar, verifieerbaar certificaat — los van je Aidemia Pro-abonnement.",
  certsObserverNote:
    "Je bent waarnemend lid van de World Innovative Teaching Association. Upgrade naar volledig lidmaatschap om je certificaat te ontvangen.",
  certsRepurchaseNote:
    "Je eerdere lidmaatschapstoegang is niet meer actief. Je kunt het certificaat opnieuw kopen.",
  certsWhyTitle: "Waarom het certificaat aanvragen",
  certsBenefits: [
    "Officieel bewijs van professioneel lidmaatschap van WITA",
    "Uniek serienummer met online validatie",
    "Liggend certificaat, klaar om te printen of te delen",
    "Erkende kwalificatie voor scholen en instellingen",
    "Vermelding in het ledenregister van de association",
    "Maandelijks lidmaatschap tegen een tijdelijke prijs",
  ],
  certsPlanLabel: "Maandelijks lidmaatschap",
  certsPriceWas: "$100,00",
  certsPriceNow: "$49,99",
  certsNotInPro:
    "Niet inbegrepen bij Aidemia Pro. Je betaalt alleen voor het lidmaatschapscertificaat en volledig WITA-lidmaatschap.",
  certsCta: "Lid worden en certificaat krijgen",
  certsSecurePay: "Veilig betalen op onze betaalpagina.",
  certsSpecimen: "Specimen",
  certsSpecimenCaption:
    "Specimen — geen echt uitgegeven certificaat. Voorbeeld van het officiële WITA-ontwerp.",
  certsSpecimenAlt: "Specimen — voorbeeld van het lidmaatschapscertificaat",
  certsPurchasedNote:
    "Uw volledige lidmaatschapscertificaat van de World Innovative Teaching Association is actief.",
  certsSerialNumber: "Serienummer van het document: {serial}",
  certsIncludesTitle: "Uw lidmaatschap omvat",
  certsDownload: "Certificaat downloaden",
  certsDownloading: "Downloaden…",
  certsDownloadError: "Het certificaat kon niet worden gedownload. Probeer het opnieuw.",
  certsPreviewCaption:
    "Uw uitgegeven certificaat gebruikt uw profielnaam en serienummer.",
  certsPreviewAlt: "Voorbeeld van het lidmaatschapscertificaat",
  certsProfileIncompleteBoth:
    "Uw voor- en achternaam zijn niet ingevuld. Het certificaat gebruikt de naam uit uw profiel.",
  certsProfileIncompleteFirst:
    "Uw voornaam is niet ingevuld. Het certificaat gebruikt de naam uit uw profiel.",
  certsProfileIncompleteLast:
    "Uw achternaam is niet ingevuld. Het certificaat gebruikt de naam uit uw profiel.",
  certsProfileIncompleteLink: "Profiel invullen",
  certsVerifyEyebrow: "WITA-lidmaatschap",
  certsVerifyTitle: "Certificaat verifiëren",
  certsVerifyDesc:
    "Voer het serienummer van het document in om te bevestigen dat een lidmaatschapscertificaat echt en momenteel geldig is.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "Verificatie bevestigt alleen de geldigheid — persoonlijke gegevens worden nooit getoond.",
  certsVerifyLabel: "Document-serienummer",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Formaat: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Voer een serienummer van het certificaat in.",
  certsVerifyFormatError: "Voer een nummer in zoals CERT-2026-000001.",
  certsVerifyCta: "Certificaat verifiëren",
  certsVerifyChecking: "Bezig met controleren…",
  certsVerifyValidTitle: "Certificaat is geldig",
  certsVerifyValidBody:
    "Dit lidmaatschapscertificaat is uitgegeven op {date} en is momenteel actief.",
  certsVerifyInvalidTitle: "Certificaat niet geverifieerd",
  certsVerifyInvalidBody:
    "We konden dit nummer niet bevestigen. Het kan verkeerd zijn getypt, verlopen of niet meer actief.",
  certsVerifyAgain: "Ander nummer controleren",
  certsVerifyError:
    "De verificatieservice is niet bereikbaar. Controleer je verbinding en probeer het opnieuw.",
  certsVerifyBackToCerts: "Terug naar lidmaatschapscertificaat",
  certsVerifyOpenLink: "Een certificaatnummer verifiëren",
  accountTitle: "Account",
  accountEmailVerified: "E-mail bevestigd",
  accountEmailUnverified: "E-mail niet bevestigd",
  accountResendConfirmation: "Bevestigingsmail opnieuw versturen",
  accountResendConfirmationSending: "Verzenden…",
  accountResendConfirmationSent: "Bevestigingsmail verzonden. Controleer je inbox.",
  emailConfirmConfirming: "E-mail bevestigen…",
  emailConfirmSuccessTitle: "E-mail bevestigd",
  emailConfirmSuccessBody:
    "Je e-mailadres is geverifieerd. Je kunt Aidemia blijven gebruiken.",
  emailConfirmInvalidTitle: "Bevestigingslink ongeldig",
  emailConfirmInvalidBody:
    "Deze bevestigingslink is ongeldig of verlopen. Vraag een nieuwe bevestigingsmail aan en probeer het opnieuw.",
  emailConfirmMissingTitle: "Bevestigingslink ontbreekt",
  emailConfirmMissingBody:
    "Open de bevestigingslink uit je e-mail om je adres te verifiëren.",
  emailConfirmErrorTitle: "E-mail kon niet worden bevestigd",
  emailConfirmGoToAccount: "Naar account",
  emailConfirmGoHome: "Terug naar home",
  loginLinkConsuming: "Bezig met inloggen…",
  loginLinkMissingTitle: "Link is ongeldig",
  loginLinkMissingBody:
    "Aan deze inloglink ontbreekt een token. Open de link uit je e-mail of betalingsbevestiging opnieuw.",
  loginLinkInvalidTitle: "Link verlopen of al gebruikt",
  loginLinkInvalidBody:
    "Deze inloglink is verlopen of al gebruikt. Vraag een nieuwe aan om door te gaan.",
  loginLinkErrorTitle: "Inloggen mislukt",
  loginLinkGoHome: "Terug naar home",
  projectLandingEyebrow: "Jouw project",
  projectLandingBody: "Je bent ingelogd. Open de werkruimte om verder te gaan.",
  projectLandingMissingTitle: "Project niet gevonden",
  projectLandingMissingBody: "Dit project bestaat niet of je hebt er geen toegang toe.",
  projectLandingErrorTitle: "Project kon niet worden geladen",
  projectLandingGoHome: "Terug naar home",
  projectLandingOpenWorkspace: "Werkruimte openen",
  paymentCheckoutSuccessTitle: "Betaling geslaagd",
  paymentCheckoutSuccessBody:
    "Bedankt. Je betaling is gelukt. Pro-toegang verschijnt binnenkort in je account.",
  paymentCheckoutSuccessCompleting: "Betaling bevestigen…",
  paymentCheckoutSuccessGuestBody:
    "Bedankt. Je account is klaar — we hebben je een wachtwoord gemaild. Je bent ingelogd en Pro-toegang is actief.",
  paymentCheckoutSuccessExistingAccountBody:
    "Bedankt. Je betaling is gekoppeld aan je bestaande account. Log in met je gebruikelijke wachtwoord om verder te gaan.",
  paymentCheckoutSuccessSignIn: "Inloggen",
  paymentCheckoutSuccessGoToAccount: "Naar account",
  paymentCheckoutSuccessGoToTools: "Terug naar tools",
  paymentCheckoutSuccessGoToCertificates: "Terug naar certificaten",
  paymentCheckoutCancelTitle: "Betaling geannuleerd",
  paymentCheckoutCancelBody:
    "Checkout is geannuleerd. Er is niets in rekening gebracht. Je kunt het opnieuw proberen wanneer je wilt.",
  paymentCheckoutCancelRetry: "Opnieuw proberen",
  paymentCheckoutCancelGoToTools: "Terug naar tools",
  paymentCheckoutCancelGoToCertificates: "Terug naar certificaten",
  accountSubscription: "Abonnement",
  accountDataPrivacy: "Gegevens & privacy",
  accountPlanFree: "Gratis",
  accountProfileHeading: "Je naam",
  accountFirstName: "Voornaam",
  accountLastName: "Achternaam",
  accountFirstNamePh: "Voornaam",
  accountLastNamePh: "Achternaam",
  accountSaveProfile: "Wijzigingen opslaan",
  accountProfileSaved: "Profiel bijgewerkt.",
  menuRecents: "Recent",
  menuRecentsEmpty: "Nog geen recente generaties",
  menuRecentsShowMore: "Meer tonen",
  menuAccountSettings: "Accountinstellingen",
  menuAbout: "Over Aidemia",
  menuPersonalize: "Personaliseren",
  menuPrivacy: "Privacybeleid",
  menuTerms: "Algemene voorwaarden",
  menuContact: "Contact",
  signIn: "Inloggen",
  signOut: "Uitloggen",
  itemDownload: "Downloaden",
  itemEdit: "Bewerken",
  itemBodyLabel: "Inhoud",
  itemGeneratedWith: "Gegenereerd met",
  itemProject: "Project",
  itemGenerateNew: "Nieuw genereren",
  itemDraftNote: "Concept gegenereerd door Aidemia — controleer en bewerk voor gebruik.",
  audioPlay: "Afspelen",
  audioPause: "Pauze",
  audioMute: "Dempen",
  audioUnmute: "Dempen opheffen",
  audioSeek: "Zoeken",
  audioVolume: "Volume",
  audioError: "Deze audio kon niet worden geladen. Probeer het bestand te downloaden.",
  audioLabel: "Audio",
  cpSubtitle: "Een plek voor het werk aan één cliëntdossier.",
  cpNameLabel: "Projectnaam",
  cpNamePh: "bijv. Cliënt S. — Paniek",
  cpDescLabel: "Beschrijving",
  cpOptional: "optioneel",
  cpRequired: "verplicht",
  cpDescPh: "Hulpvraag, modaliteit, fase van de behandeling…",
  cpAsideTitle: "Over projecten",
  cpAsideText:
    "Een project verzamelt al het werk aan één cliëntdossier op één plek — notities, plannen en brieven — zodat u het later makkelijk terugvindt.",
  cpAsideHint: "Tip: gebruik initialen of een korte code in plaats van de volledige naam van de cliënt.",
  cpCancel: "Annuleren",
  cpCreate: "Aanmaken",
  cpSave: "Opslaan",
  cpEmptyProjTitle: "Dit project is nog leeg",
  cpEmptyProj: "Hier is nog niets — concepten die u in dit project genereert, verschijnen hier.",
  tfTitleLabel: "Titel van het concept",
  tfTitlePh: "bijv. Sessievoorbereiding — week 7",
  tfProjectLabel: "Opslaan in project",
  tfContextLabel: "Klinische context",
  tfContextPh: "Plak sessienotities, een verwijsbrief of enkele regels over de cliënt…",
  tfToneLabel: "Toon",
  tfToneOpts: ["Warm", "Neutraal", "Klinisch"],
  tfNextLabel: "Volgende stappen voorstellen",
  tfDeepLabel: "Diepgaande klinische redenering",
  tfReset: "Resetten",
  tfGenerate: "Genereren",
  tfMoreIn: "Meer uit deze categorie",
  tfMoreEmptyTitle: "Nog geen andere tools",
  tfMoreEmptyDesc: "Dit is momenteel de enige tool in deze categorie.",
  badgePro: "PRO",
  badgeExp: "EXPERIMENTEEL",
  tfToday: "Vandaag",
  tfGenBlockH: "Concept",
  tfGenBlockP:
    "Een gestructureerd eerste concept op basis van uw context — klaar om te beoordelen, bewerken en eigen te maken.",
  tfCtxBlockH: "Uw context",
  tfClientLabel: "Cliëntreferentie",
  tfClientPh: "bijv. Cliënt M. (alleen initialen)",
  tfModalityLabel: "Therapeutische modaliteit",
  tfModalityOpts: ["CGT", "ACT", "DGT", "Psychodynamisch", "Persoonsgericht", "Integratief"],
  tfFocusLabel: "Belangrijkste aandachtspunten",
  tfFocusPh: "Thema's, doelen of wat het concept moet benadrukken…",
  tfFormatLabel: "Uitvoerformaat",
  tfFormatOpts: ["Opsomming", "Lopende tekst", "SOAP-notitie", "Brief"],
  tfLengthLabel: "Lengte",
  tfLengthOpts: ["Kort", "Standaard", "Uitgebreid"],
  tfReadingLabel: "Leesniveau",
  tfReadingOpts: ["Eenvoudige taal", "Standaard", "Klinisch"],
  tfRiskLabel: "Risicoscreening opnemen",
  tfMeasuresLabel: "Uitkomstmaten vermelden",
  tfAnonLabel: "Cliëntgegevens anonimiseren",
  ctxTitle: "Projectcontext",
  ctxNotesLabel: "Notities",
  ctxNotesPh:
    "Voeg uw eigen context toe — achtergrond, doelen, risicosignalen, voorkeuren, alles wat u de volgende keer wilt onthouden…",
  ctxPrivate: "Privé · opgeslagen op dit apparaat",
  ctxSummaryLabel: "Uit uw concepten",
  ctxAuto: "Auto",
  ctxUpdate: "Bijwerken",
  ctxSavedTag: "Opgeslagen",
  ctxToolsUsed: "Gebruikte tools",
  ctxNotesHelp:
    "Wordt gebruikt wanneer u dit project selecteert in een AI-tool, om de gegenereerde concepten te verrijken.",
  ctxSummaryTpl: "Tot nu toe {n} {drafts}, meest recent op {date}.",
  ctxDraftWord: "concept",
  ctxDraftsWord: "concepten",
  ctxCovers: "Omvat",
  ctxEmptySummary:
    "Nog geen concepten — hier verschijnt een samenvatting zodra u werk genereert in dit project.",
  authTitle: "Beveilig uw account",
  authSubtitle: "Met een account houdt u uw app-gegevens en PRO-status veilig.",
  authGoogle: "Doorgaan met Google",
  authApple: "Doorgaan met Apple",
  authEmail: "Doorgaan met e-mail",
  authGuest: "Doorgaan als gast",
  authClose: "Sluiten",
  authBack: "Terug",
  authOr: "of",
  authEmailLabel: "E-mail",
  authEmailPh: "u@voorbeeld.nl",
  authPasswordLabel: "Wachtwoord",
  authPasswordPh: "••••••••",
  authPasswordHint: "Minimaal 8 tekens.",
  authRestoreAccess: "Wachtwoord vergeten?",
  authRestoreTitle: "Toegang herstellen",
  authRestoreSubtitle: "Voer je e-mail in — we sturen een nieuw wachtwoord.",
  authRestoreSubmit: "Nieuw wachtwoord sturen",
  authRestoreSuccess: "Als er een account bestaat voor dit e-mailadres, is er een nieuw wachtwoord onderweg.",
  authSignInTitle: "Inloggen",
  authSignUpTitle: "Maak uw account aan",
  authSubmitSignIn: "Inloggen",
  authSubmitSignUp: "Account aanmaken",
  authSubmitting: "Een moment…",
  authSwitchToSignUp: "Nog geen account? Registreren",
  authSwitchToSignIn: "Al een account? Inloggen",
  authErrorFallback: "Er is iets misgegaan. Probeer het opnieuw.",
  authTooManyAttempts: "Te veel pogingen. Wacht even en probeer het opnieuw.",
  authEmailRequired: "E-mail is verplicht.",
  authEmailInvalid: "Voer een geldig e-mailadres in.",
  authPasswordRequired: "Wachtwoord is verplicht.",
  authPasswordTooShort: "Wachtwoord moet minstens 8 tekens bevatten.",
  authInvalidCredentials: "Ongeldige inloggegevens.",
  authEmailTaken: "Dit e-mailadres is al geregistreerd.",
  formCheckFields: "Controleer de gemarkeerde velden.",
  validationFieldRequired: "{field} is verplicht.",
  validationFieldEmail: "Voer een geldig {field} in.",
  validationFieldTaken: "Dit {field} is al in gebruik.",
  validationFieldMin: "{field} moet minstens {min} tekens bevatten.",
  validationFieldMax: "{field} mag maximaal {max} tekens bevatten.",
  fieldLabelEmail: "E-mail",
  fieldLabelPassword: "Wachtwoord",
  fieldLabelName: "Naam",
  fieldLabelDescription: "Beschrijving",
  fieldLabelNotes: "Notities",
  fieldLabelTitle: "Titel",
  fieldLabelFirstName: "Voornaam",
  fieldLabelLastName: "Achternaam",
  cpNameRequired: "Projectnaam is verplicht.",
  hiTitleRequired: "Titel is verplicht.",
  tfRunError: "Genereren mislukt. Probeer het opnieuw.",
  tfUsageLimitGuest: "Gastlimiet bereikt. Log in of maak een account om door te gaan.",
  tfUsageLimitFree: "Gratis limiet bereikt. Upgrade naar Pro voor meer generaties.",
  tfNeedProject: "Maak eerst een project om deze generatie op te slaan.",
  guestDraftsName: "Concepten",
  defaultProjectName: "Standaard",
  guestSyncFailed:
    "Je gastprojecten konden niet worden geïmporteerd. Je lokale concepten zijn nog opgeslagen — probeer het opnieuw.",
  guestSyncNotClaimable:
    "Sommige gastgeneraties konden niet worden overgenomen (ontbreken of al van iemand anders). Je lokale concepten zijn nog opgeslagen.",
  guestSyncLimitsExceeded:
    "Te veel gastprojecten of generaties om in één keer te importeren. Verwijder enkele concepten en probeer het opnieuw.",
  tfGenerating: "Bezig met maken…",
  tfGeneratingHint: "Duurt meestal een paar seconden.",
  tfCancelRequest: "Verzoek annuleren",
  authGateTitle: "Log in om door te gaan",
  authGateSubtitle: "Maak een gratis account aan om uw projecten en certificaten op één plek te bewaren.",
  authGateCta: "Inloggen of account aanmaken",
  retry: "Opnieuw proberen",
  proModalTitle: "Creëer zonder limieten",
  proModalDesc:
    "Unieke psychotherapeutische content en ideeën, op maat voor u met ChatGPT en 30 andere AI-engines — zonder complexe prompts.",
  proModalPriceWas: "$49",
  proModalPriceNow: "$19,99",
  proModalOffer: "Onbeperkte verzoeken · speciale aanbieding van vandaag",
  proModalRating: "4,9 · 3.920 reviews",
  proModalQuote: "‘Beste app voor psychotherapeuten in 2025.’ — Open Academy",
  proModalTrusted: "Vertrouwd door",
  proModalTrustedLabel: "specialisten",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Meer dan 50 creatieve modules",
      desc: "Maak alle content die uw werk verlicht.",
    },
    {
      emoji: "🐣",
      title: "Geen technische vaardigheden nodig",
      desc: "Gebruik eenvoudige formulieren in plaats van complexe prompts.",
    },
    {
      emoji: "🚩",
      title: "Regionale aanpassingen",
      desc: "Pas content aan uw lokale standaarden aan.",
    },
    {
      emoji: "🧠",
      title: "Gepersonaliseerde resultaten",
      desc: "Content op basis van patiëntgeschiedenis en uw stijl.",
    },
    {
      emoji: "⌛",
      title: "Geen wachttijd",
      desc: "Genereer content en ideeën snel, zonder wachten.",
    },
    {
      emoji: "🚀",
      title: "Onbeperkt quotum",
      desc: "Maak zoveel content als u nodig heeft.",
    },
  ],
  proModalCta: "Volledige toegang krijgen",
  proModalClose: "Sluiten",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow en Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Tijdbesparend",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Aidemia is een echte gamechanger voor therapeuten die hun werk willen stroomlijnen. Tijd is altijd schaars, en Aidemia begrijpt dat. De intuïtieve interface en AI-tools verkorten voorbereiding en analyse sterk.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Betaalbaar",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Aidemia bespaart niet alleen tijd, maar ook geld. Door de functies van talloze AI-tools in één platform te bundelen, zijn dure meervoudige abonnementen overbodig. Als psychotherapeut kan ik het scala aan mogelijkheden en de dagelijkse waarde nauwelijks onderschatten.",
    },
  ],
  proModalDataProtected: "Uw gegevens zijn beschermd",
  proModalTermsShort: "Voorwaarden",
  proModalGuarantee: "14 dagen geld-terug-garantie",
  proModalLegalNote:
    "De korting is toegepast. Uw pass geeft toegang tot alle soorten content, inclusief toekomstige functies zonder prijsverhoging. U kunt op elk moment opzeggen of elke vier weken automatisch verlengen. Een deel van uw bijdrage gaat naar kinderhulpverlening.",
};

const el: TranslationStrings = {
  navProjects: "Έργα",
  navHistory: "Ιστορικό",
  navTools: "Εργαλεία",
  navChat: "Συνομιλία",
  navCerts: "Πιστοποιητικά",
  navAccount: "Λογαριασμός",
  toolsTitle: "Εργαλεία",
  toolsDesc:
    "Εργαλεία τεχνητής νοημοσύνης για κάθε στάδιο της κλινικής εργασίας — από την παραπομπή έως την παρακολούθηση. Κάθε εργαλείο παράγει ένα προσχέδιο που εσείς ελέγχετε και κατέχετε.",
  toolsCountLabel: "εργαλεία",
  all: "Όλα",
  projectsTitle: "Έργα",
  projectsDesc: "Όλα όσα έχετε δημιουργήσει, οργανωμένα ανά περίπτωση πελάτη.",
  projTab: "Έργα",
  histTab: "Ιστορικό",
  historyTitle: "Ιστορικό",
  historyDesc: "Όλα τα προσχέδια που έχετε δημιουργήσει — σε όλα τα έργα, πρώτα τα νεότερα.",
  createProject: "Δημιουργία έργου",
  editProject: "Επεξεργασία έργου",
  emptyProjectsTitle: "Δεν υπάρχουν ακόμη έργα",
  emptyProjectsDesc:
    "Ξεκινήστε ένα έργο για έναν πελάτη ή μια υπόθεση. Τα προσχέδια που θα δημιουργήσετε θα ζουν εδώ, οργανωμένα και εύκολα να βρεθούν.",
  projectsProCtaTitle: "Οργανώστε τη δουλειά σας σε έργα",
  projectsProCtaDesc:
    "Αναβαθμίστε σε Pro για να δημιουργείτε προσαρμοσμένα έργα ανά πελάτη ή υπόθεση — κρατήστε κάθε προσχέδιο δομημένο και εύκολο να βρεθεί.",
  projectsProCtaBenefits: [
    "Δημιουργήστε απεριόριστα προσαρμοσμένα έργα",
    "Χωρίστε τη δουλειά ανά πελάτη ή υπόθεση",
    "Κρατήστε κάθε προσχέδιο στη σωστή θέση",
  ],
  emptyHistoryTitle: "Δεν υπάρχει ακόμη ιστορικό",
  emptyHistoryDesc:
    "Όταν δημιουργήσετε ένα προσχέδιο, θα εμφανιστεί εδώ — πρώτα τα νεότερα.",
  emptyHistoryCta: "Περιήγηση εργαλείων",
  notFoundTitle: "Αυτή η σελίδα δεν βρέθηκε",
  notFoundDesc:
    "Το εργαλείο, το έργο ή το προσχέδιο που αναζητάτε δεν υπάρχει ή έχει μετακινηθεί.",
  notFoundCtaTools: "Επιστροφή στα Εργαλεία",
  notFoundCtaProjects: "Μετάβαση στα Έργα",
  paginationPrevious: "Προηγούμενο",
  paginationNext: "Επόμενο",
  paginationPageOf: "Σελίδα {page} από {totalPages}",
  paginationAria: "Σελιδοποίηση έργων",
  chatTitle: "Βοηθός",
  chatDesc: "Σκεφτείτε φωναχτά με έναν βοηθό που γνωρίζει τα εργαλεία σας.",
  chatPlaceholder: "Ρωτήστε οτιδήποτε…",
  chatSend: "Αποστολή",
  certsTitle: "Πιστοποιητικό μέλους",
  certsDesc:
    "Πλήρης συνδρομή στη World Innovative Teaching Association με εκτυπώσιμο, επαληθεύσιμο πιστοποιητικό — ξεχωριστά από τη συνδρομή Aidemia Pro.",
  certsObserverNote:
    "Είστε παρατηρητής μέλος της World Innovative Teaching Association. Αναβαθμίστε σε πλήρη συνδρομή για να λάβετε το πιστοποιητικό σας.",
  certsRepurchaseNote:
    "Η προηγούμενη πρόσβαση μέλους σας δεν είναι πλέον ενεργή. Μπορείτε να αγοράσετε ξανά το πιστοποιητικό.",
  certsWhyTitle: "Γιατί να αποκτήσετε το πιστοποιητικό",
  certsBenefits: [
    "Επίσημη επιβεβαίωση επαγγελματικής συνδρομής στη WITA",
    "Μοναδικός σειριακός αριθμός με διαδικτυακή επαλήθευση",
    "Οριζόντιο πιστοποιητικό έτοιμο για εκτύπωση ή αποστολή",
    "Αναγνωρισμένο προσόν για σχολεία και ιδρύματα",
    "Καταχώριση στο μητρώο μελών της ένωσης",
    "Μηνιαία συνδρομή σε περιορισμένη τιμή",
  ],
  certsPlanLabel: "Μηνιαία συνδρομή",
  certsPriceWas: "100,00 $",
  certsPriceNow: "49,99 $",
  certsNotInPro:
    "Δεν περιλαμβάνεται στην Aidemia Pro. Πληρώνετε μόνο για το πιστοποιητικό μέλους και την πλήρη συνδρομή στη WITA.",
  certsCta: "Εγγραφείτε και λάβετε το πιστοποιητικό",
  certsSecurePay: "Ασφαλής πληρωμή στη σελίδα πληρωμών μας.",
  certsSpecimen: "Δείγμα",
  certsSpecimenCaption:
    "Δείγμα — όχι πραγματικά εκδοθέν πιστοποιητικό. Προεπισκόπηση του επίσημου σχεδιασμού WITA.",
  certsSpecimenAlt: "Δείγμα — προεπισκόπηση πιστοποιητικού μέλους",
  certsPurchasedNote:
    "Το πιστοποιητικό πλήρους μέλους σας για τη World Innovative Teaching Association είναι ενεργό.",
  certsSerialNumber: "Σειριακός αριθμός εγγράφου: {serial}",
  certsIncludesTitle: "Η ιδιότητα μέλους σας περιλαμβάνει",
  certsDownload: "Λήψη πιστοποιητικού",
  certsDownloading: "Λήψη…",
  certsDownloadError: "Δεν ήταν δυνατή η λήψη του πιστοποιητικού. Δοκιμάστε ξανά.",
  certsPreviewCaption:
    "Το εκδοθέν πιστοποιητικό χρησιμοποιεί το όνομα προφίλ και τον σειριακό αριθμό.",
  certsPreviewAlt: "Προεπισκόπηση πιστοποιητικού μέλους",
  certsProfileIncompleteBoth:
    "Το όνομα και το επώνυμο δεν έχουν συμπληρωθεί. Το πιστοποιητικό χρησιμοποιεί το όνομα από το προφίλ σας.",
  certsProfileIncompleteFirst:
    "Το όνομα δεν έχει συμπληρωθεί. Το πιστοποιητικό χρησιμοποιεί το όνομα από το προφίλ σας.",
  certsProfileIncompleteLast:
    "Το επώνυμο δεν έχει συμπληρωθεί. Το πιστοποιητικό χρησιμοποιεί το όνομα από το προφίλ σας.",
  certsProfileIncompleteLink: "Συμπληρώστε το προφίλ",
  certsVerifyEyebrow: "Μέλος WITA",
  certsVerifyTitle: "Επαλήθευση πιστοποιητικού",
  certsVerifyDesc:
    "Εισαγάγετε τον σειριακό αριθμό του εγγράφου για να επιβεβαιώσετε ότι ένα πιστοποιητικό μέλους είναι γνήσιο και επί του παρόντος έγκυρο.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "Η επαλήθευση επιβεβαιώνει μόνο την εγκυρότητα — δεν εμφανίζονται προσωπικά στοιχεία.",
  certsVerifyLabel: "Σειριακός αριθμός εγγράφου",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Μορφή: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Εισαγάγετε σειριακό αριθμό πιστοποιητικού.",
  certsVerifyFormatError: "Εισαγάγετε έναν αριθμό όπως CERT-2026-000001.",
  certsVerifyCta: "Επαλήθευση πιστοποιητικού",
  certsVerifyChecking: "Έλεγχος…",
  certsVerifyValidTitle: "Το πιστοποιητικό είναι έγκυρο",
  certsVerifyValidBody:
    "Αυτό το πιστοποιητικό μέλους εκδόθηκε στις {date} και είναι επί του παρόντος ενεργό.",
  certsVerifyInvalidTitle: "Το πιστοποιητικό δεν επαληθεύτηκε",
  certsVerifyInvalidBody:
    "Δεν μπορέσαμε να επιβεβαιώσουμε αυτόν τον αριθμό. Μπορεί να είναι λάθος, να έχει λήξει ή να μην είναι πλέον ενεργός.",
  certsVerifyAgain: "Έλεγχος άλλου αριθμού",
  certsVerifyError:
    "Δεν ήταν δυνατή η επικοινωνία με την υπηρεσία επαλήθευσης. Ελέγξτε τη σύνδεση και δοκιμάστε ξανά.",
  certsVerifyBackToCerts: "Επιστροφή στο πιστοποιητικό μέλους",
  certsVerifyOpenLink: "Επαλήθευση αριθμού πιστοποιητικού",
  accountTitle: "Λογαριασμός",
  accountEmailVerified: "Το email επιβεβαιώθηκε",
  accountEmailUnverified: "Το email δεν έχει επιβεβαιωθεί",
  accountResendConfirmation: "Επανάληψη αποστολής email επιβεβαίωσης",
  accountResendConfirmationSending: "Αποστολή…",
  accountResendConfirmationSent: "Το email επιβεβαίωσης στάλθηκε. Ελέγξτε τα εισερχόμενά σας.",
  emailConfirmConfirming: "Επιβεβαίωση email…",
  emailConfirmSuccessTitle: "Το email επιβεβαιώθηκε",
  emailConfirmSuccessBody:
    "Η διεύθυνση email σας επαληθεύτηκε. Μπορείτε να συνεχίσετε να χρησιμοποιείτε την Aidemia.",
  emailConfirmInvalidTitle: "Μη έγκυρος σύνδεσμος επιβεβαίωσης",
  emailConfirmInvalidBody:
    "Αυτός ο σύνδεσμος επιβεβαίωσης είναι μη έγκυρος ή έχει λήξει. Ζητήστε νέο email επιβεβαίωσης και δοκιμάστε ξανά.",
  emailConfirmMissingTitle: "Λείπει ο σύνδεσμος επιβεβαίωσης",
  emailConfirmMissingBody:
    "Ανοίξτε τον σύνδεσμο επιβεβαίωσης από το email σας για να επαληθεύσετε τη διεύθυνσή σας.",
  emailConfirmErrorTitle: "Αδυναμία επιβεβαίωσης email",
  emailConfirmGoToAccount: "Μετάβαση στον λογαριασμό",
  emailConfirmGoHome: "Επιστροφή στην αρχική",
  loginLinkConsuming: "Γίνεται σύνδεση…",
  loginLinkMissingTitle: "Μη έγκυρος σύνδεσμος",
  loginLinkMissingBody:
    "Αυτός ο σύνδεσμος σύνδεσης δεν έχει διακριτικό. Ανοίξτε ξανά τον σύνδεσμο από το email ή την επιβεβαίωση πληρωμής.",
  loginLinkInvalidTitle: "Ο σύνδεσμος έληξε ή χρησιμοποιήθηκε",
  loginLinkInvalidBody:
    "Αυτός ο σύνδεσμος σύνδεσης έχει λήξει ή έχει ήδη χρησιμοποιηθεί. Ζητήστε νέο για να συνεχίσετε.",
  loginLinkErrorTitle: "Αδυναμία σύνδεσης",
  loginLinkGoHome: "Επιστροφή στην αρχική",
  projectLandingEyebrow: "Το έργο σας",
  projectLandingBody: "Έχετε συνδεθεί. Ανοίξτε τον χώρο εργασίας για να συνεχίσετε.",
  projectLandingMissingTitle: "Το έργο δεν βρέθηκε",
  projectLandingMissingBody: "Αυτό το έργο δεν υπάρχει ή δεν έχετε πρόσβαση σε αυτό.",
  projectLandingErrorTitle: "Αδυναμία φόρτωσης έργου",
  projectLandingGoHome: "Επιστροφή στην αρχική",
  projectLandingOpenWorkspace: "Άνοιγμα χώρου εργασίας",
  paymentCheckoutSuccessTitle: "Η πληρωμή ολοκληρώθηκε",
  paymentCheckoutSuccessBody:
    "Ευχαριστούμε. Η πληρωμή σας ολοκληρώθηκε. Η πρόσβαση Pro θα εμφανιστεί σύντομα στον λογαριασμό σας.",
  paymentCheckoutSuccessCompleting: "Επιβεβαίωση πληρωμής…",
  paymentCheckoutSuccessGuestBody:
    "Ευχαριστούμε. Ο λογαριασμός σας είναι έτοιμος — σας στείλαμε κωδικό με email. Έχετε συνδεθεί και η πρόσβαση Pro είναι ενεργή.",
  paymentCheckoutSuccessExistingAccountBody:
    "Ευχαριστούμε. Η πληρωμή σας συνδέθηκε με τον υπάρχοντα λογαριασμό σας. Συνδεθείτε με τον συνηθισμένο κωδικό για να συνεχίσετε.",
  paymentCheckoutSuccessSignIn: "Σύνδεση",
  paymentCheckoutSuccessGoToAccount: "Μετάβαση στον λογαριασμό",
  paymentCheckoutSuccessGoToTools: "Επιστροφή στα εργαλεία",
  paymentCheckoutSuccessGoToCertificates: "Επιστροφή στα πιστοποιητικά",
  paymentCheckoutCancelTitle: "Η πληρωμή ακυρώθηκε",
  paymentCheckoutCancelBody:
    "Το checkout ακυρώθηκε. Δεν έγινε χρέωση. Μπορείτε να δοκιμάσετε ξανά όποτε είστε έτοιμοι.",
  paymentCheckoutCancelRetry: "Δοκιμάστε ξανά",
  paymentCheckoutCancelGoToTools: "Επιστροφή στα εργαλεία",
  paymentCheckoutCancelGoToCertificates: "Επιστροφή στα πιστοποιητικά",
  accountSubscription: "Συνδρομή",
  accountDataPrivacy: "Δεδομένα και απόρρητο",
  accountPlanFree: "Δωρεάν",
  accountProfileHeading: "Το όνομά σας",
  accountFirstName: "Όνομα",
  accountLastName: "Επώνυμο",
  accountFirstNamePh: "Όνομα",
  accountLastNamePh: "Επώνυμο",
  accountSaveProfile: "Αποθήκευση",
  accountProfileSaved: "Το προφίλ ενημερώθηκε.",
  menuRecents: "Πρόσφατα",
  menuRecentsEmpty: "Δεν υπάρχουν ακόμη πρόσφατες δημιουργίες",
  menuRecentsShowMore: "Εμφάνιση περισσότερων",
  menuAccountSettings: "Ρυθμίσεις λογαριασμού",
  menuAbout: "Σχετικά με την Aidemia",
  menuPersonalize: "Εξατομίκευση",
  menuPrivacy: "Πολιτική απορρήτου",
  menuTerms: "Όροι & Προϋποθέσεις",
  menuContact: "Επικοινωνήστε μαζί μας",
  signIn: "Σύνδεση",
  signOut: "Αποσύνδεση",
  itemDownload: "Λήψη",
  itemEdit: "Επεξεργασία",
  itemBodyLabel: "Περιεχόμενο",
  itemGeneratedWith: "Δημιουργήθηκε με",
  itemProject: "Έργο",
  itemGenerateNew: "Δημιουργία νέου",
  itemDraftNote: "Προσχέδιο που δημιουργήθηκε από την Aidemia — ελέγξτε και επεξεργαστείτε πριν τη χρήση.",
  audioPlay: "Αναπαραγωγή",
  audioPause: "Παύση",
  audioMute: "Σίγαση",
  audioUnmute: "Κατάργηση σίγασης",
  audioSeek: "Αναζήτηση",
  audioVolume: "Ένταση",
  audioError: "Δεν ήταν δυνατή η φόρτωση αυτού του ήχου. Δοκιμάστε να κατεβάσετε το αρχείο.",
  audioLabel: "Ήχος",
  cpSubtitle: "Ένας χώρος για την εργασία σε μία περίπτωση πελάτη.",
  cpNameLabel: "Όνομα έργου",
  cpNamePh: "π.χ. Πελάτης Σ. — Κρίσεις πανικού",
  cpDescLabel: "Περιγραφή",
  cpOptional: "προαιρετικό",
  cpRequired: "υποχρεωτικό",
  cpDescPh: "Αίτημα παρουσίασης, προσέγγιση, στάδιο φροντίδας…",
  cpAsideTitle: "Σχετικά με τα έργα",
  cpAsideText:
    "Ένα έργο συγκεντρώνει όλη την εργασία για μία περίπτωση πελάτη σε ένα μέρος — σημειώσεις, σχέδια και επιστολές — για εύκολη εύρεση αργότερα.",
  cpAsideHint: "Συμβουλή: χρησιμοποιήστε αρχικά ή έναν σύντομο κωδικό αντί για το πλήρες όνομα του πελάτη.",
  cpCancel: "Ακύρωση",
  cpCreate: "Δημιουργία",
  cpSave: "Αποθήκευση",
  cpEmptyProjTitle: "Αυτό το έργο είναι ακόμη κενό",
  cpEmptyProj: "Δεν υπάρχει ακόμη τίποτα — τα προσχέδια που θα δημιουργήσετε σε αυτό το έργο θα εμφανίζονται εδώ.",
  tfTitleLabel: "Τίτλος προσχεδίου",
  tfTitlePh: "π.χ. Προετοιμασία συνεδρίας — εβδομάδα 7",
  tfProjectLabel: "Αποθήκευση στο έργο",
  tfContextLabel: "Κλινικό πλαίσιο",
  tfContextPh: "Επικολλήστε σημειώσεις συνεδρίας, μια επιστολή παραπομπής ή λίγες γραμμές για τον πελάτη…",
  tfToneLabel: "Ύφος",
  tfToneOpts: ["Θερμό", "Ουδέτερο", "Κλινικό"],
  tfNextLabel: "Πρόταση επόμενων βημάτων",
  tfDeepLabel: "Εις βάθος κλινική ανάλυση",
  tfReset: "Επαναφορά",
  tfGenerate: "Δημιουργία",
  tfMoreIn: "Περισσότερα από αυτή την κατηγορία",
  tfMoreEmptyTitle: "Δεν υπάρχουν άλλα εργαλεία",
  tfMoreEmptyDesc: "Αυτή τη στιγμή είναι το μόνο εργαλείο σε αυτή την κατηγορία.",
  badgePro: "PRO",
  badgeExp: "ΠΕΙΡΑΜΑΤΙΚΟ",
  tfToday: "Σήμερα",
  tfGenBlockH: "Προσχέδιο",
  tfGenBlockP:
    "Ένα δομημένο πρώτο προσχέδιο βασισμένο στο πλαίσιο σας — έτοιμο για έλεγχο, επεξεργασία και εξατομίκευση.",
  tfCtxBlockH: "Το πλαίσιο σας",
  tfClientLabel: "Αναφορά πελάτη",
  tfClientPh: "π.χ. Πελάτης Μ. (μόνο αρχικά)",
  tfModalityLabel: "Θεραπευτική προσέγγιση",
  tfModalityOpts: ["ΓΣΘ", "ACT", "DBT", "Ψυχοδυναμική", "Ανθρωποκεντρική", "Ολιστική"],
  tfFocusLabel: "Κύριοι τομείς εστίασης",
  tfFocusPh: "Θέματα, στόχοι ή οτιδήποτε πρέπει να τονίσει το προσχέδιο…",
  tfFormatLabel: "Μορφή εξόδου",
  tfFormatOpts: ["Λίστα με κουκκίδες", "Πεζός λόγος", "Σημείωση SOAP", "Επιστολή"],
  tfLengthLabel: "Μήκος",
  tfLengthOpts: ["Σύντομο", "Τυπικό", "Αναλυτικό"],
  tfReadingLabel: "Επίπεδο ανάγνωσης",
  tfReadingOpts: ["Απλή γλώσσα", "Τυπικό", "Κλινικό"],
  tfRiskLabel: "Συμπερίληψη ελέγχου κινδύνου",
  tfMeasuresLabel: "Αναφορά εργαλείων έκβασης",
  tfAnonLabel: "Ανωνυμοποίηση στοιχείων πελάτη",
  ctxTitle: "Πλαίσιο έργου",
  ctxNotesLabel: "Σημειώσεις",
  ctxNotesPh:
    "Προσθέστε το δικό σας πλαίσιο — ιστορικό, στόχους, ενδείξεις κινδύνου, προτιμήσεις, οτιδήποτε θέλετε να θυμάστε την επόμενη φορά…",
  ctxPrivate: "Ιδιωτικό · αποθηκεύεται σε αυτή τη συσκευή",
  ctxSummaryLabel: "Από τα προσχέδια σας",
  ctxAuto: "Αυτόματο",
  ctxUpdate: "Ενημέρωση",
  ctxSavedTag: "Αποθηκεύτηκε",
  ctxToolsUsed: "Εργαλεία που χρησιμοποιήθηκαν",
  ctxNotesHelp:
    "Χρησιμοποιείται κάθε φορά που επιλέγετε αυτό το έργο σε ένα Εργαλείο ΤΝ, για να ενισχύσει τα προσχέδια που δημιουργεί.",
  ctxSummaryTpl: "{n} {drafts} μέχρι στιγμής, το πιο πρόσφατο στις {date}.",
  ctxDraftWord: "προσχέδιο",
  ctxDraftsWord: "προσχέδια",
  ctxCovers: "Καλύπτει",
  ctxEmptySummary:
    "Δεν υπάρχουν ακόμη προσχέδια — μια σύνοψη θα εμφανιστεί εδώ καθώς δημιουργείτε εργασία σε αυτό το έργο.",
  authTitle: "Ασφαλίστε τον λογαριασμό σας",
  authSubtitle: "Η ύπαρξη λογαριασμού σας βοηθά να διατηρείτε ασφαλή τα δεδομένα της εφαρμογής και την κατάσταση PRO.",
  authGoogle: "Συνέχεια με Google",
  authApple: "Συνέχεια με Apple",
  authEmail: "Συνέχεια με email",
  authGuest: "Συνέχεια ως επισκέπτης",
  authClose: "Κλείσιμο",
  authBack: "Πίσω",
  authOr: "ή",
  authEmailLabel: "Email",
  authEmailPh: "you@example.com",
  authPasswordLabel: "Κωδικός πρόσβασης",
  authPasswordPh: "••••••••",
  authPasswordHint: "Τουλάχιστον 8 χαρακτήρες.",
  authRestoreAccess: "Ξεχάσατε τον κωδικό;",
  authRestoreTitle: "Επαναφορά πρόσβασης",
  authRestoreSubtitle: "Εισαγάγετε το email σας και θα σας στείλουμε νέο κωδικό.",
  authRestoreSubmit: "Αποστολή νέου κωδικού",
  authRestoreSuccess: "Αν υπάρχει λογαριασμός με αυτό το email, ένας νέος κωδικός είναι καθ’ οδόν.",
  authSignInTitle: "Σύνδεση",
  authSignUpTitle: "Δημιουργήστε τον λογαριασμό σας",
  authSubmitSignIn: "Σύνδεση",
  authSubmitSignUp: "Δημιουργία λογαριασμού",
  authSubmitting: "Παρακαλώ περιμένετε…",
  authSwitchToSignUp: "Δεν έχετε λογαριασμό; Εγγραφείτε",
  authSwitchToSignIn: "Έχετε ήδη λογαριασμό; Συνδεθείτε",
  authErrorFallback: "Κάτι πήγε στραβά. Παρακαλώ δοκιμάστε ξανά.",
  authTooManyAttempts: "Πάρα πολλές προσπάθειες. Περιμένετε λίγο και δοκιμάστε ξανά.",
  authEmailRequired: "Απαιτείται email.",
  authEmailInvalid: "Εισαγάγετε έγκυρο email.",
  authPasswordRequired: "Απαιτείται κωδικός.",
  authPasswordTooShort: "Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.",
  authInvalidCredentials: "Μη έγκυρα στοιχεία σύνδεσης.",
  authEmailTaken: "Αυτό το email είναι ήδη καταχωρημένο.",
  formCheckFields: "Ελέγξτε τα επισημασμένα πεδία.",
  validationFieldRequired: "Το πεδίο {field} είναι υποχρεωτικό.",
  validationFieldEmail: "Εισαγάγετε έγκυρο {field}.",
  validationFieldTaken: "Αυτό το {field} χρησιμοποιείται ήδη.",
  validationFieldMin: "Το {field} πρέπει να έχει τουλάχιστον {min} χαρακτήρες.",
  validationFieldMax: "Το {field} πρέπει να έχει το πολύ {max} χαρακτήρες.",
  fieldLabelEmail: "Email",
  fieldLabelPassword: "Κωδικός",
  fieldLabelName: "Όνομα",
  fieldLabelDescription: "Περιγραφή",
  fieldLabelNotes: "Σημειώσεις",
  fieldLabelTitle: "Τίτλος",
  fieldLabelFirstName: "Όνομα",
  fieldLabelLastName: "Επώνυμο",
  cpNameRequired: "Το όνομα έργου είναι υποχρεωτικό.",
  hiTitleRequired: "Ο τίτλος είναι υποχρεωτικός.",
  tfRunError: "Αποτυχία δημιουργίας. Δοκιμάστε ξανά.",
  tfUsageLimitGuest: "Το όριο επισκέπτη εξαντλήθηκε. Συνδεθείτε ή δημιουργήστε λογαριασμό.",
  tfUsageLimitFree: "Το δωρεάν όριο εξαντλήθηκε. Αναβαθμίστε σε Pro για περισσότερες δημιουργίες.",
  tfNeedProject: "Δημιουργήστε πρώτα ένα έργο για να αποθηκεύσετε αυτή τη δημιουργία.",
  guestDraftsName: "Προσχέδια",
  defaultProjectName: "Προεπιλογή",
  guestSyncFailed:
    "Δεν ήταν δυνατή η εισαγωγή των έργων επισκέπτη. Τα τοπικά προσχέδια είναι ακόμα αποθηκευμένα — δοκιμάστε ξανά.",
  guestSyncNotClaimable:
    "Ορισμένες δημιουργίες επισκέπτη δεν μπορούν να μεταφερθούν (λείπουν ή ανήκουν ήδη σε άλλον). Τα τοπικά προσχέδια είναι ακόμα αποθηκευμένα.",
  guestSyncLimitsExceeded:
    "Υπερβολικά πολλά έργα ή δημιουργίες επισκέπτη για μία εισαγωγή. Αφαιρέστε κάποια προσχέδια και δοκιμάστε ξανά.",
  tfGenerating: "Δημιουργία…",
  tfGeneratingHint: "Συνήθως διαρκεί λίγα δευτερόλεπτα.",
  tfCancelRequest: "Ακύρωση αιτήματος",
  authGateTitle: "Συνδεθείτε για να συνεχίσετε",
  authGateSubtitle: "Δημιουργήστε έναν δωρεάν λογαριασμό για να διατηρείτε τα έργα και τα πιστοποιητικά σας σε ένα μέρος.",
  authGateCta: "Σύνδεση ή δημιουργία λογαριασμού",
  retry: "Δοκιμάστε ξανά",
  proModalTitle: "Δημιουργήστε χωρίς όρια",
  proModalDesc:
    "Μοναδικό ψυχοθεραπευτικό περιεχόμενο και ιδέες για εσάς με το ChatGPT και 30 άλλες μηχανές AI — χωρίς σύνθετα prompts.",
  proModalPriceWas: "$49",
  proModalPriceNow: "$19,99",
  proModalOffer: "Απεριόριστα αιτήματα · ειδική προσφορά σήμερα",
  proModalRating: "4,9 · 3.920 κριτικές",
  proModalQuote: "«Η καλύτερη εφαρμογή για ψυχοθεραπευτές το 2025.» — Open Academy",
  proModalTrusted: "Το εμπιστεύονται",
  proModalTrustedLabel: "ειδικοί",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Πάνω από 50 δημιουργικές ενότητες",
      desc: "Δημιουργήστε όλο το περιεχόμενο που διευκολύνει τη δουλειά σας.",
    },
    {
      emoji: "🐣",
      title: "Χωρίς τεχνικές δεξιότητες",
      desc: "Απλές φόρμες αντί για χειροκίνητα σύνθετα prompts.",
    },
    {
      emoji: "🚩",
      title: "Τοπικές προσαρμογές",
      desc: "Προσαρμόστε το περιεχόμενο στα τοπικά πρότυπα.",
    },
    {
      emoji: "🧠",
      title: "Εξατομικευμένα αποτελέσματα",
      desc: "Περιεχόμενο βάσει ιστορικού ασθενούς και του στυλ σας.",
    },
    {
      emoji: "⌛",
      title: "Χωρίς αναμονή",
      desc: "Δημιουργήστε περιεχόμενο και ιδέες γρήγορα, χωρίς αναμονή.",
    },
    {
      emoji: "🚀",
      title: "Απεριόριστο όριο",
      desc: "Δημιουργήστε όσο περιεχόμενο χρειάζεστε.",
    },
  ],
  proModalCta: "Αποκτήστε πλήρη πρόσβαση",
  proModalClose: "Κλείσιμο",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow και Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Εξοικονόμηση χρόνου",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "Η Aidemia αλλάζει τα δεδομένα για θεραπευτές που θέλουν να απλοποιήσουν τη δουλειά τους. Ο χρόνος είναι πάντα πολύτιμος — και η Aidemia το καταλαβαίνει. Το διαισθητικό περιβάλλον και τα εργαλεία AI μειώνουν αισθητά την προετοιμασία και την ανάλυση.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Οικονομικό",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "Η Aidemia δεν εξοικονομεί μόνο χρόνο, αλλά και χρήματα. Συγκεντρώνοντας τις δυνατότητες πολλών εργαλείων AI σε μία πλατφόρμα, καταργεί την ανάγκη για ακριβές πολλαπλές συνδρομές. Ως ψυχοθεραπευτής, δεν μπορώ να υποτιμήσω το εύρος δυνατοτήτων και την αξία που μου προσφέρει καθημερινά.",
    },
  ],
  proModalDataProtected: "Τα δεδομένα σας προστατεύονται",
  proModalTermsShort: "Όροι",
  proModalGuarantee: "Εγγύηση επιστροφής χρημάτων 14 ημερών",
  proModalLegalNote:
    "Η έκπτωση έχει εφαρμοστεί. Το πάσο σας δίνει πρόσβαση σε όλους τους τύπους περιεχομένου, συμπεριλαμβανομένων μελλοντικών λειτουργιών χωρίς αύξηση κόστους. Μπορείτε να ακυρώσετε οποιαδήποτε στιγμή ή να ανανεώνετε αυτόματα κάθε τέσσερις εβδομάδες. Μέρος της συνεισφοράς σας πηγαίνει σε φιλανθρωπίες για παιδιά.",
};

const pt: TranslationStrings = {
  navProjects: "Projetos",
  navHistory: "Histórico",
  navTools: "Ferramentas",
  navChat: "Chat",
  navCerts: "Certificados",
  navAccount: "Conta",
  toolsTitle: "Ferramentas",
  toolsDesc:
    "Ferramentas de IA para cada etapa do trabalho clínico — da referência ao acompanhamento. Cada uma produz um rascunho que você revisa e assume.",
  toolsCountLabel: "ferramentas",
  all: "Todas",
  projectsTitle: "Projetos",
  projectsDesc: "Tudo o que você gerou, organizado por caso de cliente.",
  projTab: "Projetos",
  histTab: "Histórico",
  historyTitle: "Histórico",
  historyDesc: "Todos os rascunhos que você gerou — em todos os projetos, os mais recentes primeiro.",
  createProject: "Criar projeto",
  editProject: "Editar projeto",
  emptyProjectsTitle: "Ainda sem projetos",
  emptyProjectsDesc:
    "Comece um projeto para um cliente ou caso. Os rascunhos que gerar viverão aqui, organizados e fáceis de encontrar.",
  projectsProCtaTitle: "Organize o seu trabalho em projetos",
  projectsProCtaDesc:
    "Passe para Pro para criar projetos personalizados por cliente ou caso — mantenha cada rascunho estruturado e fácil de encontrar.",
  projectsProCtaBenefits: [
    "Crie projetos personalizados sem limite",
    "Separe o trabalho por cliente ou caso",
    "Mantenha cada rascunho no lugar certo",
  ],
  emptyHistoryTitle: "Ainda sem histórico",
  emptyHistoryDesc:
    "Quando gerar um rascunho, ele aparecerá aqui — os mais recentes primeiro.",
  emptyHistoryCta: "Explorar ferramentas",
  notFoundTitle: "Esta página não foi encontrada",
  notFoundDesc:
    "A ferramenta, o projeto ou o rascunho que procura não existe ou foi movido.",
  notFoundCtaTools: "Voltar às Ferramentas",
  notFoundCtaProjects: "Ir para Projetos",
  paginationPrevious: "Anterior",
  paginationNext: "Seguinte",
  paginationPageOf: "Página {page} de {totalPages}",
  paginationAria: "Paginação de projetos",
  chatTitle: "Assistente",
  chatDesc: "Pense em voz alta com um assistente que conhece as suas ferramentas.",
  chatPlaceholder: "Pergunte qualquer coisa…",
  chatSend: "Enviar",
  certsTitle: "Certificado de adesão",
  certsDesc:
    "Adesão completa à World Innovative Teaching Association com certificado impresso e verificável — separado da sua subscrição Aidemia Pro.",
  certsObserverNote:
    "É um membro observador da World Innovative Teaching Association. Passe à adesão completa para receber o certificado.",
  certsRepurchaseNote:
    "O seu acesso de adesão anterior já não está ativo. Pode comprar o certificado novamente.",
  certsWhyTitle: "Porquê obter o certificado",
  certsBenefits: [
    "Confirmação oficial de adesão profissional à WITA",
    "Número de série único com verificação online",
    "Certificado horizontal pronto para imprimir ou enviar",
    "Qualificação reconhecida para escolas e instituições",
    "Registo no diretório de membros da associação",
    "Adesão mensal a um preço por tempo limitado",
  ],
  certsPlanLabel: "Adesão mensal",
  certsPriceWas: "100,00 $",
  certsPriceNow: "49,99 $",
  certsNotInPro:
    "Não incluído na Aidemia Pro. Paga apenas o certificado de adesão e a adesão completa à WITA.",
  certsCta: "Aderir e obter o certificado",
  certsSecurePay: "Pagamento seguro na nossa página de pagamento.",
  certsSpecimen: "Espécime",
  certsSpecimenCaption:
    "Espécime — não é um certificado emitido. Pré-visualização do design oficial da WITA.",
  certsSpecimenAlt: "Espécime — pré-visualização do certificado de adesão",
  certsPurchasedNote:
    "O seu certificado de adesão completa à World Innovative Teaching Association está ativo.",
  certsSerialNumber: "Número de série do documento: {serial}",
  certsIncludesTitle: "A sua adesão inclui",
  certsDownload: "Descarregar certificado",
  certsDownloading: "A descarregar…",
  certsDownloadError: "Não foi possível descarregar o certificado. Tente novamente.",
  certsPreviewCaption:
    "O certificado emitido usa o nome do seu perfil e o número de série.",
  certsPreviewAlt: "Pré-visualização do certificado de adesão",
  certsProfileIncompleteBoth:
    "O nome e o apelido não estão preenchidos. O certificado usa o nome do seu perfil.",
  certsProfileIncompleteFirst:
    "O nome não está preenchido. O certificado usa o nome do seu perfil.",
  certsProfileIncompleteLast:
    "O apelido não está preenchido. O certificado usa o nome do seu perfil.",
  certsProfileIncompleteLink: "Preencher o perfil",
  certsVerifyEyebrow: "Adesão WITA",
  certsVerifyTitle: "Verificar um certificado",
  certsVerifyDesc:
    "Introduza o número de série do documento para confirmar que um certificado de adesão é autêntico e está atualmente válido.",
  certsVerifyAssociation: "World Innovative Teaching Association",
  certsVerifyPrivacy:
    "A verificação confirma apenas a validade — os dados pessoais nunca são mostrados.",
  certsVerifyLabel: "Número de série do documento",
  certsVerifyPlaceholder: "CERT-2026-000001",
  certsVerifyHint: "Formato: CERT-YYYY-NNNNNN",
  certsVerifyRequired: "Introduza um número de série do certificado.",
  certsVerifyFormatError: "Introduza um número como CERT-2026-000001.",
  certsVerifyCta: "Verificar certificado",
  certsVerifyChecking: "A verificar…",
  certsVerifyValidTitle: "O certificado é válido",
  certsVerifyValidBody:
    "Este certificado de adesão foi emitido em {date} e está atualmente ativo.",
  certsVerifyInvalidTitle: "Certificado não verificado",
  certsVerifyInvalidBody:
    "Não foi possível confirmar este número. Pode estar incorreto, expirado ou já não ativo.",
  certsVerifyAgain: "Verificar outro número",
  certsVerifyError:
    "Não foi possível contactar o serviço de verificação. Verifique a ligação e tente novamente.",
  certsVerifyBackToCerts: "Voltar ao certificado de adesão",
  certsVerifyOpenLink: "Verificar um número de certificado",
  accountTitle: "Conta",
  accountEmailVerified: "E-mail confirmado",
  accountEmailUnverified: "E-mail não confirmado",
  accountResendConfirmation: "Reenviar e-mail de confirmação",
  accountResendConfirmationSending: "A enviar…",
  accountResendConfirmationSent: "E-mail de confirmação enviado. Verifique a sua caixa de entrada.",
  emailConfirmConfirming: "A confirmar o e-mail…",
  emailConfirmSuccessTitle: "E-mail confirmado",
  emailConfirmSuccessBody:
    "O seu endereço de e-mail foi verificado. Pode continuar a usar a Aidemia.",
  emailConfirmInvalidTitle: "Ligação de confirmação inválida",
  emailConfirmInvalidBody:
    "Esta ligação de confirmação é inválida ou expirou. Peça um novo e-mail de confirmação e tente novamente.",
  emailConfirmMissingTitle: "Falta a ligação de confirmação",
  emailConfirmMissingBody:
    "Abra a ligação de confirmação do seu e-mail para verificar o endereço.",
  emailConfirmErrorTitle: "Não foi possível confirmar o e-mail",
  emailConfirmGoToAccount: "Ir para a conta",
  emailConfirmGoHome: "Voltar ao início",
  loginLinkConsuming: "A iniciar sessão…",
  loginLinkMissingTitle: "Ligação inválida",
  loginLinkMissingBody:
    "Esta ligação de início de sessão não tem token. Abra novamente a ligação do e-mail ou da confirmação de pagamento.",
  loginLinkInvalidTitle: "Ligação expirada ou já utilizada",
  loginLinkInvalidBody:
    "Esta ligação de início de sessão expirou ou já foi utilizada. Peça uma nova para continuar.",
  loginLinkErrorTitle: "Não foi possível iniciar sessão",
  loginLinkGoHome: "Voltar ao início",
  projectLandingEyebrow: "O seu projeto",
  projectLandingBody: "Sessão iniciada. Abra o espaço de trabalho para continuar.",
  projectLandingMissingTitle: "Projeto não encontrado",
  projectLandingMissingBody: "Este projeto não existe ou não tem acesso a ele.",
  projectLandingErrorTitle: "Não foi possível carregar o projeto",
  projectLandingGoHome: "Voltar ao início",
  projectLandingOpenWorkspace: "Abrir espaço de trabalho",
  paymentCheckoutSuccessTitle: "Pagamento concluído",
  paymentCheckoutSuccessBody:
    "Obrigado. O pagamento foi concluído. O acesso Pro aparecerá em breve na sua conta.",
  paymentCheckoutSuccessCompleting: "A confirmar o pagamento…",
  paymentCheckoutSuccessGuestBody:
    "Obrigado. A sua conta está pronta — enviámos uma palavra-passe por e-mail. Já tem sessão iniciada e o acesso Pro está ativo.",
  paymentCheckoutSuccessExistingAccountBody:
    "Obrigado. O pagamento foi associado à sua conta existente. Inicie sessão com a palavra-passe habitual para continuar.",
  paymentCheckoutSuccessSignIn: "Iniciar sessão",
  paymentCheckoutSuccessGoToAccount: "Ir para a conta",
  paymentCheckoutSuccessGoToTools: "Voltar às ferramentas",
  paymentCheckoutSuccessGoToCertificates: "Voltar aos certificados",
  paymentCheckoutCancelTitle: "Pagamento cancelado",
  paymentCheckoutCancelBody:
    "O checkout foi cancelado. Não foi cobrado nenhum valor. Pode tentar novamente quando quiser.",
  paymentCheckoutCancelRetry: "Tentar novamente",
  paymentCheckoutCancelGoToTools: "Voltar às ferramentas",
  paymentCheckoutCancelGoToCertificates: "Voltar aos certificados",
  accountSubscription: "Assinatura",
  accountDataPrivacy: "Dados e privacidade",
  accountPlanFree: "Grátis",
  accountProfileHeading: "O seu nome",
  accountFirstName: "Nome",
  accountLastName: "Apelido",
  accountFirstNamePh: "Nome próprio",
  accountLastNamePh: "Apelido",
  accountSaveProfile: "Guardar alterações",
  accountProfileSaved: "Perfil atualizado.",
  menuRecents: "Recentes",
  menuRecentsEmpty: "Ainda sem gerações recentes",
  menuRecentsShowMore: "Mostrar mais",
  menuAccountSettings: "Configurações da conta",
  menuAbout: "Sobre a Aidemia",
  menuPersonalize: "Personalizar",
  menuPrivacy: "Política de privacidade",
  menuTerms: "Termos e condições",
  menuContact: "Contacte-nos",
  signIn: "Iniciar sessão",
  signOut: "Encerrar sessão",
  itemDownload: "Baixar",
  itemEdit: "Editar",
  itemBodyLabel: "Conteúdo",
  itemGeneratedWith: "Gerado com",
  itemProject: "Projeto",
  itemGenerateNew: "Gerar um novo",
  itemDraftNote: "Rascunho gerado pela Aidemia — reveja e edite antes de usar.",
  audioPlay: "Reproduzir",
  audioPause: "Pausar",
  audioMute: "Silenciar",
  audioUnmute: "Ativar som",
  audioSeek: "Posição",
  audioVolume: "Volume",
  audioError: "Não foi possível carregar este áudio. Tente descarregar o ficheiro.",
  audioLabel: "Áudio",
  cpSubtitle: "Um espaço para o trabalho de um caso de cliente.",
  cpNameLabel: "Nome do projeto",
  cpNamePh: "ex.: Cliente S. — Pânico",
  cpDescLabel: "Descrição",
  cpOptional: "opcional",
  cpRequired: "obrigatório",
  cpDescPh: "Motivo da consulta, modalidade, etapa do acompanhamento…",
  cpAsideTitle: "Sobre os projetos",
  cpAsideText:
    "Um projeto reúne todo o trabalho de um caso de cliente num só lugar — notas, planos e cartas — para ser fácil de encontrar mais tarde.",
  cpAsideHint: "Dica: use iniciais ou um código curto em vez do nome completo do cliente.",
  cpCancel: "Cancelar",
  cpCreate: "Criar",
  cpSave: "Guardar",
  cpEmptyProjTitle: "Este projeto está vazio",
  cpEmptyProj: "Ainda não há nada aqui — os rascunhos gerados neste projeto aparecerão aqui.",
  tfTitleLabel: "Título do rascunho",
  tfTitlePh: "ex.: Preparação de sessão — semana 7",
  tfProjectLabel: "Guardar no projeto",
  tfContextLabel: "Contexto clínico",
  tfContextPh: "Cole notas de sessão, uma carta de referência ou algumas linhas sobre o cliente…",
  tfToneLabel: "Tom de voz",
  tfToneOpts: ["Caloroso", "Neutro", "Clínico"],
  tfNextLabel: "Sugerir próximos passos",
  tfDeepLabel: "Raciocínio clínico profundo",
  tfReset: "Repor",
  tfGenerate: "Gerar",
  tfMoreIn: "Mais nesta categoria",
  tfMoreEmptyTitle: "Ainda sem outras ferramentas",
  tfMoreEmptyDesc: "Por agora, é a única ferramenta nesta categoria.",
  badgePro: "PRO",
  badgeExp: "EXPERIMENTAL",
  tfToday: "Hoje",
  tfGenBlockH: "Rascunho",
  tfGenBlockP:
    "Um primeiro rascunho estruturado com base no seu contexto — pronto para revisar, editar e tornar seu.",
  tfCtxBlockH: "O seu contexto",
  tfClientLabel: "Referência do cliente",
  tfClientPh: "ex.: Cliente M. (apenas iniciais)",
  tfModalityLabel: "Modalidade terapêutica",
  tfModalityOpts: ["TCC", "ACT", "DBT", "Psicodinâmica", "Centrada na pessoa", "Integrativa"],
  tfFocusLabel: "Principais áreas de foco",
  tfFocusPh: "Temas, objetivos ou o que o rascunho deve enfatizar…",
  tfFormatLabel: "Formato de saída",
  tfFormatOpts: ["Tópicos", "Texto corrido", "Nota SOAP", "Carta"],
  tfLengthLabel: "Extensão",
  tfLengthOpts: ["Breve", "Padrão", "Detalhado"],
  tfReadingLabel: "Nível de leitura",
  tfReadingOpts: ["Linguagem simples", "Padrão", "Clínico"],
  tfRiskLabel: "Incluir triagem de risco",
  tfMeasuresLabel: "Referenciar medidas de resultado",
  tfAnonLabel: "Anonimizar dados do cliente",
  ctxTitle: "Contexto do projeto",
  ctxNotesLabel: "Notas",
  ctxNotesPh:
    "Adicione o seu próprio contexto — histórico, objetivos, sinais de risco, preferências, tudo o que quiser lembrar para a próxima vez…",
  ctxPrivate: "Privado · guardado neste dispositivo",
  ctxSummaryLabel: "Dos seus rascunhos",
  ctxAuto: "Auto",
  ctxUpdate: "Atualizar",
  ctxSavedTag: "Guardado",
  ctxToolsUsed: "Ferramentas usadas",
  ctxNotesHelp:
    "Usado sempre que seleciona este projeto numa Ferramenta de IA, para enriquecer os rascunhos gerados.",
  ctxSummaryTpl: "{n} {drafts} até agora, o mais recente em {date}.",
  ctxDraftWord: "rascunho",
  ctxDraftsWord: "rascunhos",
  ctxCovers: "Abrange",
  ctxEmptySummary:
    "Ainda não há rascunhos — um resumo aparecerá aqui à medida que gerar trabalho neste projeto.",
  authTitle: "Proteja a sua conta",
  authSubtitle: "Ter uma conta ajuda a manter os dados da sua aplicação e o estado PRO seguros.",
  authGoogle: "Continuar com o Google",
  authApple: "Continuar com a Apple",
  authEmail: "Continuar com email",
  authGuest: "Continuar como convidado",
  authClose: "Fechar",
  authBack: "Voltar",
  authOr: "ou",
  authEmailLabel: "Email",
  authEmailPh: "você@exemplo.com",
  authPasswordLabel: "Palavra-passe",
  authPasswordPh: "••••••••",
  authPasswordHint: "Pelo menos 8 caracteres.",
  authRestoreAccess: "Esqueceu a palavra-passe?",
  authRestoreTitle: "Restaurar acesso",
  authRestoreSubtitle: "Indique o seu email e enviaremos uma nova palavra-passe.",
  authRestoreSubmit: "Enviar nova palavra-passe",
  authRestoreSuccess: "Se existir uma conta com este email, uma nova palavra-passe está a caminho.",
  authSignInTitle: "Iniciar sessão",
  authSignUpTitle: "Crie a sua conta",
  authSubmitSignIn: "Iniciar sessão",
  authSubmitSignUp: "Criar conta",
  authSubmitting: "Aguarde…",
  authSwitchToSignUp: "Não tem conta? Registe-se",
  authSwitchToSignIn: "Já tem conta? Inicie sessão",
  authErrorFallback: "Algo correu mal. Tente novamente.",
  authTooManyAttempts: "Demasiadas tentativas. Aguarde um momento e tente novamente.",
  authEmailRequired: "O email é obrigatório.",
  authEmailInvalid: "Introduza um email válido.",
  authPasswordRequired: "A palavra-passe é obrigatória.",
  authPasswordTooShort: "A palavra-passe deve ter pelo menos 8 caracteres.",
  authInvalidCredentials: "Credenciais inválidas.",
  authEmailTaken: "Este email já está registado.",
  formCheckFields: "Verifique os campos destacados.",
  validationFieldRequired: "{field} é obrigatório.",
  validationFieldEmail: "Introduza um {field} válido.",
  validationFieldTaken: "Este {field} já está em uso.",
  validationFieldMin: "{field} deve ter pelo menos {min} caracteres.",
  validationFieldMax: "{field} deve ter no máximo {max} caracteres.",
  fieldLabelEmail: "Email",
  fieldLabelPassword: "Palavra-passe",
  fieldLabelName: "Nome",
  fieldLabelDescription: "Descrição",
  fieldLabelNotes: "Notas",
  fieldLabelTitle: "Título",
  fieldLabelFirstName: "Nome",
  fieldLabelLastName: "Apelido",
  cpNameRequired: "O nome do projeto é obrigatório.",
  hiTitleRequired: "O título é obrigatório.",
  tfRunError: "Não foi possível gerar. Tente novamente.",
  tfUsageLimitGuest: "Limite de convidado atingido. Inicie sessão ou crie uma conta para continuar.",
  tfUsageLimitFree: "Limite gratuito atingido. Atualize para Pro para mais gerações.",
  tfNeedProject: "Crie primeiro um projeto para guardar esta geração.",
  guestDraftsName: "Rascunhos",
  defaultProjectName: "Predefinido",
  guestSyncFailed:
    "Não foi possível importar os seus projetos de convidado. Os rascunhos locais continuam guardados — tente novamente.",
  guestSyncNotClaimable:
    "Algumas gerações de convidado não puderam ser reclamadas (em falta ou já pertencentes a outro). Os rascunhos locais continuam guardados.",
  guestSyncLimitsExceeded:
    "Demasiados projetos ou gerações de convidado para importar de uma vez. Remova alguns rascunhos e tente novamente.",
  tfGenerating: "A criar…",
  tfGeneratingHint: "Normalmente demora alguns segundos.",
  tfCancelRequest: "Cancelar pedido",
  authGateTitle: "Inicie sessão para continuar",
  authGateSubtitle: "Crie uma conta gratuita para manter os seus projetos e certificados num só lugar.",
  authGateCta: "Iniciar sessão ou criar conta",
  retry: "Tentar novamente",
  proModalTitle: "Crie sem limites",
  proModalDesc:
    "Conteúdo e ideias psicoterapêuticas únicos, feitos para si com ChatGPT e mais 30 motores de IA — sem prompts complexos.",
  proModalPriceWas: "49 $",
  proModalPriceNow: "19,99 $",
  proModalOffer: "Pedidos ilimitados · oferta especial de hoje",
  proModalRating: "4,9 · 3.920 avaliações",
  proModalQuote: "«A melhor app para psicoterapeutas em 2025.» — Open Academy",
  proModalTrusted: "A confiança de",
  proModalTrustedLabel: "especialistas",
  proModalFeatures: [
    {
      emoji: "👨‍🏫",
      title: "Mais de 50 módulos criativos",
      desc: "Crie qualquer conteúdo necessário para facilitar o seu trabalho.",
    },
    {
      emoji: "🐣",
      title: "Sem competências técnicas",
      desc: "Use formulários simples em vez de prompts complexos.",
    },
    {
      emoji: "🚩",
      title: "Ajustes regionais",
      desc: "Ajuste o conteúdo aos seus padrões locais.",
    },
    {
      emoji: "🧠",
      title: "Resultados personalizados",
      desc: "Conteúdo com base no histórico do paciente e no seu estilo.",
    },
    {
      emoji: "⌛",
      title: "Sem tempo de espera",
      desc: "Gere conteúdo e ideias depressa, sem esperar.",
    },
    {
      emoji: "🚀",
      title: "Quota ilimitada",
      desc: "Crie tanto conteúdo quanto precisar.",
    },
  ],
  proModalCta: "Obter acesso completo",
  proModalClose: "Fechar",
  proModalSchoolsAlt: "Harvard, Eton, Cambridge, Harrow e Groton",
  proModalReviews: [
    {
      name: "Amy",
      country: "UK",
      title: "Poupa tempo",
      initials: "A",
      avatar: "/promo/review-amy.png",
      body: "A Aidemia muda por completo o trabalho de quem quer agilizar a prática. Como terapeuta, o tempo é sempre escasso — e a Aidemia entende isso. A interface intuitiva e as ferramentas de IA reduzem muito a preparação e a análise.",
    },
    {
      name: "Young",
      country: "Ireland",
      title: "Acessível",
      initials: "Y",
      avatar: "/promo/review-young.png",
      body: "A Aidemia não poupa só tempo: poupa também dinheiro. Ao reunir as funcionalidades de várias ferramentas de IA numa só plataforma, elimina a necessidade de subscrições caras a vários serviços. Como psicoterapeuta, não posso subestimar o leque de possibilidades e o valor que me traz todos os dias.",
    },
  ],
  proModalDataProtected: "Os seus dados estão protegidos",
  proModalTermsShort: "Termos",
  proModalGuarantee: "Garantia de reembolso de 14 dias",
  proModalLegalNote:
    "O desconto já está aplicado. O seu passe dá acesso a todos os tipos de conteúdo, incluindo funcionalidades futuras sem aumento de preço. Pode cancelar a qualquer momento ou renovar automaticamente a cada quatro semanas. Parte da sua contribuição vai para instituições de apoio à infância.",
};

export const TRANSLATIONS: Record<string, TranslationStrings> = {
  en,
  es,
  de,
  fr,
  it,
  ja,
  ru,
  nl,
  el,
  pt,
};

/** Resolve a locale's strings, falling back to English. */
export function getTranslations(locale: string): TranslationStrings {
  return TRANSLATIONS[locale] ?? en;
}
